(function(){
'use strict';
if(window.LCVRAudioEditorV2){window.LCVRAudioEditorV2();return;}
const yieldTask=()=>new Promise(r=>setTimeout(r,0)),clamp=(x,a,b)=>Math.min(b,Math.max(a,x));
const db=x=>x>0?20*Math.log10(x):-Infinity,ld=x=>x>0?-.691+10*Math.log10(x):-Infinity;
const show=x=>Number.isNaN(x)?'—':Number.isFinite(x)?x.toFixed(1):'−∞';
const time=(x,digits=3)=>{const unit=10**digits,total=Math.round(Math.max(0,x||0)*unit);return Math.floor(total/(60*unit))+':'+((total%(60*unit))/unit).toFixed(digits).padStart(digits?digits+3:2,'0');};
const instances=new Set();
const MAX=256*1024*1024,emptyFade=()=>({in:0,out:0,ic:1,oc:1});
// Use the same L/R samples for correlation and M/S; neither operation changes audio.
function stereoReadings(left,right,cross,frames,mono=false){
 const audible=frames>0&&left/frames>1e-12&&right/frames>1e-12;
 const correlation=audible?clamp(cross/Math.sqrt(left*right),-1,1):NaN;
 return {correlation,mid:frames?db(Math.sqrt(Math.max(0,(left+right+2*cross)/4/frames))):NaN,sideLevel:frames?db(Math.sqrt(Math.max(0,(left+right-2*cross)/4/frames))):NaN,phaseKind:!frames?'waiting':!audible?(left/frames<=1e-12&&right/frames<=1e-12?'silence':'one_channel'):mono?'mono':correlation<-.05?'negative':correlation>.05?'positive':'low'};
}
function mp3Frame(bytes,p){
 if(p+4>bytes.length||bytes[p]!==255||(bytes[p+1]&224)!==224)return null;
 const version=(bytes[p+1]>>3)&3,layer=(bytes[p+1]>>1)&3,index=bytes[p+2]>>4,rateIndex=(bytes[p+2]>>2)&3;
 if(version===1||layer!==1||index===0||index===15||rateIndex===3||(bytes[p+3]&3)===2)return null;
 const bitrates=version===3?[0,32,40,48,56,64,80,96,112,128,160,192,224,256,320]:[0,8,16,24,32,40,48,56,64,80,96,112,128,144,160];
 const rate=[44100,48000,32000][rateIndex]/(version===3?1:version===2?2:4),samples=version===3?1152:576;
 return {rate,channels:(bytes[p+3]>>6)===3?1:2,samples,size:Math.floor((version===3?144000:72000)*bitrates[index]/rate)+((bytes[p+2]>>1)&1)};
}
/**
 * Find prominent audio attacks for non-destructive cursor navigation.
 * Channels are combined as energy and absolute peak, never as L + R.
 * Returns sorted onset times in seconds. This is an onset aid, not song detection.
 */
async function findTransients(buffer, { cancelled = () => false, onProgress = () => {} } = {}) {
  const rate = buffer?.rate;
  const channels = buffer?.ch;
  const frames = channels?.[0]?.length || 0;
  if (!Number.isFinite(rate) || rate < 8000 || rate > 192000 ||
      !Array.isArray(channels) || !channels.length || channels.length > 2 ||
      !channels.every(channel => channel && channel.length === frames)) {
    throw new TypeError('Invalid audio buffer for transient search.');
  }
  const checkCancelled = () => {
    if (cancelled()) {
      const error = new Error('Transient search cancelled.');
      error.name = 'AbortError';
      throw error;
    }
  };
  const yieldTask = () => new Promise(resolve => setTimeout(resolve, 0));
  checkCancelled();
  onProgress(0);
  if (!frames) { onProgress(1); return []; }

  // Five-millisecond bins keep the analysis memory small even for long sides.
  // A 25 ms trailing window suppresses carrier-cycle changes in steady bass.
  const hop = Math.max(1, Math.round(rate * 0.005));
  const count = Math.ceil(frames / hop);
  const energy = new Float64Array(count);
  const peaks = new Float32Array(count);
  const yieldBins = Math.max(1, Math.ceil(65536 / hop));
  for (let bin = 0; bin < count; bin++) {
    const start = bin * hop;
    const end = Math.min(frames, start + hop);
    let sum = 0;
    let peak = 0;
    for (const channel of channels) {
      for (let i = start; i < end; i++) {
        const value = channel[i];
        if (!Number.isFinite(value)) throw new TypeError('Invalid audio sample for transient search.');
        sum += value * value;
        peak = Math.max(peak, Math.abs(value));
      }
    }
    energy[bin] = sum / ((end - start) * channels.length);
    peaks[bin] = peak;
    if ((bin + 1) % yieldBins === 0) {
      onProgress(0.8 * (bin + 1) / count);
      await yieldTask();
      checkCancelled();
    }
  }

  const result = [];
  const step = hop / rate;
  const energyAlpha = 1 - Math.exp(-step / 0.08);
  const peakAlpha = 1 - Math.exp(-step / 0.10);
  const peakFloor = 0.0001; // Ignore effectively silent material below -80 dBFS.
  const energyFloor = 1e-10;
  const separation = Math.round(rate * 0.06);
  let slowEnergy = 0;
  let slowPeak = 0;
  let runningEnergy = 0;
  let previousOnset = -separation;
  for (let bin = 0; bin < count; bin++) {
    runningEnergy += energy[bin];
    if (bin >= 5) runningEnergy -= energy[bin - 5];
    const fastEnergy = Math.max(0, runningEnergy / Math.min(5, bin + 1));
    let fastPeak = 0;
    for (let j = Math.max(0, bin - 4); j <= bin; j++) fastPeak = Math.max(fastPeak, peaks[j]);

    // Require both energy and peak growth. Energy alone mistakes a low-frequency
    // carrier for repeated attacks; peak alone mistakes isolated noise for one.
    if (fastEnergy > Math.max(energyFloor, slowEnergy * 2.6) &&
        fastPeak > Math.max(peakFloor, slowPeak * 1.45)) {
      const threshold = Math.max(peakFloor, slowPeak * 1.3, fastPeak * 0.08);
      const earliest = Math.max(0, (bin - 5) * hop, previousOnset + separation);
      const end = Math.min(frames, (bin + 1) * hop);
      let onset = -1;
      // Refine to the first meaningful sample near the attack, rather than
      // returning the end of the analysis window.
      for (let i = earliest; i < end; i++) {
        let peak = 0;
        for (const channel of channels) peak = Math.max(peak, Math.abs(channel[i]));
        if (peak >= threshold) { onset = i; break; }
      }
      if (onset >= 0 && onset - previousOnset >= separation) {
        // Treat a signal already present at the start as the first onset.
        if (!result.length && onset < Math.round(rate * 0.005)) onset = 0;
        result.push(onset / rate);
        previousOnset = onset;
      }
    }
    slowEnergy += energyAlpha * (fastEnergy - slowEnergy);
    slowPeak += peakAlpha * (fastPeak - slowPeak);
    if ((bin + 1) % 2048 === 0) {
      onProgress(0.8 + 0.2 * (bin + 1) / count);
      await yieldTask();
      checkCancelled();
    }
  }
  checkCancelled();
  onProgress(1);
  return result;
}

/* Pure buffer edits. Input/output: { rate, ch: Float32Array[], ...metadata }.
 * Progress is a fraction in [0, 1]; cancellation rejects with name AbortError.
 * maxBytes limits the NEW decoded buffer, not total browser resident memory.
 * Callers own fades, undo, markers, project-wide budgets and export metadata.
 */
function audioEditBufferShape(buffer) {
  if (!buffer || !Number.isFinite(buffer.rate) || buffer.rate <= 0 ||
      !Array.isArray(buffer.ch) || !buffer.ch.length) {
    throw new TypeError('Audio must have a positive sample rate and Float32 channels.');
  }
  let frames;
  for (const channel of buffer.ch) {
    if (!ArrayBuffer.isView(channel) ||
        Object.prototype.toString.call(channel) !== '[object Float32Array]') {
      throw new TypeError('Audio channels must be Float32Array values.');
    }
    if (frames === undefined) frames = channel.length;
    if (channel.length !== frames) throw new RangeError('Audio channels must have equal lengths.');
  }
  const bytes = frames * buffer.ch.length * Float32Array.BYTES_PER_ELEMENT;
  if (!Number.isSafeInteger(bytes)) throw new RangeError('Audio buffer size is too large.');
  return { frames, channels: buffer.ch.length, bytes };
}

function audioEditHooks(cancelled, onProgress) {
  if (typeof cancelled !== 'function' || typeof onProgress !== 'function') {
    throw new TypeError('Audio edit callbacks must be functions.');
  }
  const check = () => {
    if (cancelled()) {
      const error = new Error('Audio edit cancelled.');
      error.name = 'AbortError';
      throw error;
    }
  };
  const progress = fraction => {
    check();
    onProgress(fraction);
    check();
  };
  const pause = async () => {
    check();
    await new Promise(resolve => setTimeout(resolve, 0));
    check();
  };
  return { check, progress, pause };
}

async function insertSilenceBuffer(buffer, atSeconds, silenceSeconds, {
  maxBytes = 256 * 1024 * 1024,
  cancelled = () => false,
  onProgress = () => {}
} = {}) {
  const shape = audioEditBufferShape(buffer);
  const hooks = audioEditHooks(cancelled, onProgress);
  hooks.check();
  if (!Number.isFinite(atSeconds) || atSeconds < 0 ||
      atSeconds > shape.frames / buffer.rate) {
    throw new RangeError('The insertion time must be inside the audio.');
  }
  if (!Number.isFinite(silenceSeconds) || silenceSeconds < 0) {
    throw new RangeError('Silence duration must be a finite, nonnegative number.');
  }
  if (!Number.isSafeInteger(maxBytes) || maxBytes < 0) {
    throw new RangeError('The audio memory limit must be a nonnegative safe integer.');
  }
  const atFrame = Math.round(atSeconds * buffer.rate);
  const silenceFrames = Math.round(silenceSeconds * buffer.rate);
  const outputFrames = shape.frames + silenceFrames;
  const outputBytes = outputFrames * shape.channels * Float32Array.BYTES_PER_ELEMENT;
  if (!Number.isSafeInteger(atFrame) || atFrame < 0 || atFrame > shape.frames ||
      !Number.isSafeInteger(silenceFrames) || !Number.isSafeInteger(outputFrames) ||
      !Number.isSafeInteger(outputBytes) || outputBytes > maxBytes) {
    throw new RangeError('Inserting this silence exceeds the audio memory limit.');
  }

  const chunk = 65536;
  const totalSamples = shape.frames * shape.channels;
  let checked = 0;
  hooks.progress(0);
  // Validate before allocating the output, including samples not near the edit.
  for (const source of buffer.ch) {
    for (let start = 0; start < shape.frames; start += chunk) {
      const end = Math.min(shape.frames, start + chunk);
      for (let i = start; i < end; i++) {
        if (!Number.isFinite(source[i])) throw new TypeError('Audio contains a non-finite sample.');
      }
      checked += end - start;
      hooks.progress(totalSamples ? checked / totalSamples * 0.25 : 0.25);
      await hooks.pause();
    }
  }
  hooks.check();
  // Float32Array allocation initializes the inserted region to exact zero.
  const ch = buffer.ch.map(() => new Float32Array(outputFrames));
  let copied = 0;
  for (let c = 0; c < shape.channels; c++) {
    const source = buffer.ch[c];
    for (const [begin, end, offset] of [[0, atFrame, 0], [atFrame, shape.frames, silenceFrames]]) {
      for (let start = begin; start < end; start += chunk) {
        const finish = Math.min(end, start + chunk);
        ch[c].set(source.subarray(start, finish), start + offset);
        copied += finish - start;
        hooks.progress(totalSamples ? 0.25 + copied / totalSamples * 0.75 : 1);
        await hooks.pause();
      }
    }
  }
  hooks.progress(1);
  return { ...buffer, ch };
}

async function removeDcOffsetBuffer(buffer, {
  cancelled = () => false,
  onProgress = () => {}
} = {}) {
  const shape = audioEditBufferShape(buffer);
  const hooks = audioEditHooks(cancelled, onProgress);
  const chunk = 65536;
  const totalSamples = shape.frames * shape.channels;
  const means = [];
  let checked = 0;
  hooks.progress(0);
  // Kahan summation uses JS double precision and retains small contributions.
  for (const source of buffer.ch) {
    let sum = 0;
    let compensation = 0;
    for (let start = 0; start < shape.frames; start += chunk) {
      const end = Math.min(shape.frames, start + chunk);
      for (let i = start; i < end; i++) {
        const sample = source[i];
        if (!Number.isFinite(sample)) throw new TypeError('Audio contains a non-finite sample.');
        const corrected = sample - compensation;
        const next = sum + corrected;
        compensation = (next - sum) - corrected;
        sum = next;
      }
      checked += end - start;
      hooks.progress(totalSamples ? checked / totalSamples * 0.5 : 0.5);
      await hooks.pause();
    }
    const mean = shape.frames ? sum / shape.frames : 0;
    if (!Number.isFinite(mean)) throw new RangeError('The DC offset is outside the supported range.');
    means.push(mean);
  }
  hooks.check();
  const ch = buffer.ch.map(() => new Float32Array(shape.frames));
  let processed = 0;
  for (let c = 0; c < shape.channels; c++) {
    const source = buffer.ch[c];
    for (let start = 0; start < shape.frames; start += chunk) {
      const end = Math.min(shape.frames, start + chunk);
      for (let i = start; i < end; i++) {
        const value = Math.fround(source[i] - means[c]);
        if (!Number.isFinite(value)) {
          throw new RangeError('Removing DC offset would exceed the Float32 sample range.');
        }
        ch[c][i] = value;
      }
      processed += end - start;
      hooks.progress(totalSamples ? 0.5 + processed / totalSamples * 0.5 : 1);
      await hooks.pause();
    }
  }
  hooks.progress(1);
  return { ...buffer, ch, dcOffset: { means, removed: true } };
}

/** Require a non-negative, exact sample-frame position. */
function lcvrMarkerFrame(value) {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new TypeError('Marker positions must be non-negative integer sample frames.');
  }
  return value;
}

/** Copy one marker while preserving optional caller-owned metadata. */
function lcvrMarkerCopy(marker, frame = marker?.frame) {
  if (!marker || typeof marker.name !== 'string') {
    throw new TypeError('Audio markers require a name and sample frame.');
  }
  return { ...marker, frame: lcvrMarkerFrame(frame) };
}

/**
 * Remap immutable markers after a sample-accurate edit.
 *
 * markers: [{ name: string, frame: non-negative integer, ...optionalMetadata }]
 * edit:
 *   { type: 'cut', start, end }    removes samples in [start, end).
 *   { type: 'trim', start, end }   retains samples in [start, end).
 *   { type: 'insert', start, frames } inserts `frames` samples before `start`.
 *
 * Marker boundaries intentionally differ from sample intervals for trim:
 * - Cut drops markers in [start,end); markers at end move to start.
 * - Trim keeps markers in [start,end], including the retained end boundary.
 * - Insert shifts markers at/after start by frames; earlier markers stay put.
 *
 * The caller validates edit positions against the audio length. This function
 * returns fresh marker objects, preserves input order and optional metadata,
 * and does not mutate markers, their objects, or the edit description.
 */
function remapAudioMarkers(markers, edit) {
  if (!Array.isArray(markers) || !edit || !['cut', 'trim', 'insert'].includes(edit.type)) {
    throw new TypeError('Invalid audio marker edit.');
  }
  const start = lcvrMarkerFrame(edit.start);
  const end = edit.type === 'insert' ? start : lcvrMarkerFrame(edit.end);
  const amount = edit.type === 'insert' ? lcvrMarkerFrame(edit.frames) : end - start;
  if (end < start) throw new RangeError('Audio edit end precedes its start.');
  const result = [];
  for (const marker of markers) {
    const copy = lcvrMarkerCopy(marker);
    const frame = copy.frame;
    if (edit.type === 'cut') {
      if (frame >= start && frame < end) continue;
      copy.frame = frame >= end ? frame - amount : frame;
    } else if (edit.type === 'trim') {
      if (frame < start || frame > end) continue;
      copy.frame = frame - start;
    } else {
      copy.frame = lcvrMarkerFrame(frame >= start ? frame + amount : frame);
    }
    result.push(copy);
  }
  return result;
}

/**
 * Merge automatic track starts and track-relative manual markers into side time.
 * tracks: [{ name: string, markers?: [{ name, frame, ...optionalMetadata }] }]
 * starts: integer sample-frame start for each track, using the side sample rate.
 *
 * Returns fresh { name, frame, ...optionalMetadata } objects sorted by absolute
 * frame. Ties preserve track order; within one track its automatic start appears
 * first, followed by manual markers in their input order. Duplicate names/times
 * remain separate markers. End-of-track markers are valid and remain intact.
 * Gaps and crossfades are represented by `starts`; no new timing is invented.
 */
function sideAudioMarkers(tracks, starts) {
  if (!Array.isArray(tracks) || !Array.isArray(starts) || tracks.length !== starts.length) {
    throw new TypeError('Each track requires its side start frame.');
  }
  const entries = [];
  let order = 0;
  tracks.forEach((track, index) => {
    if (!track || typeof track.name !== 'string' ||
        (track.markers !== undefined && !Array.isArray(track.markers))) {
      throw new TypeError('Invalid track markers.');
    }
    const start = lcvrMarkerFrame(starts[index]);
    entries.push({ marker: { name: track.name, frame: start }, order: order++ });
    for (const marker of track.markers || []) {
      const copy = lcvrMarkerCopy(marker);
      copy.frame = lcvrMarkerFrame(start + copy.frame);
      entries.push({ marker: copy, order: order++ });
    }
  });
  entries.sort((a, b) => a.marker.frame - b.marker.frame || a.order - b.order);
  return entries.map(entry => entry.marker);
}

/** Actual edited sample positions, shared by CutLab cues and the PQ sheet. */
function sideAudioTiming(tracks, starts) {
  if (!Array.isArray(tracks) || !Array.isArray(starts) || tracks.length !== starts.length) {
    throw new TypeError('Each track requires its side start frame.');
  }
  const rows = tracks.map((track, index) => {
    if (!track || typeof track.name !== 'string' || !track.ch?.length ||
        (track.markers !== undefined && !Array.isArray(track.markers))) {
      throw new TypeError('Invalid track markers.');
    }
    const start = lcvrMarkerFrame(starts[index]), frames = lcvrMarkerFrame(track.ch[0].length);
    const end = lcvrMarkerFrame(start + frames);
    const gap = index + 1 < starts.length ? Math.max(0, lcvrMarkerFrame(starts[index + 1]) - end) : 0;
    return { track: index + 1, name: track.name, ...(track.sourceName?{sourceName:track.sourceName}:{}), start, frames, end, gap };
  });
  return { rate: tracks[0]?.rate || 0, rows, frames: rows.reduce((last, row) => Math.max(last, row.end), 0) };
}

/**
 * Keep the live builder's CutLab cue contract separate from display markers.
 * Automatic cues retain builder order and IDs; user cues follow them so they
 * cannot displace a track or silent-gap cue. Only actual positive gaps get band
 * markers. Crossfade overlaps and adjoining songs never invent a silent gap.
 */
function sideExportMarkers(tracks, starts) {
  const timing = sideAudioTiming(tracks, starts), automatic = [], manual = [];
  timing.rows.forEach((row, index) => {
    const track = tracks[index], { start, frames, end, gap } = row, number = row.track;
    automatic.push({ name: track.name, frame: start, label: 'TRACK_' + number + '_START', type: 'track_start', track: number });
    if (gap) for (const [part, offset] of [['START', 0], ['MID', Math.floor(gap / 2)], ['END', gap]]) {
      automatic.push({ name: track.name, frame: end + offset, label: 'GAP_' + number + '_' + part, type: 'gap_' + part.toLowerCase(), track: number });
    }
    (track.markers || []).forEach((marker, markerIndex) => {
      const copy = lcvrMarkerCopy(marker);
      if (copy.frame > frames) throw new TypeError('Invalid track markers.');
      manual.push({ ...copy, frame: lcvrMarkerFrame(start + copy.frame), label: 'MANUAL_' + number + '_' + (markerIndex + 1), type: 'manual', track: number });
    });
  });
  return automatic.concat(manual);
}

// Filename and PQ serialization intentionally match the live builder contract.
function lcvrSlug(value) {
  return value.normalize('NFKC').trim().replace(/\.(wav|aif|aiff)$/i, '').replace(/[<>:"/\\|?*\u0000-\u001f\u007f]/g, '').replace(/\s+/g, '_').replace(/^[._]+|[._]+$/g, '').toLowerCase().slice(0, 80);
}
function lcvrTimeAt(frames, rate) {
  const ms = Math.round(frames / rate * 1000), minutes = Math.floor(ms / 60000), seconds = Math.floor(ms / 1000) % 60;
  return minutes + ':' + String(seconds).padStart(2, '0') + '.' + String(ms % 1000).padStart(3, '0');
}
function lcvrCsvCell(value) {
  let text = String(value);
  if (/^\s*[=+@-]/.test(text)) text = "'" + text;
  return '"' + text.replace(/"/g, '""') + '"';
}
function lcvrPqCsv(title, sideName, timing) {
  // These column names are a machine-facing contract and must not be translated.
  const rows = [['Title', 'Side', 'Track', 'Source filename', 'Start', 'Duration', 'End', 'Gap after (seconds)', 'Sample rate (Hz)', 'Start frame', 'Duration frames', 'End frame']];
  const rate = timing.rate;
  for (const row of timing.rows) {
    rows.push([title, sideName, row.track, row.sourceName||row.name, lcvrTimeAt(row.start, rate), lcvrTimeAt(row.frames, rate), lcvrTimeAt(row.end, rate), row.gap / rate, rate, row.start, row.frames, row.end]);
  }
  return '\ufeff' + rows.map(row => row.map(lcvrCsvCell).join(',')).join('\r\n');
}

function lcvrPutText(view, offset, value) {
  for (let i = 0; i < value.length; i++) view.setUint8(offset + i, value.charCodeAt(i));
}

// Match assets/lcvr-audio-tools-v3-crest.js wavHeader byte for byte. PCM24/32
// uses WAVE_FORMAT_EXTENSIBLE, including valid bits, channel mask and PCM GUID.
function lcvrWavHeader(format, frames) {
  const bytes = frames * format.block, pad = bytes % 2;
  const extended = format.kind === 'pcm' && format.bits > 16;
  const fmt = extended ? 40 : format.kind === 'float' ? 18 : 16;
  const fact = format.kind === 'float' ? 12 : 0, size = 12 + 8 + fmt + fact + 8;
  if (!Number.isSafeInteger(bytes) || size - 8 + bytes + pad > 4294967295) {
    throw new RangeError('This side is too large for a standard WAV file.');
  }
  const bytesOut = new Uint8Array(size), view = new DataView(bytesOut.buffer);
  lcvrPutText(view, 0, 'RIFF'); view.setUint32(4, size - 8 + bytes + pad, true);
  lcvrPutText(view, 8, 'WAVE'); lcvrPutText(view, 12, 'fmt '); view.setUint32(16, fmt, true);
  view.setUint16(20, extended ? 65534 : format.kind === 'float' ? 3 : 1, true);
  view.setUint16(22, format.channels, true); view.setUint32(24, format.rate, true);
  view.setUint32(28, format.rate * format.block, true); view.setUint16(32, format.block, true);
  view.setUint16(34, format.bits, true);
  if (extended) {
    view.setUint16(36, 22, true); view.setUint16(38, format.bits, true);
    view.setUint32(40, format.channels === 1 ? 4 : 3, true);
    bytesOut.set([1, 0, 0, 0, 0, 0, 16, 0, 128, 0, 0, 170, 0, 56, 155, 113], 44);
  }
  let at = 20 + fmt;
  if (fact) { lcvrPutText(view, at, 'fact'); view.setUint32(at + 4, 4, true); view.setUint32(at + 8, frames, true); at += 12; }
  lcvrPutText(view, at, 'data'); view.setUint32(at + 4, bytes, true);
  return bytesOut;
}

// Same cue + LIST/adtl/labl serialization as the live builder. Machine labels
// are ASCII and distinct from user names, which remain intact in the CSV.
function lcvrWaveMarkers(marks) {
  const cue = new Uint8Array(12 + 24 * marks.length), view = new DataView(cue.buffer);
  lcvrPutText(view, 0, 'cue '); view.setUint32(4, cue.length - 8, true); view.setUint32(8, marks.length, true);
  const labels = [];
  marks.forEach((mark, index) => {
    const at = 12 + 24 * index, id = index + 1;
    view.setUint32(at, id, true); view.setUint32(at + 4, mark.frame, true);
    lcvrPutText(view, at + 8, 'data'); view.setUint32(at + 20, mark.frame, true);
    const size = 4 + mark.label.length + 1, label = new Uint8Array(8 + size + (size % 2)), labelView = new DataView(label.buffer);
    lcvrPutText(labelView, 0, 'labl'); labelView.setUint32(4, size, true); labelView.setUint32(8, id, true);
    lcvrPutText(labelView, 12, mark.label); labels.push(label);
  });
  const list = new Uint8Array(12 + labels.reduce((total, label) => total + label.length, 0)), listView = new DataView(list.buffer);
  lcvrPutText(listView, 0, 'LIST'); listView.setUint32(4, list.length - 8, true); lcvrPutText(listView, 8, 'adtl');
  let at = 12;
  for (const label of labels) { list.set(label, at); at += label.length; }
  return [cue, list];
}

/* LCVR EQ / Mid-Side DSP. Pure helpers; no DOM, playback or project mutation.
 * Coefficients: RBJ / W3C Audio EQ Cookbook
 * https://www.w3.org/TR/audio-eq-cookbook/
 * Mid=(L+R)/2, Side=(L-R)/2. Shelf slope is fixed at S=1; Q controls bells.
 * Optional HPF/LPF use second/fourth-order Butterworth cascades (12/24 dB/oct).
 * WebAudio HPF/LPF Q uses dB: stage.Q = 20*log10(stage.q).
 * options: maxBytes (new output only), cancelled(), onProgress(0..1).
 * Flat EQ with disabled pass filters is exact identity after validation.
 */
function lcvrEqRate(rate) {
  if (!Number.isFinite(rate) || rate < 8000 || rate > 192000) {
    throw new RangeError('EQ requires an audio sample rate from 8 to 192 kHz.');
  }
  return rate;
}
function lcvrEqBand(band, rate) {
  lcvrEqRate(rate);
  if (!band || !['lowshelf', 'peaking', 'highshelf', 'highpass', 'lowpass'].includes(band.type)) {
    throw new TypeError('Choose a low shelf, bell, high shelf, high-pass or low-pass filter.');
  }
  const pass = band.type === 'highpass' || band.type === 'lowpass';
  const frequency = Number(band.frequency), gain = pass ? 0 : Number(band.gain);
  const q = pass ? Math.SQRT1_2 : band.q === undefined ? (band.type === 'peaking' ? 1 : Math.SQRT1_2) : Number(band.q);
  if (!Number.isFinite(frequency) || frequency <= 0) {
    throw new RangeError('EQ frequency must be a positive number.');
  }
  if (!Number.isFinite(gain) || gain < -24 || gain > 24) {
    throw new RangeError('EQ gain must be between -24 and 24 dB.');
  }
  if (!Number.isFinite(q) || q < 0.1 || q > 20) {
    throw new RangeError('EQ width must be between 0.1 and 20 Q.');
  }
  const normalized = { type: band.type, frequency: Math.min(Math.min(20000, rate * 0.49), Math.max(20, frequency)), gain, q };
  if (pass) {
    if (band.enabled !== undefined && typeof band.enabled !== 'boolean') {
      throw new TypeError('Filter enabled must be true or false.');
    }
    const slope = Number(band.slope ?? 12);
    if (![12, 24].includes(slope)) throw new RangeError('Filter slope must be 12 or 24 dB/octave.');
    normalized.enabled = band.enabled === true;
    normalized.slope = slope;
  }
  return normalized;
}
function lcvrEqBandActive(band) {
  return band.type === 'highpass' || band.type === 'lowpass' ? band.enabled === true : band.gain !== 0;
}
function lcvrEqDefaultBands(rate = 48000) {
  return [
    { type: 'lowshelf', frequency: 70, gain: 0, q: Math.SQRT1_2 },
    { type: 'peaking', frequency: 350, gain: 0, q: 1 },
    { type: 'peaking', frequency: 3000, gain: 0, q: 1 },
    { type: 'highshelf', frequency: 12000, gain: 0, q: Math.SQRT1_2 },
    { type: 'highpass', frequency: 30, enabled: false, slope: 12 },
    { type: 'lowpass', frequency: 18000, enabled: false, slope: 12 }
  ].map(band => lcvrEqBand(band, rate));
}
/** Fixed topology: one slot per tonal band and two slots per pass filter.
 * Inactive slots are unity peaking filters so preview bypass/toggles/slopes
 * can update existing BiquadFilterNodes without disconnecting the graph.
 * q is linear; Q is the exact value to assign to WebAudio node.Q.value.
 */
function lcvrEqStages(bands, rate, { bypass = false } = {}) {
  if (!Array.isArray(bands) || bands.length > 16) {
    throw new TypeError('EQ requires an array of up to 16 bands.');
  }
  lcvrEqRate(rate);
  return bands.flatMap((band, bandIndex) => {
    const b = lcvrEqBand(band, rate), pass = b.type === 'highpass' || b.type === 'lowpass';
    if (!pass) return [{ ...b, gain: bypass ? 0 : b.gain, Q: b.q, active: !bypass && b.gain !== 0, bandIndex, stageIndex: 0 }];
    // The 4th-order Butterworth poles give two different section Q values.
    // Cascading two Q=.707 sections would instead be -6 dB at cutoff.
    const sectionQ = b.slope === 24
      ? [1 / (2 * Math.cos(Math.PI / 8)), 1 / (2 * Math.cos(3 * Math.PI / 8))]
      : [Math.SQRT1_2];
    return [0, 1].map(stageIndex => {
      const active = !bypass && b.enabled && stageIndex < sectionQ.length;
      const q = active ? sectionQ[stageIndex] : 1;
      return { type: active ? b.type : 'peaking', frequency: b.frequency, gain: 0,
        q, Q: active ? 20 * Math.log10(q) : q, active, bandIndex, stageIndex };
    });
  });
}
function lcvrEqStageCoefficients(b, rate) {
  if (!b.active) return [1, 0, 0, 0, 0];
  const A = 10 ** (b.gain / 40), w = 2 * Math.PI * b.frequency / rate;
  const c = Math.cos(w), s = Math.sin(w);
  let b0, b1, b2, a0, a1, a2;
  if (b.type === 'highpass' || b.type === 'lowpass') {
    const alpha = s / (2 * b.q);
    if (b.type === 'lowpass') {
      b0 = (1 - c) / 2; b1 = 1 - c; b2 = b0;
    } else {
      b0 = (1 + c) / 2; b1 = -(1 + c); b2 = b0;
    }
    a0 = 1 + alpha; a1 = -2 * c; a2 = 1 - alpha;
  } else if (b.type === 'peaking') {
    const alpha = s / (2 * b.q);
    b0 = 1 + alpha * A; b1 = -2 * c; b2 = 1 - alpha * A;
    a0 = 1 + alpha / A; a1 = -2 * c; a2 = 1 - alpha / A;
  } else {
    // S=1 gives a monotonic shelf. Q is intentionally inactive for shelves.
    const beta = Math.sqrt(2 * A) * s;
    if (b.type === 'lowshelf') {
      b0 = A * ((A + 1) - (A - 1) * c + beta);
      b1 = 2 * A * ((A - 1) - (A + 1) * c);
      b2 = A * ((A + 1) - (A - 1) * c - beta);
      a0 = (A + 1) + (A - 1) * c + beta;
      a1 = -2 * ((A - 1) + (A + 1) * c);
      a2 = (A + 1) + (A - 1) * c - beta;
    } else {
      b0 = A * ((A + 1) + (A - 1) * c + beta);
      b1 = -2 * A * ((A - 1) + (A + 1) * c);
      b2 = A * ((A + 1) + (A - 1) * c - beta);
      a0 = (A + 1) - (A - 1) * c + beta;
      a1 = 2 * ((A - 1) - (A + 1) * c);
      a2 = (A + 1) - (A - 1) * c - beta;
    }
  }
  const result = [b0 / a0, b1 / a0, b2 / a0, a1 / a0, a2 / a0];
  if (!result.every(Number.isFinite)) throw new RangeError('The EQ settings produced an invalid filter.');
  return result;
}
function lcvrEqCoefficientList(bands, rate) {
  return lcvrEqStages(bands, rate).map(stage => lcvrEqStageCoefficients(stage, rate));
}
/** Compatibility helper for one biquad. Use lcvrEqCoefficientList([band],rate)
 * for complete per-band responses, including the 24 dB/octave cascade.
 */
function lcvrEqCoefficients(band, rate) {
  const stages = lcvrEqStages([band], rate), active = stages.filter(stage => stage.active);
  if (active.length > 1) throw new RangeError('Use cascaded EQ coefficients for a 24 dB/octave filter.');
  return active.length ? lcvrEqStageCoefficients(active[0], rate) : [1, 0, 0, 0, 0];
}
function lcvrEqResponseFromCoefficients(coefficients, rate, frequency) {
  lcvrEqRate(rate);
  if (!Number.isFinite(frequency) || frequency < 0 || frequency > rate / 2) {
    throw new RangeError('EQ response frequency must be between zero and Nyquist.');
  }
  const w = 2 * Math.PI * frequency / rate, c1 = Math.cos(w), s1 = Math.sin(w);
  const c2 = Math.cos(2 * w), s2 = Math.sin(2 * w);
  let db = 0;
  for (const co of coefficients) {
    if (!Array.isArray(co) || co.length !== 5 || !co.every(Number.isFinite)) {
      throw new TypeError('EQ coefficients must contain five finite numbers.');
    }
    const nr = co[0] + co[1] * c1 + co[2] * c2, ni = -co[1] * s1 - co[2] * s2;
    const dr = 1 + co[3] * c1 + co[4] * c2, di = -co[3] * s1 - co[4] * s2;
    const numerator = nr * nr + ni * ni, denominator = dr * dr + di * di;
    if (!(denominator > 0) || !Number.isFinite(numerator) || !Number.isFinite(denominator)) {
      throw new RangeError('The EQ response is outside the supported range.');
    }
    // HPF at DC and LPF at Nyquist have a real transmission zero.
    if (numerator === 0) return -Infinity;
    db += 10 * Math.log10(numerator / denominator);
  }
  return db;
}
function lcvrEqResponseDb(bands, rate, frequency) {
  return lcvrEqResponseFromCoefficients(lcvrEqCoefficientList(bands, rate), rate, frequency);
}
function lcvrEqShape(buffer, target, maxBytes) {
  if (!buffer || !Array.isArray(buffer.ch) || ![1, 2].includes(buffer.ch.length)) {
    throw new TypeError('Mid/Side editing requires mono or stereo audio.');
  }
  lcvrEqRate(buffer.rate);
  if (!['stereo', 'mid', 'side'].includes(target)) {
    throw new TypeError('Choose Stereo, Left, Right, Mid or Side.');
  }
  if (target === 'side' && buffer.ch.length === 1) {
    throw new RangeError('Side editing requires a stereo file.');
  }
  let frames;
  for (const channel of buffer.ch) {
    if (!ArrayBuffer.isView(channel) || Object.prototype.toString.call(channel) !== '[object Float32Array]') {
      throw new TypeError('Audio channels must be Float32Array values.');
    }
    if (frames === undefined) frames = channel.length;
    if (channel.length !== frames) throw new RangeError('Audio channels must have equal lengths.');
  }
  if (!Number.isSafeInteger(maxBytes) || maxBytes < 0) {
    throw new RangeError('The audio memory limit must be a nonnegative safe integer.');
  }
  const bytes = frames * buffer.ch.length * 4;
  if (!Number.isSafeInteger(bytes) || bytes > maxBytes) {
    throw new RangeError('This edit exceeds the audio memory limit.');
  }
  return { frames, channels: buffer.ch.length, bytes };
}
function lcvrEqHooks(cancelled, onProgress) {
  if (typeof cancelled !== 'function' || typeof onProgress !== 'function') {
    throw new TypeError('Audio edit callbacks must be functions.');
  }
  const check = () => {
    if (cancelled()) {
      const error = new Error('Audio edit cancelled.'); error.name = 'AbortError'; throw error;
    }
  };
  return {
    check,
    progress(value) { check(); onProgress(value); check(); },
    async pause() { check(); await new Promise(resolve => setTimeout(resolve, 0)); check(); }
  };
}
async function lcvrEqValidateSamples(buffer, hooks, fraction) {
  const n = buffer.ch[0].length, total = n * buffer.ch.length;
  let done = 0;
  hooks.progress(0);
  for (const source of buffer.ch) {
    for (let begin = 0; begin < n; begin += 65536) {
      const end = Math.min(n, begin + 65536);
      for (let i = begin; i < end; i++) {
        if (!Number.isFinite(source[i])) throw new TypeError('Audio contains a non-finite sample.');
      }
      done += end - begin;
      hooks.progress(total ? done / total * fraction : fraction);
      await hooks.pause();
    }
  }
  hooks.check();
}
async function lcvrProjectChannelsCore(buffer, target = 'stereo', {
  maxBytes = 256 * 1024 * 1024, cancelled = () => false, onProgress = () => {}
} = {}) {
  const shape = lcvrEqShape(buffer, target, maxBytes), hooks = lcvrEqHooks(cancelled, onProgress);
  const identity = target === 'stereo' || shape.channels === 1;
  await lcvrEqValidateSamples(buffer, hooks, identity ? 1 : 0.2);
  if (identity) { hooks.progress(1); return buffer; }
  const out = new Float32Array(shape.frames), sign = target === 'mid' ? 1 : -1;
  const left = buffer.ch[0], right = buffer.ch[1];
  for (let begin = 0; begin < shape.frames; begin += 65536) {
    const end = Math.min(shape.frames, begin + 65536);
    for (let i = begin; i < end; i++) out[i] = left[i] * 0.5 + right[i] * sign * 0.5;
    hooks.progress(0.2 + end / shape.frames * 0.8);
    await hooks.pause();
  }
  hooks.progress(1);
  return { ...buffer, ch: [out] };
}
function lcvrEqFilter(coefficients) {
  const state = coefficients.filter(c => !(c[0] === 1 && c.slice(1).every(x => x === 0)))
    .map(c => ({ c, x1: 0, x2: 0, y1: 0, y2: 0 }));
  return x => {
    for (const s of state) {
      const c = s.c, y = c[0] * x + c[1] * s.x1 + c[2] * s.x2 - c[3] * s.y1 - c[4] * s.y2;
      s.x2 = s.x1; s.x1 = x; s.y2 = s.y1; s.y1 = y;
      x = y;
    }
    return x;
  };
}
async function lcvrProcessTarget(buffer, target, makeProcessor, identity, {
  maxBytes = 256 * 1024 * 1024, cancelled = () => false, onProgress = () => {}
} = {}) {
  const shape = lcvrEqShape(buffer, target, maxBytes), hooks = lcvrEqHooks(cancelled, onProgress);
  await lcvrEqValidateSamples(buffer, hooks, identity ? 1 : 0.15);
  if (identity) { hooks.progress(1); return buffer; }
  const ch = buffer.ch.map(() => new Float32Array(shape.frames));
  const processors = Array.from({ length: target === 'stereo' ? shape.channels : 1 }, () => makeProcessor());
  const floatSample = value => {
    const output = Math.fround(value);
    if (!Number.isFinite(output)) throw new RangeError('This edit would exceed the Float32 audio range.');
    return output;
  };
  for (let begin = 0; begin < shape.frames; begin += 32768) {
    const end = Math.min(shape.frames, begin + 32768);
    if (target === 'stereo' || shape.channels === 1) {
      for (let c = 0; c < shape.channels; c++) {
        for (let i = begin; i < end; i++) ch[c][i] = floatSample(processors[c](buffer.ch[c][i]));
      }
    } else {
      const left = buffer.ch[0], right = buffer.ch[1], process = processors[0], sign = target === 'mid' ? 1 : -1;
      for (let i = begin; i < end; i++) {
        const component = left[i] * 0.5 + right[i] * sign * 0.5;
        const difference = process(component) - component;
        // Add only the edited component's change: preserve the other component
        // and avoid a gratuitous encode/decode round trip on unchanged samples.
        if (difference === 0) { ch[0][i] = left[i]; ch[1][i] = right[i]; }
        else {
          ch[0][i] = floatSample(left[i] + difference);
          ch[1][i] = floatSample(right[i] + sign * difference);
        }
      }
    }
    hooks.progress(0.15 + end / shape.frames * 0.85);
    await hooks.pause();
  }
  hooks.progress(1);
  return { ...buffer, ch };
}
async function lcvrProcessTargetGainCore(buffer, gainDb, target = 'stereo', options = {}) {
  if (!Number.isFinite(gainDb) || gainDb < -120 || gainDb > 60) {
    throw new RangeError('Gain must be between -120 and 60 dB.');
  }
  const factor = 10 ** (gainDb / 20);
  return lcvrProcessTarget(buffer, target, () => value => value * factor, gainDb === 0, options);
}
async function lcvrProcessTargetEqCore(buffer, bands, target = 'stereo', options = {}) {
  const coefficients = lcvrEqCoefficientList(bands, buffer?.rate);
  const identity = coefficients.every(c => c[0] === 1 && c.slice(1).every(x => x === 0));
  return lcvrProcessTarget(buffer, target, () => lcvrEqFilter(coefficients), identity, options);
}

/* LCVR split-band de-esser. Pure, deterministic, offline helpers.
 * Detector biquad: W3C Audio EQ Cookbook high-pass, Q=1/sqrt(2).
 * https://www.w3.org/TR/audio-eq-cookbook/
 * A complementary one-pole split preserves dry = low + high exactly.
 * Only the high branch is attenuated. Stereo detection is energy-linked;
 * Mid/Side processing adds only the chosen component's change to the source.
 * This is frequency-selective dynamics, not speech/stem identification.
 * Preview and Apply should share the returned buffer, not a different engine.
 */
function lcvrDeesserSettings(settings = {}, rate = 48000) {
  if (!Number.isFinite(rate) || rate < 8000 || rate > 192000) {
    throw new RangeError('The de-esser requires a sample rate from 8 to 192 kHz.');
  }
  if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
    throw new TypeError('Choose de-esser Amount and Frequency settings.');
  }
  const amountValue = settings.amount === undefined ? 0 : settings.amount;
  const frequencyValue = settings.frequency === undefined ? 6000 : settings.frequency;
  const amount = Number(amountValue), frequency = Number(frequencyValue);
  if (!['number', 'string'].includes(typeof amountValue) || (typeof amountValue === 'string' && !amountValue.trim()) ||
      !Number.isFinite(amount) || amount < 0 || amount > 100) {
    throw new RangeError('De-esser Amount must be between 0 and 100.');
  }
  if (!['number', 'string'].includes(typeof frequencyValue) || (typeof frequencyValue === 'string' && !frequencyValue.trim()) ||
      !Number.isFinite(frequency) || frequency <= 0) {
    throw new RangeError('De-esser Frequency must be a positive number.');
  }
  return { amount, frequency: Math.min(Math.min(12000, rate * 0.45), Math.max(2000, frequency)) };
}

function lcvrDeesserShape(buffer, target, maxBytes) {
  if (!buffer || !Array.isArray(buffer.ch) || ![1, 2].includes(buffer.ch.length)) {
    throw new TypeError('The de-esser requires mono or stereo audio.');
  }
  lcvrDeesserSettings({}, buffer.rate);
  if (!['stereo', 'mid', 'side'].includes(target)) {
    throw new TypeError('Choose Stereo, Left, Right, Mid or Side.');
  }
  if (target === 'side' && buffer.ch.length === 1) {
    throw new RangeError('Side editing requires a stereo file.');
  }
  let frames;
  for (const channel of buffer.ch) {
    if (!ArrayBuffer.isView(channel) || Object.prototype.toString.call(channel) !== '[object Float32Array]') {
      throw new TypeError('Audio channels must be Float32Array values.');
    }
    if (frames === undefined) frames = channel.length;
    if (channel.length !== frames) throw new RangeError('Audio channels must have equal lengths.');
  }
  if (!Number.isSafeInteger(maxBytes) || maxBytes < 0) {
    throw new RangeError('The audio memory limit must be a nonnegative safe integer.');
  }
  const bytes = frames * buffer.ch.length * Float32Array.BYTES_PER_ELEMENT;
  if (!Number.isSafeInteger(bytes) || bytes > maxBytes) {
    throw new RangeError('This de-esser edit exceeds the audio memory limit.');
  }
  return { frames, channels: buffer.ch.length, bytes };
}

function lcvrDeesserHooks(cancelled, onProgress) {
  if (typeof cancelled !== 'function' || typeof onProgress !== 'function') {
    throw new TypeError('Audio edit callbacks must be functions.');
  }
  const check = () => {
    if (cancelled()) {
      const error = new Error('De-esser processing cancelled.');
      error.name = 'AbortError';
      throw error;
    }
  };
  return {
    check,
    progress(value) { check(); onProgress(value); check(); },
    async pause() { check(); await new Promise(resolve => setTimeout(resolve, 0)); check(); }
  };
}

async function lcvrDeesserValidateSamples(buffer, hooks, start, end) {
  const total = buffer.ch[0].length * buffer.ch.length;
  let done = 0;
  hooks.progress(start);
  for (const source of buffer.ch) {
    for (let begin = 0; begin < source.length; begin += 65536) {
      const finish = Math.min(source.length, begin + 65536);
      for (let i = begin; i < finish; i++) {
        if (!Number.isFinite(source[i])) throw new TypeError('Audio contains a non-finite sample.');
      }
      done += finish - begin;
      hooks.progress(total ? start + done / total * (end - start) : end);
      await hooks.pause();
    }
  }
  hooks.progress(end);
}

function lcvrDeesserChannel(frequency, rate) {
  const w = 2 * Math.PI * frequency / rate;
  const cosine = Math.cos(w), alpha = Math.sin(w) / Math.SQRT2;
  const a0 = 1 + alpha;
  const b0 = (1 + cosine) / (2 * a0), b1 = -(1 + cosine) / a0;
  const a1 = -2 * cosine / a0, a2 = (1 - alpha) / a0;
  const g = Math.tan(Math.PI * frequency / rate), splitG = g / (1 + g);
  let x1 = 0, x2 = 0, y1 = 0, y2 = 0, splitState = 0;
  return {
    high: 0,
    detected: 0,
    process(x) {
      const y = b0 * x + b1 * x1 + b0 * x2 - a1 * y1 - a2 * y2;
      x2 = x1; x1 = x; y2 = y1; y1 = y;
      const v = (x - splitState) * splitG, low = v + splitState;
      splitState = low + v;
      this.high = x - low;
      this.detected = y;
    }
  };
}

function lcvrDeesserFloat(value) {
  const output = Math.fround(value);
  if (!Number.isFinite(output)) throw new RangeError('This de-esser edit would exceed the Float32 audio range.');
  return output;
}

/**
 * Settings: {amount:0..100, frequency:Hz}. Amount0 is exact identity/off.
 * options.maxBytes bounds new output samples, not total browser memory.
 * Returns {buffer, settings, stats}; input and settings are never mutated.
 * Stats describe gain reduction of the high branch, not full-band loudness.
 */
async function lcvrProcessDeesserCore(buffer, settings = {}, target = 'stereo', {
  maxBytes = 256 * 1024 * 1024, cancelled = () => false, onProgress = () => {}
} = {}) {
  const shape = lcvrDeesserShape(buffer, target, maxBytes);
  const normalized = lcvrDeesserSettings(settings, buffer.rate);
  const hooks = lcvrDeesserHooks(cancelled, onProgress), identity = normalized.amount === 0 || shape.frames === 0;
  const stats = { maxReductionDb: 0, meanReductionDb: 0, affectedFrames: 0, frames: shape.frames, target };
  await lcvrDeesserValidateSamples(buffer, hooks, 0, identity ? 1 : 0.1);
  if (identity) { hooks.progress(1); return { buffer, settings: normalized, stats }; }

  const ch = buffer.ch.map(() => new Float32Array(shape.frames));
  const channelCount = target === 'stereo' ? shape.channels : 1;
  const processors = Array.from({ length: channelCount }, () => lcvrDeesserChannel(normalized.frequency, buffer.rate));
  const attack = Math.exp(-1 / (buffer.rate * 0.001));
  const release = Math.exp(-1 / (buffer.rate * 0.060));
  const amount = normalized.amount / 100, maximum = 9 * amount;
  const relativeThreshold = -6 - 12 * amount, absoluteThreshold = -42;
  const controlHop = Math.max(1, Math.round(buffer.rate / 3000));
  const left = buffer.ch[0], right = buffer.ch[1];
  const sign = target === 'mid' ? 1 : -1;
  let fullEnvelope = 0, highEnvelope = 0, reduction = 0, desired = 0, totalReduction = 0;
  const aboveKnee = over => over <= -3 ? 0 : over >= 3 ? over : (over + 3) * (over + 3) / 12;
  for (let begin = 0; begin < shape.frames; begin += 32768) {
    const end = Math.min(shape.frames, begin + 32768);
    for (let i = begin; i < end; i++) {
      let fullPower = 0, highPower = 0, component = 0;
      if (target === 'stereo' || shape.channels === 1) {
        for (let c = 0; c < channelCount; c++) {
          const x = buffer.ch[c][i], processor = processors[c];
          processor.process(x);
          fullPower += x * x;
          highPower += processor.detected * processor.detected;
        }
        fullPower /= channelCount; highPower /= channelCount;
      } else {
        component = left[i] * 0.5 + right[i] * sign * 0.5;
        processors[0].process(component);
        fullPower = component * component;
        highPower = processors[0].detected * processors[0].detected;
      }
      fullEnvelope = fullPower + (fullPower > fullEnvelope ? attack : release) * (fullEnvelope - fullPower);
      highEnvelope = highPower + (highPower > highEnvelope ? attack : release) * (highEnvelope - highPower);
      if (i % controlHop === 0) {
        const highDb = 10 * Math.log10(Math.max(1e-24, highEnvelope));
        const relativeDb = 10 * Math.log10(Math.max(1e-24, highEnvelope) / Math.max(1e-24, fullEnvelope));
        desired = Math.min(maximum, 0.75 * aboveKnee(relativeDb - relativeThreshold), 0.75 * aboveKnee(highDb - absoluteThreshold));
      }
      reduction = desired + (desired > reduction ? attack : release) * (reduction - desired);
      if (reduction < 1e-12) reduction = 0;
      const removed = 1 - 10 ** (-reduction / 20);
      if (target === 'stereo' || shape.channels === 1) {
        for (let c = 0; c < channelCount; c++) {
          const x = buffer.ch[c][i];
          ch[c][i] = removed === 0 ? x : lcvrDeesserFloat(x - processors[c].high * removed);
        }
      } else {
        const change = -processors[0].high * removed;
        if (change === 0) { ch[0][i] = left[i]; ch[1][i] = right[i]; }
        else {
          ch[0][i] = lcvrDeesserFloat(left[i] + change);
          ch[1][i] = lcvrDeesserFloat(right[i] + sign * change);
        }
      }
      if (reduction > stats.maxReductionDb) stats.maxReductionDb = reduction;
      if (reduction >= 0.1) stats.affectedFrames++;
      totalReduction += reduction;
    }
    hooks.progress(0.1 + end / shape.frames * 0.9);
    await hooks.pause();
  }
  stats.meanReductionDb = totalReduction / shape.frames;
  hooks.progress(1);
  return { buffer: { ...buffer, ch }, settings: normalized, stats };
}

/** Difference monitoring is separate so normal previews allocate no extra audio. */
async function lcvrDeesserRemoved(original, processed, {
  maxBytes = 256 * 1024 * 1024, cancelled = () => false, onProgress = () => {}
} = {}) {
  const shape = lcvrDeesserShape(original, 'stereo', maxBytes);
  const other = lcvrDeesserShape(processed, 'stereo', maxBytes);
  if (original.rate !== processed.rate || shape.frames !== other.frames || shape.channels !== other.channels) {
    throw new RangeError('De-esser comparison audio must have matching channels, length and sample rate.');
  }
  const hooks = lcvrDeesserHooks(cancelled, onProgress);
  await lcvrDeesserValidateSamples(original, hooks, 0, 0.1);
  await lcvrDeesserValidateSamples(processed, hooks, 0.1, 0.2);
  const ch = original.ch.map(() => new Float32Array(shape.frames));
  for (let begin = 0; begin < shape.frames; begin += 32768) {
    const end = Math.min(shape.frames, begin + 32768);
    for (let c = 0; c < shape.channels; c++) {
      for (let i = begin; i < end; i++) ch[c][i] = lcvrDeesserFloat(original.ch[c][i] - processed.ch[c][i]);
    }
    hooks.progress(0.2 + end / shape.frames * 0.8);
    await hooks.pause();
  }
  hooks.progress(1);
  return { ...original, ch };
}

/** A high-band peak ceiling, separate from sibilance-sensitive de-essing. */
function lcvrHfLimiterSettings(settings = {}, rate = 48000) {
  if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
    throw new TypeError('Choose HF limiter Frequency and Ceiling settings.');
  }
  const frequency = lcvrDeesserSettings({ frequency: settings.frequency }, rate).frequency;
  const value = settings.ceilingDb === undefined ? -12 : settings.ceilingDb;
  const ceilingDb = Number(value);
  if (!['number', 'string'].includes(typeof value) || (typeof value === 'string' && !value.trim()) ||
      !Number.isFinite(ceilingDb) || ceilingDb < -36 || ceilingDb > 0) {
    throw new RangeError('HF limiter Ceiling must be between -36 and 0 dBFS.');
  }
  if (settings.enabled !== undefined && typeof settings.enabled !== 'boolean') {
    throw new TypeError('HF limiter enabled must be true or false.');
  }
  return { frequency, ceilingDb, enabled: settings.enabled !== false };
}

function lcvrHfSplit(frequency, rate) {
  const g = Math.tan(Math.PI * frequency / rate), splitG = g / (1 + g);
  let state = 0;
  return x => {
    const v = (x - state) * splitG, low = v + state;
    state = low + v;
    return x - low;
  };
}

/**
 * Offline split-band sample-peak limiter. A backwards attack pass anticipates
 * transients (1ms time constant), then a forward 50ms release pass smooths gain.
 * Both passes only LOWER the instantaneous ceiling-safe gain. No delay,
 * padding, trim or length change is introduced. There is no true-peak claim.
 * The ceiling measures the internal high branch, NOT reconstructed full audio.
 * Temporary storage: one Float32 gain per frame, plus final output channels.
 */
async function lcvrProcessHfLimiterCore(buffer, settings = {}, target = 'stereo', {
  maxBytes = 256 * 1024 * 1024, cancelled = () => false, onProgress = () => {}
} = {}) {
  const shape = lcvrDeesserShape(buffer, target, maxBytes);
  const normalized = lcvrHfLimiterSettings(settings, buffer.rate);
  const hooks = lcvrDeesserHooks(cancelled, onProgress);
  const stats = {
    maxReductionDb: 0, meanReductionDb: 0, affectedFrames: 0, frames: shape.frames, target,
    maxBandPeakBeforeDb: -Infinity, maxBandPeakAfterDb: -Infinity, ceilingDb: normalized.ceilingDb
  };
  const disabled = !normalized.enabled || !shape.frames;
  await lcvrDeesserValidateSamples(buffer, hooks, 0, disabled ? 1 : 0.08);
  if (disabled) { hooks.progress(1); return { buffer, settings: normalized, stats }; }
  const ceiling = 10 ** (normalized.ceilingDb / 20);
  const gainPlan = new Float32Array(shape.frames);
  const channelCount = target === 'stereo' ? shape.channels : 1;
  let split = Array.from({ length: channelCount }, () => lcvrHfSplit(normalized.frequency, buffer.rate));
  const left = buffer.ch[0], right = buffer.ch[1], sign = target === 'mid' ? 1 : -1;
  let peakBefore = 0, exceeded = false;
  for (let begin = 0; begin < shape.frames; begin += 32768) {
    const end = Math.min(shape.frames, begin + 32768);
    for (let i = begin; i < end; i++) {
      let highPeak = 0;
      if (target === 'stereo' || shape.channels === 1) {
        for (let c = 0; c < channelCount; c++) highPeak = Math.max(highPeak, Math.abs(split[c](buffer.ch[c][i])));
      } else highPeak = Math.abs(split[0](left[i] * 0.5 + right[i] * sign * 0.5));
      if (highPeak > peakBefore) peakBefore = highPeak;
      if (highPeak > ceiling) exceeded = true;
      gainPlan[i] = highPeak > ceiling ? ceiling / highPeak : 1;
    }
    hooks.progress(0.08 + end / shape.frames * 0.3);
    await hooks.pause();
  }
  stats.maxBandPeakBeforeDb = peakBefore ? 20 * Math.log10(peakBefore) : -Infinity;
  if (!exceeded) {
    stats.maxBandPeakAfterDb = stats.maxBandPeakBeforeDb;
    hooks.progress(1);
    return { buffer, settings: normalized, stats };
  }

  const attack = Math.exp(-1 / (buffer.rate * 0.001));
  const release = Math.exp(-1 / (buffer.rate * 0.050));
  let next = 1;
  for (let begin = shape.frames; begin > 0; begin -= 32768) {
    const end = Math.max(0, begin - 32768);
    for (let i = begin - 1; i >= end; i--) {
      const requested = gainPlan[i];
      next = Math.min(requested, requested + attack * (next - requested));
      gainPlan[i] = next;
    }
    hooks.progress(0.38 + (shape.frames - end) / shape.frames * 0.2);
    await hooks.pause();
  }

  const ch = buffer.ch.map(() => new Float32Array(shape.frames));
  split = Array.from({ length: channelCount }, () => lcvrHfSplit(normalized.frequency, buffer.rate));
  const highs = new Float64Array(channelCount);
  let previous = 1, peakAfter = 0, totalReduction = 0;
  for (let begin = 0; begin < shape.frames; begin += 32768) {
    const end = Math.min(shape.frames, begin + 32768);
    for (let i = begin; i < end; i++) {
      let highPeak = 0;
      if (target === 'stereo' || shape.channels === 1) {
        for (let c = 0; c < channelCount; c++) {
          highs[c] = split[c](buffer.ch[c][i]);
          highPeak = Math.max(highPeak, Math.abs(highs[c]));
        }
      } else {
        highs[0] = split[0](left[i] * 0.5 + right[i] * sign * 0.5);
        highPeak = Math.abs(highs[0]);
      }
      const requested = gainPlan[i];
      let gain = Math.min(requested, requested + release * (previous - requested));
      // Recheck in double precision after Float32 plan storage: rounding may
      // never push a high-band sample above the requested internal ceiling.
      gain = Math.min(1, gain, highPeak > ceiling ? ceiling / highPeak : 1);
      previous = gain;
      peakAfter = Math.max(peakAfter, highPeak * gain);
      const removed = 1 - gain;
      if (target === 'stereo' || shape.channels === 1) {
        for (let c = 0; c < channelCount; c++) {
          const x = buffer.ch[c][i];
          ch[c][i] = removed === 0 ? x : lcvrDeesserFloat(x - highs[c] * removed);
        }
      } else {
        const change = -highs[0] * removed;
        if (change === 0) { ch[0][i] = left[i]; ch[1][i] = right[i]; }
        else { ch[0][i] = lcvrDeesserFloat(left[i] + change); ch[1][i] = lcvrDeesserFloat(right[i] + sign * change); }
      }
      const reduction = -20 * Math.log10(gain);
      stats.maxReductionDb = Math.max(stats.maxReductionDb, reduction);
      if (reduction >= 0.1) stats.affectedFrames++;
      totalReduction += reduction;
    }
    hooks.progress(0.58 + end / shape.frames * 0.42);
    await hooks.pause();
  }
  stats.meanReductionDb = totalReduction / shape.frames;
  stats.maxBandPeakAfterDb = peakAfter ? 20 * Math.log10(peakAfter) : -Infinity;
  hooks.progress(1);
  return { buffer: { ...buffer, ch }, settings: normalized, stats };
}

// Text and optional mastering metadata. This sheet is not a DDP image.
// Audio samples, PQ timing, source filenames and machine cue labels are read-only.
function lcvrMetadataText(value, max = 160) {
  const limit = Number.isSafeInteger(max) && max >= 0 ? max : 160;
  const text = String(value ?? '').replace(/[\u0000-\u001f\u007f-\u009f]/g, '').trim();
  return Array.from(text).slice(0, limit).join('');
}

function lcvrNormalizeIsrc(value) {
  return String(value ?? '').toUpperCase().replace(/[\s-]/g, '');
}

function lcvrValidIsrc(value) {
  const code = lcvrNormalizeIsrc(value);
  return code === '' || /^[A-Z]{2}[A-Z0-9]{3}[0-9]{7}$/.test(code);
}

function lcvrNormalizeBarcode(value) {
  return String(value ?? '').replace(/[\s-]/g, '');
}

function lcvrValidBarcode(value) {
  const code = lcvrNormalizeBarcode(value);
  if (code === '') return true;
  if (!/^(?:[0-9]{12}|[0-9]{13})$/.test(code)) return false;
  let sum = 0;
  for (let i = 0; i < code.length; i++) {
    sum += Number(code[i]) * ((code.length - 1 - i) % 2 ? 3 : 1);
  }
  return sum % 10 === 0;
}

function lcvrMetadataCsv(release, tracks, timing, side) {
  if (!Array.isArray(tracks) || !timing || !Array.isArray(timing.rows) || timing.rows.length !== tracks.length) {
    throw new TypeError('Metadata requires one timing row per song.');
  }
  const rate = timing.rate;
  if (tracks.length && (!Number.isFinite(rate) || rate <= 0)) {
    throw new TypeError('Metadata requires the audio sample rate.');
  }
  const title = lcvrMetadataText(release?.title), artist = lcvrMetadataText(release?.artist);
  const catalog = lcvrMetadataText(release?.catalog), barcode = lcvrNormalizeBarcode(release?.barcode);
  // A separate, versioned sheet leaves the builder's 12-column PQ schema intact.
  const rows = [['Metadata version', 'Release title', 'Release artist', 'Catalog number', 'UPC/EAN', 'Side', 'Track', 'Track title', 'Track artist', 'ISRC', 'Source filename', 'Start', 'Duration', 'End', 'Gap after (seconds)', 'Sample rate (Hz)', 'Start frame', 'Duration frames', 'End frame']];
  timing.rows.forEach((row, index) => {
    const track = tracks[index];
    if (!track || row.track !== index + 1) throw new TypeError('Metadata timing must follow the current song order.');
    rows.push([
      'LCVR-METADATA-1', title, artist, catalog, barcode, side, row.track,
      lcvrMetadataText(track.name), lcvrMetadataText(track.artist) || artist,
      lcvrNormalizeIsrc(track.isrc), String(track.sourceName || track.name || ''),
      lcvrTimeAt(row.start, rate), lcvrTimeAt(row.frames, rate), lcvrTimeAt(row.end, rate),
      row.gap / rate, rate, row.start, row.frames, row.end
    ]);
  });
  return '\ufeff' + rows.map(row => row.map(lcvrCsvCell).join(',')).join('\r\n');
}

/* Export-only, band-limited sample-rate conversion. Working audio is unchanged.
 * Windowed-sinc FIR with a Kaiser window, bounded polyphase tables and zero
 * extension at file boundaries. The sample at output time zero stays at zero.
 */
function lcvrExportFrameCount(frames, sourceRate, targetRate) {
  if (!Number.isSafeInteger(frames) || frames < 0 ||
      !Number.isInteger(sourceRate) || sourceRate < 8000 || sourceRate > 96000 ||
      !Number.isInteger(targetRate) || targetRate < 8000 || targetRate > 96000) {
    throw new TypeError('Invalid sample-rate conversion dimensions.');
  }
  const result = Math.round(frames * targetRate / sourceRate);
  if (!Number.isSafeInteger(result)) throw new RangeError('Invalid sample-rate conversion dimensions.');
  return result;
}

function lcvrExportAudioShape(audio) {
  if (!audio || !Array.isArray(audio.ch) || audio.ch.length < 1 || audio.ch.length > 2 ||
      !audio.ch.every(channel => channel instanceof Float32Array) ||
      !audio.ch[0].length || !audio.ch.every(channel => channel.length === audio.ch[0].length)) {
    throw new TypeError('Sample-rate conversion requires nonempty mono or stereo audio.');
  }
  lcvrExportFrameCount(audio.ch[0].length, audio.rate, audio.rate);
  return { frames: audio.ch[0].length, channels: audio.ch.length, bytes: audio.ch[0].length * audio.ch.length * 4 };
}

/** No PCM allocation: each planned channel is a frame-length descriptor. */
function lcvrExportPlan(inputTracks, transition, targetRate, maxBytes = 256 * 1024 * 1024) {
  if (!Array.isArray(inputTracks) || !inputTracks.length ||
      !Number.isSafeInteger(maxBytes) || maxBytes < 0 || !transition ||
      !['gap', 'cross', 'none'].includes(transition.mode)) {
    throw new TypeError('Invalid sample-rate export plan.');
  }
  const gapSeconds = transition.mode === 'gap' ? transition.gap : 0;
  const crossSeconds = transition.mode === 'cross' ? transition.cross : 0;
  if (!Number.isFinite(gapSeconds) || gapSeconds < 0 || !Number.isFinite(crossSeconds) || crossSeconds < 0) {
    throw new TypeError('Invalid sample-rate export plan.');
  }
  const sourceRate = inputTracks[0].rate;
  let convertedBytes = 0;
  const tracks = inputTracks.map(track => {
    const shape = lcvrExportAudioShape(track);
    if (typeof track.name !== 'string' || track.rate !== sourceRate) throw new TypeError('Invalid sample-rate export plan.');
    const frames = lcvrExportFrameCount(shape.frames, track.rate, targetRate);
    if (!frames) throw new RangeError('This audio is too short for the selected sample rate.');
    convertedBytes += frames * shape.channels * 4;
    const markers = (track.markers || []).map(marker => {
      const copy = lcvrMarkerCopy(marker);
      if (copy.frame > shape.frames) throw new TypeError('Invalid track markers.');
      return lcvrMarkerCopy(copy, lcvrExportFrameCount(copy.frame, track.rate, targetRate));
    });
    return { ...track, rate: targetRate, ch: Array.from({ length: shape.channels }, () => ({ length: frames })), markers };
  });
  const channels = Math.max(...tracks.map(track => track.ch.length)), starts = [0], overlaps = [];
  let frames = tracks[0].ch[0].length;
  for (let index = 1; index < tracks.length; index++) {
    const previous = tracks[index - 1].ch[0].length, current = tracks[index].ch[0].length;
    const overlap = transition.mode === 'cross'
      ? Math.max(0, Math.min(Math.round(crossSeconds * targetRate), Math.floor(previous / 2), Math.floor(current / 2))) : 0;
    const gap = transition.mode === 'gap' ? Math.round(gapSeconds * targetRate) : 0;
    overlaps.push(overlap); starts.push(frames + gap - overlap); frames += gap + current - overlap;
  }
  const bytes = frames * channels * 4;
  if (!Number.isSafeInteger(bytes) || !Number.isSafeInteger(convertedBytes) || bytes > maxBytes || convertedBytes > maxBytes) {
    throw new RangeError('This export exceeds the audio memory limit at the selected sample rate. Choose a lower sample rate or shorten the side.');
  }
  return { rate: targetRate, frames, channels, bytes, convertedBytes, tracks, starts, overlaps,
    timing: sideAudioTiming(tracks, starts), marks: sideAudioMarkers(tracks, starts), exportMarks: sideExportMarkers(tracks, starts) };
}

/** Bounded, deterministic design; standard sample rates use exact rational phases. */
function lcvrResampleDesign(sourceRate, targetRate) {
  lcvrExportFrameCount(1, sourceRate, targetRate);
  let a = sourceRate, b = targetRate;
  while (b) { const remainder = a % b; a = b; b = remainder; }
  const scale = Math.min(1, targetRate / sourceRate), half = Math.ceil(64 / scale), taps = 2 * half + 1;
  const denominator = targetRate / a, step = sourceRate / a;
  const maximumPhases = Math.min(2048, Math.floor(16 * 1024 * 1024 / (taps * 8)) - 1);
  const exact = denominator <= maximumPhases, phases = exact ? denominator : maximumPhases;
  const rows = phases + (exact ? 0 : 1);
  return { half, taps, cutoff: .475 * scale, beta: 9, denominator, step, exact, phases, rows, bytes: rows * taps * 8 };
}

function lcvrResampleI0(value) {
  const quarter = value * value / 4;
  let term = 1, sum = 1;
  for (let index = 1; index < 40; index++) {
    term *= quarter / (index * index); sum += term;
    if (term < sum * 1e-16) break;
  }
  return sum;
}

/**
 * Polyphase, zero-centered windowed-sinc interpolation. The kernel cutoff is
 * 95% of the lower Nyquist rate, with a pass band through 90% of that Nyquist.
 * Source positions use integer ratios, so long files do not accumulate drift.
 * Every phase has unity DC gain; no sample peak normalization is performed.
 * General background: Smith, Windowed Sinc Interpolation; SciPy resample_poly.
 */
async function lcvrResampleAudio(audio, targetRate, options = {}) {
  const shape = lcvrExportAudioShape(audio), frames = lcvrExportFrameCount(shape.frames, audio.rate, targetRate);
  const maxOutputBytes = options.maxOutputBytes ?? 256 * 1024 * 1024;
  const maxWorkingBytes = options.maxWorkingBytes ?? 512 * 1024 * 1024;
  const yieldTask = options.yieldTask || (() => new Promise(resolve => setTimeout(resolve, 0)));
  const cancelled = options.cancelled || (() => false), onProgress = options.onProgress || (() => {});
  if (!Number.isSafeInteger(maxOutputBytes) || maxOutputBytes < 0 || !Number.isSafeInteger(maxWorkingBytes) || maxWorkingBytes < 0 ||
      typeof yieldTask !== 'function' || typeof cancelled !== 'function' || typeof onProgress !== 'function') {
    throw new TypeError('Invalid sample-rate conversion options.');
  }
  const check = () => { if (cancelled()) throw new Error('Sample-rate conversion cancelled.'); };
  check();
  if (!frames) throw new RangeError('This audio is too short for the selected sample rate.');
  const outputBytes = frames * shape.channels * 4;
  if (outputBytes > maxOutputBytes) {
    throw new RangeError('This export exceeds the audio memory limit at the selected sample rate. Choose a lower sample rate or shorten the side.');
  }
  // Original and explicit same-rate exports retain every sample exactly.
  if (targetRate === audio.rate) return audio;
  const design = lcvrResampleDesign(audio.rate, targetRate);
  if (shape.bytes + outputBytes + design.bytes > maxWorkingBytes) {
    throw new RangeError('This export exceeds the audio memory limit at the selected sample rate. Choose a lower sample rate or shorten the side.');
  }
  const now = () => globalThis.performance?.now ? globalThis.performance.now() : Date.now();
  let sliceStart = now(), lastProgress = 0;
  const report = value => { check(); const progress = Math.max(lastProgress, Math.min(1, value)); lastProgress = progress; onProgress(progress); check(); };
  report(0); await yieldTask(); check(); sliceStart = now();
  // Validate before filtering: an invalid sample must not spread through a song.
  for (let channel = 0; channel < shape.channels; channel++) {
    const data = audio.ch[channel];
    for (let start = 0; start < shape.frames; start += 65536) {
      check(); const end = Math.min(shape.frames, start + 65536);
      for (let index = start; index < end; index++) if (!Number.isFinite(data[index])) throw new TypeError('Audio contains a non-finite sample.');
      if (now() - sliceStart >= 10 || end === shape.frames) {
        report(.03 * (channel * shape.frames + end) / (shape.channels * shape.frames));
        await yieldTask(); check(); sliceStart = now();
      }
    }
  }
  const { half, taps, phases, rows, cutoff, beta, denominator, step, exact } = design;
  let kernel, ch;
  try {
    kernel = new Float64Array(rows * taps);
    ch = Array.from({ length: shape.channels }, () => new Float32Array(frames));
  } catch (_) {
    throw new Error('There is not enough memory to convert this audio. Choose a lower sample rate or shorten the side.');
  }
  const windowScale = lcvrResampleI0(beta);
  for (let phase = 0; phase < rows; phase++) {
    check(); const fraction = phase / phases, offset = phase * taps;
    let sum = 0;
    for (let tap = 0; tap < taps; tap++) {
      const distance = tap - half - fraction;
      let weight = 0;
      if (Math.abs(distance) <= half) {
        const angle = 2 * Math.PI * cutoff * distance;
        const sinc = Math.abs(angle) < 1e-12 ? 1 : Math.sin(angle) / angle;
        const window = lcvrResampleI0(beta * Math.sqrt(Math.max(0, 1 - (distance / half) ** 2))) / windowScale;
        weight = 2 * cutoff * sinc * window;
      }
      kernel[offset + tap] = weight; sum += weight;
    }
    if (!Number.isFinite(sum) || Math.abs(sum) < .5) throw new Error('The sample-rate filter could not be prepared.');
    for (let tap = 0; tap < taps; tap++) kernel[offset + tap] /= sum;
    if (now() - sliceStart >= 10 || phase + 1 === rows) {
      report(.03 + .07 * (phase + 1) / rows); await yieldTask(); check(); sliceStart = now();
    }
  }
  const left = audio.ch[0], right = audio.ch[1], outLeft = ch[0], outRight = ch[1];
  // A bounded operation block checks the clock. Yield after ~10 ms of work,
  // avoiding both long main-thread stalls and a timer wait for every sample block.
  const blockFrames = Math.max(32, Math.floor(131072 / (taps * shape.channels)));
  for (let begin = 0; begin < frames; begin += blockFrames) {
    check(); const end = Math.min(frames, begin + blockFrames);
    for (let index = begin; index < end; index++) {
      const numerator = index * step, center = Math.floor(numerator / denominator), remainder = numerator % denominator;
      const phasePosition = exact ? remainder : remainder * phases / denominator;
      const phase = Math.floor(phasePosition), blend = exact ? 0 : phasePosition - phase;
      const offset = phase * taps, start = center - half;
      const first = Math.max(0, -start), last = Math.min(taps, shape.frames - start);
      let l = 0, r = 0;
      if (exact || blend === 0) {
        if (right) for (let tap = first; tap < last; tap++) {
          const weight = kernel[offset + tap], at = start + tap;
          l += left[at] * weight; r += right[at] * weight;
        } else for (let tap = first; tap < last; tap++) l += left[start + tap] * kernel[offset + tap];
      } else {
        if (right) for (let tap = first; tap < last; tap++) {
          const weight = kernel[offset + tap] + (kernel[offset + taps + tap] - kernel[offset + tap]) * blend, at = start + tap;
          l += left[at] * weight; r += right[at] * weight;
        } else for (let tap = first; tap < last; tap++) {
          const weight = kernel[offset + tap] + (kernel[offset + taps + tap] - kernel[offset + tap]) * blend;
          l += left[start + tap] * weight;
        }
      }
      outLeft[index] = l; if (outRight) outRight[index] = r;
      if (!Number.isFinite(outLeft[index]) || (outRight && !Number.isFinite(outRight[index]))) throw new Error('Sample-rate conversion produced invalid audio. Export stopped.');
    }
    if (now() - sliceStart >= 10 || end === frames) {
      report(.1 + .9 * end / frames); await yieldTask(); check(); sliceStart = now();
    }
  }
  check(); report(1);
  return { ...audio, rate: targetRate, ch };
}

/* Left/Right views wrap the existing Stereo/Mid/Side processors. The unchosen
 * channel is shared unchanged; previews project only the chosen channel.
 * Processing validates both source channels before sharing either of them.
 */
function lcvrChannelTarget(target) {
  return target === 'left' || target === 'right';
}
function lcvrStereoOnlyComponent(target) {
  return target === 'side' || lcvrChannelTarget(target);
}
async function lcvrChannelInput(buffer, target, {
  maxBytes = 256 * 1024 * 1024, cancelled = () => false, onProgress = () => {}
} = {}) {
  const shape = lcvrEqShape(buffer, 'stereo', maxBytes);
  if (!lcvrChannelTarget(target)) throw new TypeError('Choose Left or Right for a single-channel edit.');
  if (shape.channels !== 2) throw new RangeError('Left and Right editing require a stereo file.');
  const hooks = lcvrEqHooks(cancelled, onProgress), index = target === 'left' ? 0 : 1;
  await lcvrEqValidateSamples(buffer, hooks, 0.1);
  hooks.progress(0.1);
  return {
    index, hooks, mono: { ...buffer, ch: [buffer.ch[index]] },
    options: { maxBytes, cancelled, onProgress: value => hooks.progress(0.1 + value * 0.9) }
  };
}
function lcvrRestoreChannel(buffer, processed, index) {
  if (processed.ch[0] === buffer.ch[index]) return buffer;
  const ch = buffer.ch.slice();
  ch[index] = processed.ch[0];
  return { ...buffer, ch };
}
async function lcvrProjectChannels(buffer, target = 'stereo', options = {}) {
  if (!lcvrChannelTarget(target)) return lcvrProjectChannelsCore(buffer, target, options);
  const input = await lcvrChannelInput(buffer, target, options);
  input.hooks.progress(1);
  return input.mono;
}
async function lcvrProcessTargetGain(buffer, gainDb, target = 'stereo', options = {}) {
  if (!lcvrChannelTarget(target)) return lcvrProcessTargetGainCore(buffer, gainDb, target, options);
  const input = await lcvrChannelInput(buffer, target, options);
  const processed = await lcvrProcessTargetGainCore(input.mono, gainDb, 'stereo', input.options);
  input.hooks.check();
  return lcvrRestoreChannel(buffer, processed, input.index);
}
async function lcvrProcessTargetEq(buffer, bands, target = 'stereo', options = {}) {
  if (!lcvrChannelTarget(target)) return lcvrProcessTargetEqCore(buffer, bands, target, options);
  const input = await lcvrChannelInput(buffer, target, options);
  const processed = await lcvrProcessTargetEqCore(input.mono, bands, 'stereo', input.options);
  input.hooks.check();
  return lcvrRestoreChannel(buffer, processed, input.index);
}
async function lcvrProcessDeesser(buffer, settings = {}, target = 'stereo', options = {}) {
  if (!lcvrChannelTarget(target)) return lcvrProcessDeesserCore(buffer, settings, target, options);
  const input = await lcvrChannelInput(buffer, target, options);
  const result = await lcvrProcessDeesserCore(input.mono, settings, 'stereo', input.options);
  input.hooks.check();
  return { ...result, buffer: lcvrRestoreChannel(buffer, result.buffer, input.index), stats: { ...result.stats, target } };
}
async function lcvrProcessHfLimiter(buffer, settings = {}, target = 'stereo', options = {}) {
  if (!lcvrChannelTarget(target)) return lcvrProcessHfLimiterCore(buffer, settings, target, options);
  const input = await lcvrChannelInput(buffer, target, options);
  const result = await lcvrProcessHfLimiterCore(input.mono, settings, 'stereo', input.options);
  input.hooks.check();
  return { ...result, buffer: lcvrRestoreChannel(buffer, result.buffer, input.index), stats: { ...result.stats, target } };
}
/** Convert an already validated/projected editor buffer into speaker channels.
 * Left/Right stay on their physical speaker; Mid is centred and Side is L=-R.
 * These monitor arrays are never committed to the project or used for export.
 */
function lcvrMonitorChannels(buffer, target = 'stereo') {
  const shape = lcvrEqShape(buffer, 'stereo', 256 * 1024 * 1024);
  if (!['stereo', 'left', 'right', 'mid', 'side'].includes(target)) {
    throw new TypeError('Choose Stereo, Left, Right, Mid or Side.');
  }
  if (target !== 'stereo' && shape.channels !== 1) {
    throw new TypeError('Project the selected channel or component before monitoring.');
  }
  const first = buffer.ch[0];
  if (target === 'left') return [first, new Float32Array(shape.frames)];
  if (target === 'right') return [new Float32Array(shape.frames), first];
  if (target === 'side') {
    const right = new Float32Array(shape.frames);
    for (let i = 0; i < shape.frames; i++) right[i] = -first[i];
    return [first, right];
  }
  return [first, buffer.ch[Math.min(1, shape.channels - 1)]];
}
/** Full-file analysis uses the projected mono signal. Report the actual solo
 * monitor's M/S levels and undefined inter-channel phase in Left/Right views.
 */
function lcvrChannelMetrics(metrics, target) {
  if (!metrics || !lcvrChannelTarget(target)) return metrics;
  const componentLevel = metrics.rms - 20 * Math.log10(2);
  return { ...metrics, correlation: NaN, phaseKind: Number.isFinite(metrics.rms) ? 'one_channel' : metrics.phaseKind,
    mid: componentLevel, sideLevel: componentLevel };
}

function init(root){
const $=n=>root.querySelector('[data-el="'+n+'"]'),q=s=>Array.from(root.querySelectorAll(s));
let messages={};try{messages=JSON.parse($('messages')?.textContent||'{}');}catch(e){console.warn('LCVR editor translations:',e);}
const tr=(key,fallback,values={})=>String(messages[key]??fallback).replace(/\{(\w+)\}/g,(match,k)=>Object.prototype.hasOwnProperty.call(values,k)?String(values[k]):match);
const helperErrorKeys={"Invalid audio buffer for transient search.":"error_invalid_audio_buffer_for_transient_search","Transient search cancelled.":"error_transient_search_cancelled","Invalid audio sample for transient search.":"error_invalid_audio_sample_for_transient_search","Audio must have a positive sample rate and Float32 channels.":"error_audio_must_have_a_positive_sample_rate_and_float32_channels","Audio channels must be Float32Array values.":"error_audio_channels_must_be_float32array_values","Audio channels must have equal lengths.":"error_audio_channels_must_have_equal_lengths","Audio buffer size is too large.":"error_audio_buffer_size_is_too_large","Audio edit callbacks must be functions.":"error_audio_edit_callbacks_must_be_functions","Audio edit cancelled.":"error_audio_edit_cancelled","The insertion time must be inside the audio.":"error_the_insertion_time_must_be_inside_the_audio","Silence duration must be a finite, nonnegative number.":"error_silence_duration_must_be_a_finite_nonnegative_number","The audio memory limit must be a nonnegative safe integer.":"error_the_audio_memory_limit_must_be_a_nonnegative_safe_integer","Inserting this silence exceeds the audio memory limit.":"error_inserting_this_silence_exceeds_the_audio_memory_limit","Audio contains a non-finite sample.":"error_audio_contains_a_non_finite_sample","The DC offset is outside the supported range.":"error_the_dc_offset_is_outside_the_supported_range","Removing DC offset would exceed the Float32 sample range.":"error_removing_dc_offset_would_exceed_the_float32_sample_range","Marker positions must be non-negative integer sample frames.":"error_marker_positions_must_be_non_negative_integer_sample_frames","Audio markers require a name and sample frame.":"error_audio_markers_require_a_name_and_sample_frame","Invalid audio marker edit.":"error_invalid_audio_marker_edit","Audio edit end precedes its start.":"error_audio_edit_end_precedes_its_start","Each track requires its side start frame.":"error_each_track_requires_its_side_start_frame","Invalid track markers.":"error_invalid_track_markers"};
helperErrorKeys["Audio channels must be Float32Array values."]="error_audio_channels_must_be_float32array_values";
helperErrorKeys["Audio channels must have equal lengths."]="error_audio_channels_must_have_equal_lengths";
helperErrorKeys["Audio contains a non-finite sample."]="error_audio_contains_a_non_finite_sample";
helperErrorKeys["Audio edit callbacks must be functions."]="error_audio_edit_callbacks_must_be_functions";
helperErrorKeys["Audio edit cancelled."]="error_audio_edit_cancelled";
helperErrorKeys["Choose Stereo, Mid or Side."]="error_choose_stereo_mid_or_side";
helperErrorKeys["Choose a low shelf, bell, high shelf, high-pass or low-pass filter."]="error_choose_a_low_shelf_bell_high_shelf_high_pass_or_low_pass_filter";
helperErrorKeys["EQ coefficients must contain five finite numbers."]="error_eq_coefficients_must_contain_five_finite_numbers";
helperErrorKeys["EQ frequency must be a positive number."]="error_eq_frequency_must_be_a_positive_number";
helperErrorKeys["EQ gain must be between -24 and 24 dB."]="error_eq_gain_must_be_between_24_and_24_db";
helperErrorKeys["EQ requires an array of up to 16 bands."]="error_eq_requires_an_array_of_up_to_16_bands";
helperErrorKeys["EQ requires an audio sample rate from 8 to 192 kHz."]="error_eq_requires_an_audio_sample_rate_from_8_to_192_khz";
helperErrorKeys["EQ response frequency must be between zero and Nyquist."]="error_eq_response_frequency_must_be_between_zero_and_nyquist";
helperErrorKeys["EQ width must be between 0.1 and 20 Q."]="error_eq_width_must_be_between_0_1_and_20_q";
helperErrorKeys["Filter enabled must be true or false."]="error_filter_enabled_must_be_true_or_false";
helperErrorKeys["Filter slope must be 12 or 24 dB/octave."]="error_filter_slope_must_be_12_or_24_db_octave";
helperErrorKeys["Gain must be between -120 and 60 dB."]="error_gain_must_be_between_120_and_60_db";
helperErrorKeys["Mid/Side editing requires mono or stereo audio."]="error_mid_side_editing_requires_mono_or_stereo_audio";
helperErrorKeys["Side editing requires a stereo file."]="error_side_editing_requires_a_stereo_file";
helperErrorKeys["The EQ response is outside the supported range."]="error_the_eq_response_is_outside_the_supported_range";
helperErrorKeys["The EQ settings produced an invalid filter."]="error_the_eq_settings_produced_an_invalid_filter";
helperErrorKeys["The audio memory limit must be a nonnegative safe integer."]="error_the_audio_memory_limit_must_be_a_nonnegative_safe_integer";
helperErrorKeys["This edit exceeds the audio memory limit."]="error_this_edit_exceeds_the_audio_memory_limit";
helperErrorKeys["This edit would exceed the Float32 audio range."]="error_this_edit_would_exceed_the_float32_audio_range";
helperErrorKeys["Use cascaded EQ coefficients for a 24 dB/octave filter."]="error_use_cascaded_eq_coefficients_for_a_24_db_octave_filter";
helperErrorKeys["The de-esser requires a sample rate from 8 to 192 kHz."]="error_the_de_esser_requires_a_sample_rate_from_8_to_192_khz";
helperErrorKeys["Choose de-esser Amount and Frequency settings."]="error_choose_de_esser_amount_and_frequency_settings";
helperErrorKeys["De-esser Amount must be between 0 and 100."]="error_de_esser_amount_must_be_between_0_and_100";
helperErrorKeys["De-esser Frequency must be a positive number."]="error_de_esser_frequency_must_be_a_positive_number";
helperErrorKeys["The de-esser requires mono or stereo audio."]="error_the_de_esser_requires_mono_or_stereo_audio";
helperErrorKeys["Choose Stereo, Mid or Side."]="error_choose_stereo_mid_or_side";
helperErrorKeys["Side editing requires a stereo file."]="error_side_editing_requires_a_stereo_file";
helperErrorKeys["Audio channels must be Float32Array values."]="error_audio_channels_must_be_float32array_values";
helperErrorKeys["Audio channels must have equal lengths."]="error_audio_channels_must_have_equal_lengths";
helperErrorKeys["The audio memory limit must be a nonnegative safe integer."]="error_the_audio_memory_limit_must_be_a_nonnegative_safe_integer";
helperErrorKeys["This de-esser edit exceeds the audio memory limit."]="error_this_de_esser_edit_exceeds_the_audio_memory_limit";
helperErrorKeys["Audio edit callbacks must be functions."]="error_audio_edit_callbacks_must_be_functions";
helperErrorKeys["De-esser processing cancelled."]="error_de_esser_processing_cancelled";
helperErrorKeys["Audio contains a non-finite sample."]="error_audio_contains_a_non_finite_sample";
helperErrorKeys["This de-esser edit would exceed the Float32 audio range."]="error_this_de_esser_edit_would_exceed_the_float32_audio_range";
helperErrorKeys["De-esser comparison audio must have matching channels, length and sample rate."]="error_de_esser_comparison_audio_must_have_matching_channels_length_and_sample_rate";
helperErrorKeys["Choose HF limiter Frequency and Ceiling settings."]="error_choose_hf_limiter_frequency_and_ceiling_settings";
helperErrorKeys["HF limiter Ceiling must be between -36 and 0 dBFS."]="error_hf_limiter_ceiling_must_be_between_36_and_0_dbfs";
helperErrorKeys["HF limiter enabled must be true or false."]="error_hf_limiter_enabled_must_be_true_or_false";
helperErrorKeys['This side is too large for a standard WAV file.']='wav_size_limit';
const exportRateErrors={
"Audio contains a non-finite sample.":()=>tr("export_rate_error_audio_contains_a_non_finite_sample","Audio contains a non-finite sample."),
"Invalid sample-rate conversion dimensions.":()=>tr("export_rate_error_invalid_sample_rate_conversion_dimensions","Invalid sample-rate conversion dimensions."),
"Invalid sample-rate conversion options.":()=>tr("export_rate_error_invalid_sample_rate_conversion_options","Invalid sample-rate conversion options."),
"Invalid sample-rate export plan.":()=>tr("export_rate_error_invalid_sample_rate_export_plan","Invalid sample-rate export plan."),
"Invalid track markers.":()=>tr("export_rate_error_invalid_track_markers","Invalid track markers."),
"Sample-rate conversion cancelled.":()=>tr("export_rate_error_sample_rate_conversion_cancelled","Sample-rate conversion cancelled."),
"Sample-rate conversion produced invalid audio. Export stopped.":()=>tr("export_rate_error_sample_rate_conversion_produced_invalid_audio_export_stopped","Sample-rate conversion produced invalid audio. Export stopped."),
"Sample-rate conversion requires nonempty mono or stereo audio.":()=>tr("export_rate_error_sample_rate_conversion_requires_nonempty_mono_or_stereo_audio","Sample-rate conversion requires nonempty mono or stereo audio."),
"The sample-rate filter could not be prepared.":()=>tr("export_rate_error_the_sample_rate_filter_could_not_be_prepared","The sample-rate filter could not be prepared."),
"There is not enough memory to convert this audio. Choose a lower sample rate or shorten the side.":()=>tr("export_rate_error_there_is_not_enough_memory_to_convert_this_audio_choose_a_lower_sample_rate_or_shorten_the_side","There is not enough memory to convert this audio. Choose a lower sample rate or shorten the side."),
"This audio is too short for the selected sample rate.":()=>tr("export_rate_error_this_audio_is_too_short_for_the_selected_sample_rate","This audio is too short for the selected sample rate."),
"This export exceeds the audio memory limit at the selected sample rate. Choose a lower sample rate or shorten the side.":()=>tr("export_rate_error_this_export_exceeds_the_audio_memory_limit_at_the_selected_sample_rate_choose_a_lower_sample_rate_or_shorten_the_side","This export exceeds the audio memory limit at the selected sample rate. Choose a lower sample rate or shorten the side.")
};
const channelViewErrors={
"Choose Left or Right for a single-channel edit.":()=>tr("channel_view_error_choose_left_or_right_for_a_single_channel_edit","Choose Left or Right for a single-channel edit."),
"Choose Stereo, Left, Right, Mid or Side.":()=>tr("channel_view_error_choose_stereo_left_right_mid_or_side","Choose Stereo, Left, Right, Mid or Side."),
"Left and Right editing require a stereo file.":()=>tr("channel_view_error_left_and_right_editing_require_a_stereo_file","Left and Right editing require a stereo file."),
"Project the selected channel or component before monitoring.":()=>tr("channel_view_error_project_the_selected_channel_or_component_before_monitoring","Project the selected channel or component before monitoring.")
};
const errorText=error=>{const text=error?.message||String(error),key=helperErrorKeys[text];return key?tr(key,text):exportRateErrors[text]?exportRateErrors[text]():channelViewErrors[text]?channelViewErrors[text]():text;};
const listeners=new AbortController(),on=(node,type,handler,options={})=>node.addEventListener(type,handler,{...options,signal:listeners.signal});
let tracks=[],selected=0,viewBuffer=null,sourceBuffer=null,sideCache=null,metrics=null,undo=null;
const HISTORY_STATES=24,HISTORY_EXTRA_BYTES=MAX;
let historyEntries=[],historyIndex=-1,historyPending=null,historyRestoring=false,historyEarliestPruned=false,historyRetainedBytes=0,historyRevision=0,historyRenderedRevision=-1;
let cleanupDraft=null,cleanupRemoved=null,cleanupBypass=false,cleanupMonitor=false;
let releaseInfo={title:'',artist:'',catalog:'',barcode:''};
let eqBands=lcvrEqDefaultBands(44100),eqBandIndex=0,eqBypassed=false,eqGesture=null,playEqNodes=[],eqInvalidFields=new Set();
let selection=[0,0],cursor=0,busy=false,dead=false,context=null,source=null,splitter=null,meters=[];
let playing=false,started=0,offset=0,playEnd=0,raf=0,lastFrame=0,urls=[],playToken=0;
let playStarting=false,playAnchor=0,playPaused=false,pausedEnd=0,transportSpaceDown=false;
let transition={mode:'gap',gap:2,cross:1};
const meterState=[{value:0,hold:0,until:0},{value:0,hold:0,until:0}];
const canvas=$('wave'),base=document.createElement('canvas'),renderCache=new WeakMap(),peakCache=new WeakMap();
const transientCache=new WeakMap();let transientMode=false;
let w=800,h=280,dpr=1,v0=0,v1=0,spec=null,specTimer=0,specToken=0,drag=null,draft=null,modal=null,placeholder=null;
const pointers=new Map(),transport=root.querySelector('[data-act="play"]');
let gestureFrame=null,layoutDeferred=false,pinchFrame=null;
const assertAlive=()=>{if(dead)throw Error(tr('closed','Editor closed.'));};
const pause=async()=>{await yieldTask();assertAlive();};
const note=(text,error=false,percent=null)=>{if(dead)return;$('status').textContent=text;$('status').dataset.error=String(error);const progress=$('progress');if(progress&&busy){progress.hidden=false;if(Number.isFinite(percent))progress.value=clamp(percent,0,100);else progress.removeAttribute('value');}};
const current=()=>tracks[selected],sideView=()=>$('view').value==='side';
const duration=()=>viewBuffer?viewBuffer.ch[0].length/viewBuffer.rate:0,hasSelection=()=>selection[1]>selection[0];
const minZoom=()=>Math.min(duration(),8/(viewBuffer?.rate||8000));
const snap=t=>viewBuffer?Math.round(clamp(t,0,duration())*viewBuffer.rate)/viewBuffer.rate:0;
const projectBytes=a=>a.reduce((n,t)=>n+t.ch[0].length*t.ch.length*4,0);
const xAt=t=>(t-v0)/(v1-v0||1)*w,tAt=x=>clamp(v0+x/w*(v1-v0),0,duration());
const fadeState=()=>draft||current()?.fade||emptyFade();
function invalidateDownloads(){urls.forEach(URL.revokeObjectURL);urls=[];['download','csv','pq','metadataDownload'].forEach(key=>{const link=$(key);if(link){link.hidden=true;link.removeAttribute('href');link.removeAttribute('download');}});}
function invalidate(){discardCleanup();sideCache=null;invalidateDownloads();}
function checkpoint(label){historyCheckpoint(label);}
function updateControls(){
 if(dead)return;historyCommit();
 const loaded=!!viewBuffer,blocked=busy||!!drag||!!eqGesture;
 if(!loaded&&transientMode)setTransientMode(false);
 q('[data-act]').forEach(b=>{const a=b.dataset.act;let off=blocked||!loaded;if(a==='split')off=off||sideView()||!splitBoundaryAvailable()||eqDirty()||eqInvalidFields.size>0;if(a==='pause')off=off||(!playing&&!playStarting);if(a==='reset')off=blocked||!tracks.length;if(a==='undo')off=blocked||!undo;if(['cut','trim','trimStart','trimEnd','setFades','resetFades','insertSilence','insertSilenceStart','insertSilenceEnd','addMarker'].includes(a))off=off||sideView();if(['cut','trim','playSelection'].includes(a))off=off||!hasSelection();if(['trimStart','trimEnd'].includes(a))off=off||!trimBoundaryAvailable();if(['cut','trim','trimStart','trimEnd'].includes(a))off=off||eqDirty()||eqInvalidFields.size>0;if(['setFades','resetFades','normalize','removeDc','mono','stereo'].includes(a))off=off||component()!=='stereo';if(a==='applyEq')off=off||!eqDirty()||eqInvalidFields.size>0;if(a==='resetEq')off=off||(!eqDirty()&&!eqInvalidFields.size);if(['export','pq','markers','metadata'].includes(a))off=off||eqDirty()||eqInvalidFields.size>0;b.disabled=off;});
 q('[data-track]').forEach(b=>b.disabled=blocked);
 q('[data-marker],[data-marker-name]').forEach(b=>b.disabled=blocked||sideView());
 q('[data-ui]').forEach(b=>{if(b.dataset.ui!=='expand'&&b.dataset.ui!=='load')b.disabled=blocked||!loaded;});
 $('files').disabled=blocked||tracks.length>=5;root.querySelector('[data-ui="load"]').disabled=$('files').disabled;
 $('view').disabled=blocked||!tracks.length;$('song').disabled=blocked||!tracks.length;
 ['start','end','transition','gap','cross','gain','fadeInTime','fadeOutTime','inCurve','outCurve','peakTarget','lufsTarget','ceiling','normMode','bits','side','display','tool','silenceSeconds'].forEach(k=>$(k).disabled=blocked);
 if($('releaseTitle'))$('releaseTitle').disabled=blocked;
 if($('exportRate'))$('exportRate').disabled=blocked;
 if($('component')){$('component').disabled=blocked||!loaded;['side','left','right'].forEach(target=>{const option=$('component').querySelector('option[value="'+target+'"]');if(option)option.disabled=!sourceBuffer||sourceBuffer.ch.length!==2;});}
 ['eqFrequency','eqGain','eqQ','eqBypass','eqEnabled','eqSlope'].forEach(key=>{if($(key))$(key).disabled=blocked||!loaded||(key==='eqQ'&&eqBands[eqBandIndex].type!=='peaking');});
 q('[data-eq-band]').forEach(button=>button.disabled=blocked||!loaded);
 if(component()!=='stereo')['fadeInTime','fadeOutTime','inCurve','outCurve'].forEach(key=>$(key).disabled=true);
 $('pan').disabled=blocked||!loaded||duration()<=v1-v0+.000001;
 cleanupUpdateControls(blocked,loaded);updateMetadataControls(blocked,loaded);updateEditSectionControls();updateHistoryControls();setTransportLabel();
}
async function run(work){if(metadataDirty()){metadataPending();return;}if(busy||dead||drag||eqGesture)return;busy=true;root.setAttribute('aria-busy','true');const progress=$('progress');if(progress){progress.max=100;progress.hidden=false;progress.removeAttribute('value');}try{stop(false);updateControls();await pause();await work();}catch(e){if(!dead){console.error('LCVR editor:',e);note(errorText(e),true);}}finally{busy=false;if(!dead){root.setAttribute('aria-busy','false');if(progress)progress.hidden=true;updateControls();}}}
function num(k,min,max){const v=Number($(k).value);if(!Number.isFinite(v)||v<min||v>max){const labels={"gap":tr("field_gap","gap duration"),"cross":tr("field_cross","crossfade duration"),"fadeInTime":tr("field_fade_in","fade-in duration"),"fadeOutTime":tr("field_fade_out","fade-out duration"),"inCurve":tr("field_in_curve","fade-in curve"),"outCurve":tr("field_out_curve","fade-out curve"),"silenceSeconds":tr("field_silence","silence duration"),"gain":tr("field_gain","gain"),"peakTarget":tr("field_peak_target","sample-peak target"),"lufsTarget":tr("field_lufs_target","LUFS target"),"ceiling":tr("field_ceiling","true-peak ceiling")};throw Error(tr("number_range","Enter {min} to {max} for {field}",{min,max,field:labels[k]||tr("field_value","value")}));}return v;}
function curve(x,p){return Math.pow(clamp(x,0,1),p);}
function envelope(t,i){const f=t.fade||emptyFade(),n=t.ch[0].length,ni=Math.min(n,Math.round(f.in*t.rate)),no=Math.min(n,Math.round(f.out*t.rate));let g=1;if(ni>1&&i<ni)g*=curve(i/(ni-1),f.ic);if(no>1&&i>=n-no)g*=curve((n-1-i)/(no-1),f.oc);return g;}
async function rendered(t){
 const hit=renderCache.get(t);if(hit)return hit;const f=t.fade||emptyFade();if(!f.in&&!f.out)return t;
 const n=t.ch[0].length,ch=t.ch.map(()=>new Float32Array(n));
 for(let b=0;b<n;b+=65536){for(let i=b;i<Math.min(n,b+65536);i++){const g=envelope(t,i);for(let c=0;c<ch.length;c++)ch[c][i]=t.ch[c][i]*g;}await pause();if(dead)throw Error(tr("closed","Editor closed."));}
 const out={...t,ch};renderCache.set(t,out);return out;
}
async function makePeaks(b){
 if(peakCache.has(b))return;const step=256,n=b.ch[0].length,count=Math.ceil(n/step),peaks=b.ch.map(()=>({min:new Float32Array(count),max:new Float32Array(count)}));
 for(let c=0;c<b.ch.length;c++)for(let k=0;k<count;k++){let mn=Infinity,mx=-Infinity;for(let i=k*step;i<Math.min(n,(k+1)*step);i++){const x=b.ch[c][i];mn=Math.min(mn,x);mx=Math.max(mx,x);}peaks[c].min[k]=mn;peaks[c].max[k]=mx;if(k%4096===0){await pause();if(dead)return;}}
 peakCache.set(b,peaks);
}
function setViewport(a,b,schedule=true){
 const d=duration();if(!d){v0=v1=0;return;}const len=clamp(b-a,minZoom(),d);v0=clamp(a,0,d-len);v1=v0+len;
 $('pan').value=d>len?Math.round(v0/(d-len)*1000):0;$('pan').disabled=d<=len+.000001;$('zoomLabel').textContent=(d/len).toFixed(1)+'×';
 drawBase();draw();if(schedule)scheduleSpectrum();
}
function layout(){
 if(dead)return;if(pointers.size||eqGesture){layoutDeferred=true;return;}layoutDeferred=false;const width=document.documentElement.clientWidth;if(!modal){root.style.width=width+'px';root.style.marginLeft=(-width/2)+'px';}
 w=Math.max(100,canvas.clientWidth);h=Math.max(70,canvas.clientHeight);dpr=Math.min(2,window.devicePixelRatio||1);
 [canvas,base].forEach(c=>{c.width=Math.round(w*dpr);c.height=Math.round(h*dpr);c.getContext('2d').setTransform(dpr,0,0,dpr,0,0);});drawBase();draw();scheduleSpectrum();drawSpectrumMeter();drawEq();
}
function rangePeak(b,c,start,end){
 const a=b.ch[c],p=peakCache.get(b)?.[c];let mn=0,mx=0,i=start;
 if(p&&end-start>512){while(i<end&&i%256){mn=Math.min(mn,a[i]);mx=Math.max(mx,a[i++]);}while(i+256<=end){const k=i/256;mn=Math.min(mn,p.min[k]);mx=Math.max(mx,p.max[k]);i+=256;}}
 for(;i<end;i++){mn=Math.min(mn,a[i]);mx=Math.max(mx,a[i]);}return [mn,mx];
}
function timelineTicks(){
 if(!viewBuffer||v1<=v0)return [];
 const rate=viewBuffer.rate,desiredSeconds=(v1-v0)/Math.max(2,w/130),desired=desiredSeconds>=.01?desiredSeconds:desiredSeconds*rate,power=10**Math.floor(Math.log10(Math.max(desiredSeconds>=.01?1e-9:1,desired))),factor=desired/power;
 const nice=(factor<=1?1:factor<=2?2:factor<=5?5:10)*power,frames=Math.max(1,Math.round(desiredSeconds>=.01?nice*rate:nice)),step=frames/rate;
 const digits=step>=1?0:step<.01?6:Math.min(6,Math.ceil(-Math.log10(step))),ticks=[];
 for(let frame=Math.ceil(v0*rate/frames)*frames;frame<=Math.min(viewBuffer.ch[0].length,v1*rate+.00001);frame+=frames)ticks.push({x:xAt(frame/rate),label:time(frame/rate,digits)});
 return ticks;
}
function drawBase(){
 const g=base.getContext('2d');g.clearRect(0,0,w,h);g.fillStyle='#050b07';g.fillRect(0,0,w,h);
 if(!viewBuffer){g.fillStyle='#94ae9b';g.font='13px Arial';g.textAlign='center';g.fillText(tr("waveform_empty","ADD AUDIO TO BEGIN"),w/2,h/2);return;}
 const mode=$('display').value,n=viewBuffer.ch[0].length,plotH=h-24,waveH=mode==='both'?plotH*.5:plotH,ticks=timelineTicks();
 if(mode!=='wave'){
  const sy=mode==='both'?waveH:0,sh=plotH-sy;
  if(spec&&spec.buffer===viewBuffer){const sx=(v0-spec.a)/(spec.b-spec.a)*spec.canvas.width,sw=(v1-v0)/(spec.b-spec.a)*spec.canvas.width;g.drawImage(spec.canvas,sx,0,sw,spec.canvas.height,0,sy,w,sh);}
  g.font='12px Arial';g.textAlign='left';const max=Math.min(20000,viewBuffer.rate/2);
  [50,100,500,1000,5000,10000].filter(f=>f<max).forEach(f=>{const y=sy+sh*(1-Math.log(f/20)/Math.log(max/20));g.fillStyle='#ebefdcbb';g.fillText(f>=1000?f/1000+'k':String(f),5,y-2);g.strokeStyle='#d4e2c51a';g.beginPath();g.moveTo(0,y);g.lineTo(w,y);g.stroke();});
 }
 g.strokeStyle='#91a79724';g.lineWidth=1;g.beginPath();ticks.forEach(t=>{g.moveTo(t.x+.5,0);g.lineTo(t.x+.5,plotH);});g.stroke();
 if(mode!=='spec'){
  const channels=viewBuffer.ch.length,lh=waveH/channels;
  for(let c=0;c<channels;c++){const mid=lh*(c+.5);g.strokeStyle='#273b2d';g.beginPath();g.moveTo(0,mid);g.lineTo(w,mid);g.stroke();g.strokeStyle=c?'#eac45c':'#4bd184';g.beginPath();
   if((v1-v0)*viewBuffer.rate<=w/3){const start=Math.max(0,Math.floor(v0*viewBuffer.rate)),end=Math.min(n-1,Math.ceil(v1*viewBuffer.rate));for(let i=start;i<=end;i++){const x=xAt(i/viewBuffer.rate),y=mid-clamp(viewBuffer.ch[c][i],-1,1)*lh*.42;if(i===start)g.moveTo(x,y);else g.lineTo(x,y);}g.stroke();g.fillStyle=c?'#eac45c':'#4bd184';for(let i=start;i<=end;i++){g.beginPath();g.arc(xAt(i/viewBuffer.rate),mid-clamp(viewBuffer.ch[c][i],-1,1)*lh*.42,2,0,Math.PI*2);g.fill();}}
   else{for(let x=0;x<w;x++){const lo=clamp(Math.floor((v0+x/w*(v1-v0))*viewBuffer.rate),0,n-1),hi=clamp(Math.ceil((v0+(x+1)/w*(v1-v0))*viewBuffer.rate),lo+1,n);const [mn,mx]=rangePeak(viewBuffer,c,lo,hi);g.moveTo(x+.5,mid-clamp(mx,-1,1)*lh*.42);g.lineTo(x+.5,mid-clamp(mn,-1,1)*lh*.42);}g.stroke();}g.fillStyle='#a9bcae';g.font='12px Arial';g.textAlign='left';g.fillText(component()!=='stereo'?componentLabel():channels===1?tr("channel_mono","MONO"):c?tr("channel_right","R"):tr("channel_left","L"),7,c*lh+13);
  }
 }
 g.fillStyle='#0c160f';g.fillRect(0,plotH,w,24);g.font='12px ui-monospace,monospace';g.fillStyle='#91a797';g.textAlign='center';
 ticks.forEach(t=>{g.fillRect(t.x,plotH,1,5);g.fillText(t.label,clamp(t.x,50,w-50),h-7);});
 {
  const visibleMarkers=(sideView()?[...(sideCache?.marks||[]),...(sideCache?.exportMarks||[]).filter(m=>m.type==='gap_mid').map(m=>({...m,name:tr('gap_midpoint','Gap {number} midpoint',{number:m.track})}))]:(current()?.markers||[]))
   .map(m=>({...m,x:xAt(m.frame/viewBuffer.rate)})).filter(m=>m.x>=0&&m.x<=w).sort((a,b)=>a.x-b.x);
  visibleMarkers.forEach(m=>{g.strokeStyle=m.type==='gap_mid'?'#90bca0':'#eac45c';g.beginPath();g.moveTo(m.x,0);g.lineTo(m.x,plotH);g.stroke();});
  g.save();g.font='12px Arial';g.textAlign='left';
  [false,true].forEach(isGap=>{
   const lane=visibleMarkers.filter(m=>(m.type==='gap_mid')===isGap),y=plotH-(isGap?25:7);
   lane.forEach((m,i)=>{
    const left=m.x+5,room=Math.min(180,(lane[i+1]?.x??w)-left-7,w-left-5);if(room<18)return;
    const letters=Array.from(m.name),full=letters.join('');let label=full;
    while(letters.length&&g.measureText(label).width>room){letters.pop();label=letters.join('')+'…';}
    if(!letters.length)return;
    const textWidth=g.measureText(label).width;g.fillStyle='#071009e8';g.fillRect(left-3,y-12,textWidth+6,16);
    g.fillStyle=isGap?'#a8cbb3':'#e6c875';g.fillText(label,left,y);
   });
  });g.restore();
 }
}
function handlePoints(){
 if(!viewBuffer||sideView()||component()!=='stereo'||$('tool').value!=='fade')return [];const f=fadeState(),d=duration(),ph=h-24,points=[];
 [['in',f.in,f.ic],['out',d-f.out,f.oc]].forEach(([kind,t,p])=>{const x=xAt(t);if(x>=-1&&x<=w+1)points.push({kind,x:clamp(x,23,w-23),y:20});const len=kind==='in'?f.in:f.out;if(len>0){const xx=xAt(kind==='in'?len/2:d-len/2),yy=42+(1-curve(.5,p))*(ph-62);if(xx>15&&xx<w-15)points.push({kind:kind==='in'?'ic':'oc',x:xx,y:yy});}});return points;
}
function draw(){
 const g=canvas.getContext('2d');g.clearRect(0,0,w,h);g.drawImage(base,0,0,w,h);const d=duration();
 if(d){const a=xAt(selection[0]),b=xAt(selection[1]),ph=h-24;if(b>a){g.fillStyle='#eac45c26';g.fillRect(a,0,b-a,ph);g.strokeStyle='#eac45c';g.lineWidth=1;g.strokeRect(a+.5,.5,b-a-1,ph-1);
  [a,b].forEach(x=>{if(x>=0&&x<=w){g.fillStyle='#eac45c';g.fillRect(clamp(x-7,0,w-14),ph-27,14,26);g.fillStyle='#151b0b';g.fillRect(clamp(x-1,5,w-8),ph-22,2,16);}});
 }
 if(!sideView()&&component()==='stereo'&&$('tool').value==='fade'){
  const f=fadeState();g.strokeStyle='#ef8170';g.lineWidth=2;
  [['in',f.in,f.ic],['out',f.out,f.oc]].forEach(([kind,len,p])=>{if(!len)return;g.beginPath();for(let i=0;i<=80;i++){const t=kind==='in'?len*i/80:d-len+len*i/80,x=xAt(t),gain=curve(kind==='in'?i/80:1-i/80,p),y=42+(1-gain)*(ph-62);if(i)g.lineTo(x,y);else g.moveTo(x,y);}g.stroke();});
  handlePoints().forEach(p=>{g.fillStyle=(p.kind==='ic'||p.kind==='oc')?'#eac45c':'#ee6862';g.strokeStyle='#f7eccb';g.lineWidth=1;if((p.kind==='ic'||p.kind==='oc')){g.beginPath();g.arc(p.x,p.y,8,0,Math.PI*2);g.fill();g.stroke();}else{g.fillRect(p.x-22,p.y-13,44,26);g.fillStyle='#151b0b';g.font='bold 10px Arial';g.textAlign='center';g.fillText(p.kind==='in'?tr("fade_handle_in","IN ↔"):tr("fade_handle_out","OUT ↔"),p.x,p.y+4);}});
 }
 drawEditSectionGuides(g);
 const x=xAt(cursor);if(x>=0&&x<=w){g.strokeStyle='#ff766a';g.lineWidth=1;g.beginPath();g.moveTo(x,0);g.lineTo(x,h-24);g.stroke();}
 }
 $('clock').textContent=time(cursor,v1-v0<1?6:3)+' / '+time(d);
}
function syncSelection(){
 $('start').value=selection[0].toFixed(6);$('end').value=selection[1].toFixed(6);
 if($('selectionLength'))$('selectionLength').textContent=tr('selection_length',"{time} · {samples} samples",{time:time(selection[1]-selection[0],6),samples:viewBuffer?Math.round((selection[1]-selection[0])*viewBuffer.rate):0});
}
function setSelection(a,b){selection=[snap(Math.min(a,b)),snap(Math.max(a,b))];syncSelection();draw();updateControls();}
function displayMetrics(m){
 m=lcvrChannelMetrics(m,component());
 ['lufs','rms','tp','crest','peak'].forEach(k=>$(k).textContent=m?show(m[k]):'—');$('channels').textContent=viewBuffer?(viewBuffer.ch.length===2?tr("channels_stereo","2 / ST"):tr("channels_mono","1 / MO")):'—';$('rate').textContent=viewBuffer?tr("sample_rate","{rate} kHz",{rate:viewBuffer.rate/1000}):tr("no_audio","No audio");
 const correlation=m?.correlation,valid=Number.isFinite(correlation),needle=$('correlationNeedle');
 if($('correlation'))$('correlation').textContent=valid?(correlation>=0?'+':'')+correlation.toFixed(2):'—';
 if(needle){needle.hidden=!valid;needle.style.left=(valid?(correlation+1)*50:50)+'%';needle.dataset.phase=m?.phaseKind||'waiting';}
 const kind=m?.phaseKind||'waiting',labels={waiting:tr('phase_waiting','Waiting for audio'),silence:tr('phase_silence','Too quiet to measure phase'),one_channel:tr('phase_one_channel','One channel is silent'),mono:tr('phase_mono','Mono signal'),negative:tr('phase_negative','Negative correlation · check mono playback'),positive:tr('phase_positive','Positive correlation'),low:tr('phase_low','Low correlation')};
 if($('phaseStatus'))$('phaseStatus').textContent=labels[kind];
 [['mid','M'],['sideLevel','S']].forEach(([key,letter])=>{const value=m?m[key]:NaN;if($(key))$(key).textContent=m?show(value):'—';const mask=$('mask'+letter);if(mask)mask.style.width=(100-(Number.isNaN(value)?0:clamp((value+60)/60*100,0,100)))+'%';});
}
function stats(){const live=$('meterMode').value==='live';$('lufsLabel').textContent=live?tr("lufs_momentary","LUFS-M"):tr("lufs_integrated","LUFS-I");if(processingPreviewActive())displayMetrics(null);else if(live&&playing)liveStats();else displayMetrics(live?null:metrics);}
function syncFades(){const f=current()?.fade||emptyFade();$('fadeInTime').value=f.in.toFixed(3);$('fadeOutTime').value=f.out.toFixed(3);$('inCurve').value=f.ic;$('outCurve').value=f.oc;}

 function list(){
  const box=$('tracks');box.replaceChildren();tracks.forEach((t,i)=>{
   const row=document.createElement('div');row.className='ae-track'+(i===selected?' active':'');
   const n=document.createElement('span');n.className='ae-tracknum';n.textContent=i+1;
   const title=document.createElement('button');title.type='button';title.className='ae-name';title.dataset.track='select';title.dataset.index=i;title.append(document.createTextNode(t.name));
   const info=document.createElement('small');info.textContent=tr("track_info","{time} · {channels} ch · {rate} Hz",{time:time(t.ch[0].length/t.rate),channels:t.ch.length,rate:t.rate});title.append(info);
   const buttons=document.createElement('div');buttons.className='ae-row';[['up','↑',tr("move_song_up","Move song {number} up",{number:i+1})],['down','↓',tr("move_song_down","Move song {number} down",{number:i+1})],['remove',tr("remove_button","Remove"),tr("remove_song","Remove song {number}",{number:i+1})]].forEach(([act,text,label])=>{const b=document.createElement('button');b.type='button';b.dataset.track=act;b.dataset.index=i;b.textContent=text;b.setAttribute('aria-label',label);buttons.append(b);});row.append(n,title,buttons);box.append(row);
  });
  $('song').replaceChildren(...tracks.map((t,i)=>{const o=document.createElement('option');o.value=i;o.textContent=(i+1)+'. '+t.name;return o;}));$('song').value=String(selected);
  $('summary').textContent=tracks.length?tr("project_summary","{count} / 5",{count:tracks.length,rate:current().rate/1000}):tr("empty_project_summary","{count} / 5",{count:tracks.length});
  listMarkers();syncMetadataFields();listEditSections();
 }


function listMarkers(){
 const box=$('markers');if(!box)return;box.replaceChildren();
 const markers=current()?.markers||[];
 if(!markers.length){const hint=document.createElement('small');hint.textContent=tr('markers_empty','Press M or Add marker to mark a point in the selected song.');box.append(hint);return;}
 markers.forEach((marker,index)=>{
  const row=document.createElement('div');row.className='ae-marker';
  const name=document.createElement('input');name.type='text';name.maxLength=80;name.value=marker.name;name.dataset.markerName=String(index);name.setAttribute('aria-label',tr('marker_name','Marker {number} name',{number:index+1}));
  const jump=document.createElement('button');jump.type='button';jump.dataset.marker='jump';jump.dataset.index=String(index);jump.textContent=time(marker.frame/current().rate,6);jump.setAttribute('aria-label',tr('marker_jump','Go to {name} at {time}',{name:marker.name,time:time(marker.frame/current().rate,6)}));
  const remove=document.createElement('button');remove.type='button';remove.dataset.marker='remove';remove.dataset.index=String(index);remove.textContent=tr('marker_remove','Remove');remove.setAttribute('aria-label',tr('marker_remove_named','Remove {name}',{name:marker.name}));
  // Rows can be rebuilt while refresh is still analyzing audio.
  [name,jump,remove].forEach(control=>control.disabled=busy||!!drag||sideView());
  row.append(name,jump,remove);box.append(row);
 });
}
function commitMarkers(markers,rebuild=true){
 if(metadataDirty()){metadataPending();return;}
 if(cleanupDraft)return;
 const old=current(),next={...old,markers};checkpoint(historyMarkerLabel(markers));tracks=tracks.map((track,index)=>index===selected?next:track);invalidate();
 // Marker metadata does not require reprocessing audio or interrupting playback.
 if(!sideView()&&viewBuffer){const previous=viewBuffer,previousSource=sourceBuffer;sourceBuffer={...previousSource,markers};viewBuffer=previous===previousSource?sourceBuffer:{...previous,markers};const peaks=peakCache.get(previous);if(peaks)peakCache.set(viewBuffer,peaks);const attacks=transientCache.get(previous);if(attacks)transientCache.set(viewBuffer,attacks);renderCache.set(next,sourceBuffer);if(spec?.buffer===previous)spec={...spec,buffer:viewBuffer};scheduleSpectrum();}
 if(rebuild)listMarkers();drawBase();draw();updateControls();
}
function commitMarkerName(e){
 const field=e.target.closest?.('[data-marker-name]');if(!field||busy||drag||dead||cleanupDraft||sideView())return;
 const index=Number(field.dataset.markerName),markers=current()?.markers||[];if(!markers[index])return;
 const name=field.value.trim().slice(0,80)||tr('marker_default','Marker {number}',{number:index+1});
 // change, focusout and Enter may all occur for one edit. Commit only once.
 if(name===markers[index].name){field.value=name;return;}
 commitMarkers(markers.map((marker,i)=>i===index?{...marker,name}:marker),false);field.value=name;
 const row=field.closest('.ae-marker');
 row?.querySelector('[data-marker="jump"]')?.setAttribute('aria-label',tr('marker_jump','Go to {name} at {time}',{name,time:time(markers[index].frame/current().rate,6)}));
 row?.querySelector('[data-marker="remove"]')?.setAttribute('aria-label',tr('marker_remove_named','Remove {name}',{name}));
 note(tr('marker_renamed','Marker renamed.'));
}
function addMarker(){
 if(busy||drag||dead||cleanupDraft||!viewBuffer)return;
 if(sideView()){note(tr('marker_song_only','Choose a song to add a marker. Whole-side view shows every song’s markers.'),true);return;}
 const markers=current().markers||[],frame=Math.round(snap(position())*viewBuffer.rate);
 if(markers.length>=200){note(tr('marker_limit','This song already has 200 markers. Remove a marker before adding another.'),true);return;}
 const name=tr('marker_default','Marker {number}',{number:markers.length+1});
 commitMarkers([...markers,{name,frame}].sort((a,b)=>a.frame-b.frame));
 note(tr('marker_added','Added {name} at {time}. Open Make a side to rename it.',{name,time:time(frame/viewBuffer.rate,6)}));
}

 async function inspectMp3(bytes,used,projectRate,name){
  const fail=key=>{throw Error(tr(key,'{file}: this MP3 is incomplete or unsupported. Try exporting a fresh MP3 or WAV.',{file:name}));};
  const tag=(p,s)=>p>=0&&p+s.length<=bytes.length&&[...s].every((c,i)=>bytes[p+i]===c.charCodeAt(0));
  let start=0,end=bytes.length;
  if(tag(0,'ID3')){
   if(bytes.length<10||![2,3,4].includes(bytes[3])||[6,7,8,9].some(i=>bytes[i]&128))fail('mp3_tag_error');
   const size=bytes[6]*2097152+bytes[7]*16384+bytes[8]*128+bytes[9];
   start=10+size+(bytes[3]===4&&(bytes[5]&16)?10:0);if(start>end)fail('mp3_tag_error');
  }
  if(tag(end-128,'TAG'))end-=128;
  if(tag(end-32,'APETAGEX')){const v=new DataView(bytes.buffer,bytes.byteOffset,bytes.byteLength),size=v.getUint32(end-20,true);if(size<32||size>end-start)fail('mp3_tag_error');end-=size;if(tag(end-32,'APETAGEX'))end-=32;}
  let p=start;
  // Allow zero padding after a tag, but never skip damaged MPEG frames.
  while(p<Math.min(end,start+65536)&&bytes[p]===0)p++;
  const first=mp3Frame(bytes,p);if(!first||p+first.size>end)fail('mp3_header_error');
  if(projectRate&&first.rate!==projectRate)throw Error(tr('sample_rate_mismatch','{file} is {source} Hz. This project is {project} Hz. Use files with the same sample rate, or clear the project to convert this file separately.',{file:name,source:first.rate,project:projectRate}));
  let samples=0,frames=0;
  for(;p<end;){
   const frame=mp3Frame(bytes,p);
   if(!frame){if(end-p<=4096&&bytes.subarray(p,end).every(x=>x===0))break;fail('mp3_frame_error');}
   if(frame.rate!==first.rate||frame.channels!==first.channels||p+frame.size>end)fail('mp3_frame_error');
   samples+=frame.samples;frames++;p+=frame.size;
   if(used+samples*first.channels*4>MAX)throw Error(tr('project_memory','Decoded audio would exceed the 256 MiB project limit. Use shorter files or fewer songs.'));
   if(frames%4096===0){note(tr('mp3_inspecting','Checking MP3 {percent}%',{percent:Math.round(p/end*100)}),false,p/end*100);await pause();}
  }
  if(!frames)fail('mp3_header_error');
  return {rate:first.rate,channels:first.channels,samples,frames};
 }
 async function readMp3(file,used,rate){
  if(file.size>MAX)throw Error(tr('file_memory','{file}: file exceeds the 256 MiB limit.',{file:file.name}));
  note(tr('mp3_checking','Checking {file}…',{file:file.name}));
  const bytes=await file.arrayBuffer();assertAlive();
  const info=await inspectMp3(new Uint8Array(bytes),used,rate,file.name);assertAlive();
  note(tr('mp3_decoding','Decoding {file} to editable audio…',{file:file.name}));
  const Offline=window.OfflineAudioContext||window.webkitOfflineAudioContext,AC=window.AudioContext||window.webkitAudioContext;
  if(!Offline&&!AC)throw Error(tr('web_audio_missing','This browser does not support Web Audio. Try a current browser.'));
  let decoder,decoded;
  try{
   decoder=Offline?new Offline(info.channels,1,info.rate):new AC({sampleRate:info.rate});
   if(decoder.sampleRate!==info.rate)throw Error(tr('mp3_rate_unavailable','This browser cannot decode this MP3 at its original sample rate. Try another current browser or convert it to WAV first.'));
   try{decoded=await decoder.decodeAudioData(bytes);}catch(e){throw Error(tr('mp3_decode_error','{file}: the browser could not decode this MP3. Try exporting a fresh MP3 or WAV.',{file:file.name}));}
  }finally{if(decoder&&!Offline&&typeof decoder.close==='function')await decoder.close().catch(()=>{});}
  assertAlive();
  if(decoded.sampleRate!==info.rate||decoded.numberOfChannels!==info.channels||!decoded.length||decoded.length>info.samples+1152)throw Error(tr('mp3_decode_mismatch','The decoded MP3 did not match its audio header. Try exporting a fresh MP3 or WAV.'));
  if(used+decoded.length*decoded.numberOfChannels*4>MAX)throw Error(tr('project_memory','Decoded audio would exceed the 256 MiB project limit. Use shorter files or fewer songs.'));
  const ch=Array.from({length:decoded.numberOfChannels},(_,c)=>decoded.getChannelData(c));
  for(let b=0;b<decoded.length;b+=65536){for(let c=0;c<ch.length;c++)for(let i=b;i<Math.min(decoded.length,b+65536);i++)if(!Number.isFinite(ch[c][i]))throw Error(tr('mp3_invalid_samples','The decoded MP3 contains invalid samples. Try exporting a fresh file.'));await pause();}
  return {name:file.name,rate:decoded.sampleRate,ch,fade:emptyFade(),sourceFormat:'mp3'};
 }
 async function readAudio(file,used,rate){
  const head=new Uint8Array(await file.slice(0,12).arrayBuffer());assertAlive();
  if(head.length>=12&&String.fromCharCode(...head.subarray(0,4))==='RIFF'&&String.fromCharCode(...head.subarray(8,12))==='WAVE')return readWav(file,used,rate);
  if(/\.mp3$/i.test(file.name)||file.type==='audio/mpeg'||String.fromCharCode(...head.subarray(0,3))==='ID3'||mp3Frame(head,0))return readMp3(file,used,rate);
  throw Error(tr('audio_format','{file}: choose an MP3 or an uncompressed WAV file.',{file:file.name}));
 }
 async function readWav(file,used,rate){
  if(file.size>MAX)throw Error(tr("file_memory","{file}: file exceeds the 256 MiB limit.",{file:file.name}));
  const bytes=await file.arrayBuffer(),v=new DataView(bytes);
  const tag=p=>p+4<=v.byteLength?String.fromCharCode(v.getUint8(p),v.getUint8(p+1),v.getUint8(p+2),v.getUint8(p+3)):'';
  if(tag(0)!=='RIFF'||tag(8)!=='WAVE')throw Error(tr("wav_format","{file}: use an uncompressed RIFF/WAV file.",{file:file.name}));
  let fmt=null,data=null;
  for(let p=12;p+8<=v.byteLength;){const size=v.getUint32(p+4,true),start=p+8;if(start+size>v.byteLength)throw Error(tr("wav_truncated","{file}: truncated WAV chunk.",{file:file.name}));
   if(tag(p)==='fmt '){if(size<16)throw Error(tr("wav_invalid_format","Invalid WAV format chunk."));let kind=v.getUint16(start,true);const channels=v.getUint16(start+2,true),sr=v.getUint32(start+4,true),align=v.getUint16(start+12,true),bits=v.getUint16(start+14,true);
    if(kind===65534){if(size<40||v.getUint16(start+16,true)<22)throw Error(tr("wav_invalid_header","Invalid extensible WAV header."));const guid=[0,0,16,0,128,0,0,170,0,56,155,113];if(!guid.every((x,i)=>v.getUint8(start+28+i)===x))throw Error(tr("wav_unsupported_subformat","Unsupported WAV subformat."));kind=v.getUint32(start+24,true);}
    fmt={kind,channels,sr,align,bits};
   }else if(tag(p)==='data'&&!data)data={start,size};p=start+size+(size&1);
  }
  if(!fmt||!data)throw Error(tr("wav_missing_data","{file}: missing WAV format/audio data.",{file:file.name}));
  const {kind,channels,sr,align,bits}=fmt;
  if(![1,2].includes(channels)||sr<8000||sr>96000)throw Error(tr("wav_supported_rate","Use a mono/stereo WAV at 8–96 kHz."));
  if(!((kind===1&&[8,16,24,32].includes(bits))||(kind===3&&bits===32)))throw Error(tr("wav_supported_encoding","Use PCM WAV (8/16/24/32-bit) or 32-bit float WAV."));
  if(align!==channels*bits/8||data.size%align)throw Error(tr("wav_alignment","Unsupported WAV block alignment."));
  if(rate&&sr!==rate)throw Error(tr("sample_rate_mismatch","{file} is {source} Hz. This project is {project} Hz. Use files with the same sample rate, or clear the project to convert this file separately.",{file:file.name,source:sr,project:rate}));
  const frames=data.size/align;if(!frames)throw Error(tr("wav_empty","The WAV contains no audio."));
  if(used+frames*channels*4>MAX)throw Error(tr("project_memory","Decoded audio would exceed the 256 MiB project limit. Use shorter files or fewer songs."));
  const ch=Array.from({length:channels},()=>new Float32Array(frames)),step=bits/8;
  for(let begin=0;begin<frames;begin+=65536){const end=Math.min(frames,begin+65536);for(let i=begin;i<end;i++)for(let c=0;c<channels;c++){const p=data.start+i*align+c*step;let x;
    if(kind===3)x=v.getFloat32(p,true);else if(bits===8)x=(v.getUint8(p)-128)/128;else if(bits===16)x=v.getInt16(p,true)/32768;else if(bits===24){let z=v.getUint8(p)|(v.getUint8(p+1)<<8)|(v.getUint8(p+2)<<16);if(z&0x800000)z-=0x1000000;x=z/8388608;}else x=v.getInt32(p,true)/2147483648;
    if(!Number.isFinite(x))throw Error(tr("wav_invalid_samples","{file}: audio contains invalid floating-point samples.",{file:file.name}));ch[c][i]=x;
   }await pause();if(dead)throw Error(tr("closed","Editor closed."));}
  return {name:file.name,rate:sr,ch,fade:emptyFade(),sourceFormat:'wav',sourceBits:bits};
 }

 async function assembleInputs(inputTracks){
  const tracks=inputTracks;if(!tracks.length)return null;
  const rate=tracks[0].rate,nc=Math.max(...tracks.map(t=>t.ch.length)),starts=[0],overlaps=[];
  let frames=tracks[0].ch[0].length;
  for(let i=1;i<tracks.length;i++){const prev=tracks[i-1].ch[0].length,now=tracks[i].ch[0].length;
   const overlap=transition.mode==='cross'?Math.max(0,Math.min(Math.round(transition.cross*rate),Math.floor(prev/2),Math.floor(now/2))):0;
   const gap=transition.mode==='gap'?Math.round(transition.gap*rate):0;overlaps.push(overlap);starts.push(frames+gap-overlap);frames+=gap+now-overlap;
  }
  if(frames*nc*4>MAX)throw Error(tr("side_memory","The assembled side exceeds the 256 MiB decoded-audio limit."));
  const timing=sideAudioTiming(tracks,starts),exportMarks=sideExportMarkers(tracks,starts);
  if(tracks.length===1){const assembled={...await rendered(tracks[0]),marks:sideAudioMarkers(tracks,[0]),exportMarks,timing};return assembled;}
  const ch=Array.from({length:nc},()=>new Float32Array(frames));
  for(let t=0;t<tracks.length;t++){const tr=await rendered(tracks[t]),len=tr.ch[0].length,fi=t?overlaps[t-1]:0,fo=t<tracks.length-1?overlaps[t]:0;
   for(let b=0;b<len;b+=65536){const end=Math.min(len,b+65536);for(let i=b;i<end;i++){let gain=1;if(fi&&i<fi)gain*=fi>1?i/(fi-1):.5;if(fo&&i>=len-fo)gain*=fo>1?(len-1-i)/(fo-1):.5;for(let c=0;c<nc;c++)ch[c][starts[t]+i]+=tr.ch[Math.min(c,tr.ch.length-1)][i]*gain;}await pause();}
  }
  const assembled={name:tr("complete_side","Complete record side"),rate,ch,marks:sideAudioMarkers(tracks,starts),exportMarks,timing};return assembled;
 }

 async function assemble(){if(sideCache)return sideCache;const result=await assembleInputs(tracks);sideCache=result;return result;}

 // K weighting: shelf + RLB high-pass, with 400 ms blocks / 100 ms hop.
 function filters(rate){let k=Math.tan(Math.PI*1681.974450955533/rate),q=.7071752369554196,vh=10**(3.999843853973347/20),vb=vh**.4996667741545416,a=1+k/q+k*k;
  const shelf=[(vh+vb*k/q+k*k)/a,2*(k*k-vh)/a,(vh-vb*k/q+k*k)/a,2*(k*k-1)/a,(1-k/q+k*k)/a];
  k=Math.tan(Math.PI*38.13547087602444/rate);q=.5003270373238773;a=1+k/q+k*k;return [shelf,[1,-2,1,2*(k*k-1)/a,(1-k/q+k*k)/a]];
 }
 function biquad(c){let x1=0,x2=0,y1=0,y2=0;return x=>{const y=c[0]*x+c[1]*x1+c[2]*x2-c[3]*y1-c[4]*y2;x2=x1;x1=x;y2=y1;y1=y;return y;};}
 // 4x windowed-sinc interpolation; estimate, not a certified true-peak detector.
 const kernels=[.25,.5,.75].map(t=>{const a=[];let sum=0;for(let k=-7;k<=8;k++){const x=k-t,u=Math.PI*x;const z=Math.abs(x)<8?(Math.abs(x)<1e-10?1:Math.sin(u)/u)*(.42+.5*Math.cos(Math.PI*x/8)+.08*Math.cos(2*Math.PI*x/8)):0;a.push(z);sum+=z;}return a.map(x=>x/sum);});
 async function analyze(buffer){
  const n=buffer.ch[0].length,nc=buffer.ch.length,rate=buffer.rate,hop=Math.max(1,Math.round(rate/10)),count=Math.ceil(n/hop);
  const raw=new Float64Array(count),weighted=new Float64Array(count),peaks=new Float64Array(count),truePeaks=new Float64Array(count),leftEnergy=new Float64Array(count),rightEnergy=new Float64Array(count),crossEnergy=new Float64Array(count),co=filters(rate);
  let energy=0,peak=0,tp=0,left=0,right=0,cross=0;
  for(let c=0;c<nc;c++){const a=buffer.ch[c],f1=biquad(co[0]),f2=biquad(co[1]);
   for(let b=0;b<n;b+=32768){const end=Math.min(n,b+32768);for(let i=b;i<end;i++){const x=a[i],bin=Math.floor(i/hop),z=f2(f1(x));energy+=x*x;raw[bin]+=x*x;weighted[bin]+=z*z;const ax=Math.abs(x);peak=Math.max(peak,ax);peaks[bin]=Math.max(peaks[bin],ax);
     if(c===0){const r=buffer.ch[Math.min(1,nc-1)][i],ll=x*x,rr=r*r,lr=x*r;left+=ll;right+=rr;cross+=lr;leftEnergy[bin]+=ll;rightEnergy[bin]+=rr;crossEnergy[bin]+=lr;}
     let y1=0,y2=0,y3=0;for(let k=0;k<16;k++){const at=i+k-7,v=at>=0&&at<n?a[at]:0;y1+=v*kernels[0][k];y2+=v*kernels[1][k];y3+=v*kernels[2][k];}const p=Math.max(ax,Math.abs(y1),Math.abs(y2),Math.abs(y3));tp=Math.max(tp,p);truePeaks[bin]=Math.max(truePeaks[bin],p);
    }const progress=(c*n+end)/(nc*n)*100;note(tr("analyzing","Analyzing {percent}%",{percent:Math.round(progress)}),false,progress);await pause();}
  }
  const blocks=[];for(let b=0;b+4<=Math.floor(n/hop);b++){const e=(weighted[b]+weighted[b+1]+weighted[b+2]+weighted[b+3])/(4*hop);if(ld(e)>=-70)blocks.push(e);}
  let lufs=n<4*hop?NaN:-Infinity;
  if(blocks.length){const avg=blocks.reduce((a,b)=>a+b,0)/blocks.length,gate=Math.max(-70,ld(avg)-10),kept=blocks.filter(x=>ld(x)>=gate);lufs=ld(kept.reduce((a,b)=>a+b,0)/kept.length);}
  const rms=db(Math.sqrt(energy/(n*nc))),p=db(peak);return {lufs,rms,peak:p,tp:db(tp),crest:peak>0?p-rms:NaN,hop,raw,weighted,peaks,truePeaks,leftEnergy,rightEnergy,crossEnergy,nc,...stereoReadings(left,right,cross,n,nc===1)};
 }

 async function applyGain(amount,label=historyLabel('gain')){
  const all=sideView(),next=[],target=component();
  for(let ti=0;ti<tracks.length;ti++){const t=tracks[ti];if((!all&&ti!==selected)||(lcvrStereoOnlyComponent(target)&&t.ch.length===1)){next.push(t);continue;}next.push(await lcvrProcessTargetGain(t,amount,target,{cancelled:()=>dead}));}
  assertAlive();checkpoint(label);tracks=next;invalidate();await refresh(true);
 }

 // Named project states share immutable PCM. This is bounded edit history,
// not a list of live effects: restoring a step restores the whole project.
function historySnapshot(){
 return {tracks:tracks.slice(),selected,transition:{...transition},release:{...releaseInfo},view:$('view').value,
  component:component(),cursor,selection:selection.slice(),viewport:[v0,v1]};
}
function historySameContent(a,b){
 return a.tracks.length===b.tracks.length&&a.tracks.every((track,index)=>track===b.tracks[index])&&
  ['mode','gap','cross'].every(key=>a.transition[key]===b.transition[key])&&
  ['title','artist','catalog','barcode'].every(key=>(a.release[key]||'')===(b.release[key]||''));
}
function historyBuffers(state){
 const buffers=new Set();
 for(const track of state.tracks)for(const channel of track.ch||[])if(channel?.buffer)buffers.add(channel.buffer);
 return buffers;
}
function historyExtraBytes(entries,index){
 if(!entries[index])return 0;
 const active=historyBuffers(entries[index].state),retained=new Set();let bytes=0;
 for(const entry of entries)for(const buffer of historyBuffers(entry.state))if(!active.has(buffer)&&!retained.has(buffer)){retained.add(buffer);bytes+=buffer.byteLength;}
 return bytes;
}
function historyReleaseCaches(entries){
 const active=new Set(tracks);
 for(const entry of entries)for(const track of entry.state.tracks)if(!active.has(track)){
  const cached=renderCache.get(track);renderCache.delete(track);peakCache.delete(track);transientCache.delete(track);
  if(cached&&cached!==sourceBuffer&&cached!==viewBuffer){peakCache.delete(cached);transientCache.delete(cached);}
 }
}
function historySyncUndo(){undo=historyIndex>0?historyEntries[historyIndex-1].state:null;}
function historyPrune(){
 const dropped=[];
 while(historyEntries.length>1&&(historyEntries.length>HISTORY_STATES||historyExtraBytes(historyEntries,historyIndex)>HISTORY_EXTRA_BYTES)){
  // Keep the current state and nearby steps. A large current project can still
  // retain its immediate predecessor within the previous single-Undo budget.
  if(historyIndex>0&&(historyIndex>=historyEntries.length-1-historyIndex||historyIndex===historyEntries.length-1)){
   dropped.push(historyEntries.shift());historyIndex--;historyEarliestPruned=true;
  }else dropped.push(historyEntries.pop());
 }
 historyReleaseCaches([...historyEntries,...dropped]);
 historyRetainedBytes=historyExtraBytes(historyEntries,historyIndex);historySyncUndo();
}
function historyReset(){
 historyReleaseCaches(historyEntries);historyPending=null;historyRestoring=false;historyEarliestPruned=false;
 historyEntries=[{label:tracks.length?tr('history_original','Original'):tr('history_empty','Empty'),scope:'',state:historySnapshot()}];historyIndex=0;historyRetainedBytes=0;
 historyRevision++;historySyncUndo();
}
function historyLabel(action){
 const labels={
  cut:tr('history_cut','Cut'),trim:tr('history_trim','Trim'),trimStart:tr('history_trim_start','Trim start'),trimEnd:tr('history_trim_end','Trim end'),
  insertSilence:tr('history_silence_middle','Silence here'),insertSilenceStart:tr('history_silence_start','Silence at start'),insertSilenceEnd:tr('history_silence_end','Silence at end'),
  gain:tr('history_gain','Gain'),normalize:tr('history_normalize','Normalize'),applyEq:tr('history_eq','EQ'),removeDc:tr('history_dc','Remove DC'),
  mono:tr('history_mono','Make mono'),stereo:tr('history_stereo','Make stereo')
 };
 const label=labels[action]||tr('history_edit','Audio edit');
 return ['gain','applyEq'].includes(action)?historyTargetLabel(label):label;
}
function historyTargetLabel(label){return component()!=='stereo'?tr('history_component','{edit} · {component}',{edit:label,component:componentLabel()}):label;}
function historyMarkerLabel(markers){
 const old=current()?.markers||[];
 return markers.length>old.length?tr('history_marker_add','Add marker'):markers.length<old.length?tr('history_marker_remove','Remove marker'):tr('history_marker_name','Rename marker');
}
function historyCheckpoint(label){
 if(historyRestoring)return;
 historyCommit();
 if(!historyEntries.length)historyReset();
 const before=historySnapshot();
 // Save the user's latest audition position and selection before this edit.
 if(historySameContent(historyEntries[historyIndex].state,before))historyEntries[historyIndex]={...historyEntries[historyIndex],state:before};
 historyPending={before,label:label||tr('history_edit','Audio edit'),scope:sideView()?tr('history_scope_side','Whole side'):current()?.name||''};
}
function historyCommit(){
 if(!historyPending||historyRestoring)return;
 const next=historySnapshot(),pending=historyPending;historyPending=null;
 if(historySameContent(pending.before,next)){historySyncUndo();return;}
 const discarded=historyEntries.slice(historyIndex+1);historyEntries=historyEntries.slice(0,historyIndex+1);
 historyEntries.push({label:pending.label,scope:pending.scope,state:next});historyIndex=historyEntries.length-1;
 historyReleaseCaches(discarded);historyPrune();historyRevision++;
}
function historyLocked(){return busy||dead||!!drag||!!eqGesture||!!cleanupDraft||metadataDirty()||eqDirty()||eqInvalidFields.size>0;}
function updateHistoryControls(){
 const strip=$('historyStrip'),box=$('historySteps');if(!strip||!box)return;
 const locked=historyLocked();
 q('[data-act="undo"]').forEach(button=>button.disabled=locked||historyIndex<=0);
 q('[data-act="redo"]').forEach(button=>button.disabled=locked||historyIndex>=historyEntries.length-1);
 strip.hidden=historyEntries.length<2&&!tracks.length;
 if(historyRenderedRevision!==historyRevision){
  const hadFocus=!!document.activeElement?.closest?.('[data-history-step]');box.replaceChildren();
  historyEntries.forEach((entry,index)=>{
   const button=document.createElement('button');button.type='button';button.className='ae-history-step';button.dataset.historyStep=String(index);
   const number=document.createElement('span');number.className='ae-history-number';number.textContent=String(index+1);number.setAttribute('aria-hidden','true');
   const copy=document.createElement('span');copy.className='ae-history-copy';
   if(index===0&&historyEarliestPruned){const boundary=document.createElement('small');boundary.textContent=tr('history_earliest','Earliest kept');copy.append(boundary);}
   const label=document.createElement('span');label.textContent=entry.label;copy.append(label);button.append(number,copy);
   button.title=entry.scope?tr('history_step_scope','{edit} · {scope}',{edit:entry.label,scope:entry.scope}):entry.label;
   button.setAttribute('aria-label',tr('history_go_to','Return to step {number}: {edit}',{number:index+1,edit:button.title}));
   button.setAttribute('aria-pressed',String(index===historyIndex));if(index===historyIndex)button.setAttribute('aria-current','step');
   button.dataset.future=String(index>historyIndex);button.disabled=locked;box.append(button);
  });
  const selectedButton=box.querySelector('[data-history-step="'+historyIndex+'"]');
  if(selectedButton){const left=selectedButton.offsetLeft-box.offsetLeft,right=left+selectedButton.offsetWidth;
   if(left<box.scrollLeft)box.scrollLeft=left;else if(right>box.scrollLeft+box.clientWidth)box.scrollLeft=Math.max(0,right-box.clientWidth);
   if(hadFocus&&!locked)selectedButton.focus({preventScroll:true});
  }
  historyRenderedRevision=historyRevision;
 }else q('[data-history-step]').forEach(button=>button.disabled=locked);
 const status=$('historyStatus'),text=historyEntries[historyIndex]?tr('history_current','Current edit: {edit}. {count} steps kept.',{edit:historyEntries[historyIndex].label,count:historyEntries.length}):'';
 if(status&&status.textContent!==text)status.textContent=text;
}
async function historyNavigate(index){
 if(busy||dead||drag||eqGesture||!Number.isInteger(index))return;
 if(metadataDirty()){metadataPending();return;}
 if(cleanupDraft){note(tr('cleanup_pending','Apply or reset the processed preview before saving or starting another edit.'),true);return;}
 if(eqDirty()||eqInvalidFields.size){note(tr('eq_pending','Apply or reset your EQ preview before saving.'),true);return;}
 historyCommit();
 if(index<0||index>=historyEntries.length||index===historyIndex)return;
 await run(async()=>{
  const entry=historyEntries[index],snapshot=entry.state;
  historyEntries[historyIndex]={...historyEntries[historyIndex],state:historySnapshot()};
  historyPending=null;historyRestoring=true;historyIndex=index;
  try{
   tracks=snapshot.tracks.slice();selected=clamp(snapshot.selected,0,Math.max(0,tracks.length-1));
   transition={...snapshot.transition};releaseInfo={title:'',artist:'',catalog:'',barcode:'',...snapshot.release};
   $('view').value=snapshot.view;$('transition').value=transition.mode;$('gap').value=transition.gap;$('cross').value=transition.cross;
   if($('component'))$('component').value=snapshot.component||'stereo';
   eqBands=lcvrEqDefaultBands(current()?.rate||44100);eqBypassed=false;eqInvalidFields.clear();
   cursor=snapshot.cursor;selection=snapshot.selection.slice();[v0,v1]=snapshot.viewport;
   invalidate();await refresh(true);syncMetadataFields();
   note(tr('history_restored','Restored {edit}.',{edit:entry.label}));
  }finally{historyRestoring=false;historyPrune();historyRevision++;}
 });
}
function historyKeydown(event){
 if(event.defaultPrevented||event.isComposing||event.repeat||event.altKey||!(event.ctrlKey||event.metaKey))return;
 const target=event.target;
 if(target?.isContentEditable||target?.closest?.('input,textarea,select,[contenteditable]:not([contenteditable="false"]),[role="textbox"],[role="combobox"]'))return;
 const key=event.key.toLowerCase(),redo=(key==='z'&&event.shiftKey)||(key==='y'&&!event.shiftKey);
 if(key!=='z'&&!redo)return;
 event.preventDefault();event.stopPropagation();historyNavigate(historyIndex+(redo?1:-1));
}
function installHistoryControls(){
 historyReset();
 on(root,'keydown',historyKeydown,{capture:true});
 on(root,'click',event=>{const button=event.target.closest?.('[data-history-step]');if(button&&!button.disabled)historyNavigate(Number(button.dataset.historyStep));});
}

function metadataValues(){
 const track=current();
 return {releaseTitle:releaseInfo.title,releaseArtist:releaseInfo.artist,catalogNumber:releaseInfo.catalog,barcode:releaseInfo.barcode,trackTitle:track?.name||'',trackArtist:track?.artist||'',trackIsrc:track?.isrc||''};
}
function metadataDirty(){
 if(!tracks.length)return false;
 return Object.entries(metadataValues()).some(([key,value])=>$(key)&&$(key).value!==value);
}
function syncMetadataFields(){
 for(const [key,value] of Object.entries(metadataValues())){const field=$(key);if(field){field.value=value;field.removeAttribute('aria-invalid');}}
 if($('metadataSource'))$('metadataSource').textContent=current()?.sourceName||current()?.name||'';
 if($('metadataState'))$('metadataState').textContent='';
}
function metadataPending(){
 note(tr('metadata_pending','Save or reset song details before another edit.'),true);
}
function updateMetadataControls(blocked,loaded){
 const locked=blocked||!loaded||!!cleanupDraft,dirty=metadataDirty();
 for(const key of Object.keys(metadataValues()))if($(key))$(key).disabled=locked;
 q('[data-act="applyMetadata"],[data-act="resetMetadata"]').forEach(button=>button.disabled=locked||!dirty);
 if(dirty){
  const allowed=['play','pause','stop','returnStart','playSelection','selectAll','clearSelection','prevTransient','nextTransient','applyMetadata','resetMetadata'];
  q('[data-act]').forEach(button=>{if(!allowed.includes(button.dataset.act))button.disabled=true;});
  q('[data-track],[data-marker],[data-marker-name]').forEach(field=>field.disabled=true);
  $('song').disabled=true;$('view').disabled=true;$('files').disabled=true;
  if($('component'))$('component').disabled=true;
  root.querySelector('[data-ui="load"]').disabled=true;
 }
 if($('metadataState'))$('metadataState').textContent=dirty?tr('metadata_unsaved','Unsaved details'):cleanupDraft?tr('metadata_preview_locked','Apply or reset the audio preview to edit details.'):'';
}
function metadataAlias(before,after){
 if(!before||!after||before===after)return;
 const peaks=peakCache.get(before),attacks=transientCache.get(before);
 if(peaks)peakCache.set(after,peaks);
 if(attacks)transientCache.set(after,attacks);
 if(spec?.buffer===before)spec={...spec,buffer:after};
}
function updateMetadataCaches(old,next){
 const info={name:next.name,sourceName:next.sourceName,artist:next.artist,isrc:next.isrc,splits:next.splits||[]};
 const renderedOld=renderCache.get(old),renderedNext=renderedOld?{...renderedOld,...info}:null;
 if(renderedNext){metadataAlias(renderedOld,renderedNext);renderCache.set(next,renderedNext);}
 if(sideCache){
  const previous=sideCache,starts=previous.timing.rows.map(row=>row.start);
  sideCache={...previous,marks:sideAudioMarkers(tracks,starts),exportMarks:sideExportMarkers(tracks,starts),timing:sideAudioTiming(tracks,starts)};
  if(tracks.length===1)Object.assign(sideCache,info);
  metadataAlias(previous,sideCache);
 }
 if(sourceBuffer){
  const previousSource=sourceBuffer,previousView=viewBuffer;
  sourceBuffer=sideView()?sideCache:previousSource===renderedOld&&renderedNext?renderedNext:{...sourceBuffer,...info};
  if(!sourceBuffer)sourceBuffer=previousSource;
  viewBuffer=previousView===previousSource?sourceBuffer:{...previousView,...(sideView()?{}:info)};
  metadataAlias(previousSource,sourceBuffer);metadataAlias(previousView,viewBuffer);
  if(!sideView())renderCache.set(next,sourceBuffer);
 }
}
function applyMetadata(){
 if(busy||dead||drag||eqGesture||cleanupDraft||!current())return;
 const fieldKeys=Object.keys(metadataValues());
 fieldKeys.forEach(key=>$(key)?.removeAttribute('aria-invalid'));
 const nextRelease={title:lcvrMetadataText($('releaseTitle').value),artist:lcvrMetadataText($('releaseArtist').value),catalog:lcvrMetadataText($('catalogNumber').value,80),barcode:lcvrNormalizeBarcode($('barcode').value)};
 const title=lcvrMetadataText($('trackTitle').value),artist=lcvrMetadataText($('trackArtist').value),isrc=lcvrNormalizeIsrc($('trackIsrc').value);
 let problem='';
 if(!title){$('trackTitle').setAttribute('aria-invalid','true');problem=tr('metadata_title_required','Enter a song title.');}
 if(!lcvrValidIsrc(isrc)){$('trackIsrc').setAttribute('aria-invalid','true');problem=tr('metadata_isrc_invalid','Use a 12-character ISRC, or leave it blank.');}
 if(!lcvrValidBarcode(nextRelease.barcode)){$('barcode').setAttribute('aria-invalid','true');problem=tr('metadata_barcode_invalid','Use a valid 12-digit UPC or 13-digit EAN, or leave it blank.');}
 if(problem){note(problem,true);return;}
 if(!metadataDirty())return;
 if(JSON.stringify(nextRelease)===JSON.stringify(releaseInfo)&&title===current().name&&artist===(current().artist||'')&&isrc===(current().isrc||'')){syncMetadataFields();updateControls();return;}
 const old=current(),next={...old,name:title,sourceName:old.sourceName||old.name,artist,isrc};
 checkpoint(tr('history_metadata','Names & codes'));releaseInfo=nextRelease;tracks=tracks.map((track,index)=>index===selected?next:track);
 updateMetadataCaches(old,next);invalidateDownloads();list();
 if($('name')&&viewBuffer)$('name').textContent=viewBuffer.name;
 draw();updateControls();note(tr('metadata_saved','Song details saved. Prepare a metadata sheet in Save.'));
}
function resetMetadata(){
 if(busy||dead||drag||eqGesture||cleanupDraft)return;
 syncMetadataFields();updateControls();note(tr('metadata_reset','Unsaved song details reset.'));
}
function installMetadataControls(){
 syncMetadataFields();
 for(const key of Object.keys(metadataValues()))on($(key),'input',()=>{
  $(key).removeAttribute('aria-invalid');invalidateDownloads();updateControls();
 });
 on(root,'keydown',event=>{
  if(event.key==='Enter'&&!event.isComposing&&event.target.closest?.('[data-meta]')){event.preventDefault();applyMetadata();}
 });
}

function revealPlayhead(){
 if(!viewBuffer)return;
 if(cursor<v0||cursor>=v1){const span=Math.max(minZoom(),v1-v0);setViewport(cursor,cursor+span);}
 draw();
}
function pausePlayback(){
 if(!viewBuffer||busy||dead||drag||eqGesture||(!playing&&!playStarting))return;
 const end=playEnd,anchor=playAnchor;
 stop(false);playPaused=true;pausedEnd=end;playAnchor=anchor;updateControls();
 canvas.focus({preventScroll:true});
}
function stopPlayback(){
 if(!viewBuffer||busy||dead||drag||eqGesture)return;
 const anchor=playing||playStarting||playPaused?playAnchor:0;
 stop(false);cursor=snap(anchor);revealPlayhead();updateControls();
}
function returnToStart(){
 if(!viewBuffer||busy||dead||drag||eqGesture)return;
 stop(true);playAnchor=0;revealPlayhead();updateControls();
}
function togglePlayback(){
 if(!viewBuffer||busy||dead||drag||eqGesture)return;
 if(playing||playStarting)stopPlayback();else action('play');
}
function transportKeydown(e){
 if(!viewBuffer||e.defaultPrevented||e.isComposing||e.ctrlKey||e.metaKey||e.altKey||e.shiftKey)return;
 const target=e.target;
 if(target?.isContentEditable||target?.closest?.('input,textarea,select,[contenteditable]:not([contenteditable="false"]),[role="textbox"],[role="combobox"],[role="slider"],[role="spinbutton"],[role="checkbox"],[role="switch"],[role="radio"]'))return;
 const space=e.code==='Space'||e.key===' ',start=e.key==='Home'||(e.key==='Enter'&&(target===canvas||target===root));
 if(!space&&!start)return;
 e.preventDefault();e.stopPropagation();
 if(space)transportSpaceDown=true;
 if(e.repeat)return;
 if(space)togglePlayback();else returnToStart();
}
function transportKeyup(e){
 if((e.code==='Space'||e.key===' ')&&transportSpaceDown){transportSpaceDown=false;e.preventDefault();e.stopPropagation();}
}


 function position(){return playing?Math.min(playEnd,offset+context.currentTime-started):cursor;}
 function stop(reset){playToken++;if(playing)cursor=position();playing=false;playStarting=false;playPaused=false;if(source){source.onended=null;try{source.stop();}catch(e){}source.disconnect();source=null;}if(splitter){splitter.disconnect();splitter=null;}meters.forEach(m=>m.node.disconnect());meters=[];playEqNodes.forEach(node=>node.disconnect());playEqNodes=[];cancelAnimationFrame(raf);if(reset)cursor=0;meterState.forEach(x=>{x.value=0;x.hold=0;x.until=0;});renderMeter([0,0]);draw();stats();drawSpectrumMeter();drawEq();if(!dead)updateControls();}
 function renderMeter(values){const now=performance.now();['L','R'].forEach((letter,c)=>{const s=meterState[c];s.value=Math.max(values[c],s.value*.87);if(values[c]>=s.hold||now>s.until){s.hold=values[c];s.until=now+1000;}const p=clamp((db(s.value)+60)/60*100,0,100),hold=clamp((db(s.hold)+60)/60*100,0,100);$('mask'+letter).style.width=(100-p)+'%';$('hold'+letter).style.left=hold+'%';$('level'+letter).textContent=show(db(s.value));});}
 function liveStats(){
  if(processingPreviewActive()){displayMetrics(null);return;}if(!metrics||$('meterMode').value!=='live')return;
  const end=Math.min(Math.floor(cursor*viewBuffer.rate/metrics.hop),Math.floor(viewBuffer.ch[0].length/metrics.hop)),first=Math.ceil(offset*viewBuffer.rate/metrics.hop);
  if(end-4<first){displayMetrics(null);return;}
  let z=0,r=0,p=0,tp=0,left=0,right=0,cross=0;for(let i=end-4;i<end;i++){z+=metrics.weighted[i];r+=metrics.raw[i];p=Math.max(p,metrics.peaks[i]);tp=Math.max(tp,metrics.truePeaks[i]);left+=metrics.leftEnergy[i];right+=metrics.rightEnergy[i];cross+=metrics.crossEnergy[i];}
  const rms=db(Math.sqrt(r/(4*metrics.hop*metrics.nc))),peak=db(p);displayMetrics({lufs:ld(z/(4*metrics.hop)),rms,peak,tp:db(tp),crest:p>0?peak-rms:NaN,...stereoReadings(left,right,cross,4*metrics.hop,metrics.nc===1)});
 }
 function frame(now){if(!playing||dead)return;cursor=position();if(cursor>v1&&v1<duration())setViewport(cursor,cursor+(v1-v0));if(now-lastFrame>32){lastFrame=now;draw();const values=meters.map(m=>{m.node.getFloatTimeDomainData(m.data);let max=0;for(let i=0;i<m.data.length;i++)max=Math.max(max,Math.abs(m.data[i]));return max;});renderMeter(values);liveStats();syncPhaseFromPlayback();drawSpectrumMeter();if(!root.querySelector('[data-pane="eq"]')?.hidden)drawEq();}raf=requestAnimationFrame(frame);}
 async function play(useSelection){
  if(!viewBuffer||busy||dead||drag||eqGesture)return;if(playing||playStarting)return;
  const resuming=playPaused&&!useSelection,anchor=playAnchor,resumeEnd=pausedEnd;
  let start=useSelection?selection[0]:cursor,end=useSelection?selection[1]:(resuming?resumeEnd:duration());
  if(end<=start||start>=duration()){start=0;end=duration();}
  stop(false);const ticket=playToken;cursor=start;playAnchor=resuming?anchor:start;playEnd=end;playStarting=true;updateControls();
  try{const AC=window.AudioContext||window.webkitAudioContext;if(!AC)throw Error(tr("web_audio_missing","This browser does not support Web Audio. Try a current browser."));if(context&&context.sampleRate!==viewBuffer.rate){const closing=context;context=null;await closing.close();if(ticket!==playToken||busy||dead||!viewBuffer)return;}if(!context)context=new AC({sampleRate:viewBuffer.rate});const playbackContext=context;await playbackContext.resume();if(ticket!==playToken||context!==playbackContext||busy||dead||!viewBuffer)return;
   const playBuffer=cleanupPlayback(),monitor=lcvrMonitorChannels(playBuffer,component());const b=context.createBuffer(2,playBuffer.ch[0].length,playBuffer.rate);monitor.forEach((channel,index)=>b.copyToChannel(channel,index));
   source=context.createBufferSource();source.buffer=b;let tail=source;playEqNodes=lcvrEqStages(eqBands,viewBuffer.rate,{bypass:eqBypassed}).map(stage=>{const node=context.createBiquadFilter();node.type=stage.type;node.frequency.value=stage.frequency;node.Q.value=stage.Q;node.gain.value=stage.gain;tail.connect(node);tail=node;return node;});splitter=context.createChannelSplitter(2);tail.connect(splitter);tail.connect(context.destination);
   meters=[0,1].map(c=>{const node=context.createAnalyser();node.fftSize=8192;node.smoothingTimeConstant=.65;splitter.connect(node,c);return {node,data:new Float32Array(8192),frequency:new Float32Array(node.frequencyBinCount)};});
   offset=start;playEnd=end;started=context.currentTime;playStarting=false;playing=true;const own=source;source.onended=()=>{if(source===own)stopPlayback();};source.start(0,start,end-start);updateControls();raf=requestAnimationFrame(frame);
  }catch(e){if(ticket===playToken){stop(false);note(errorText(e),true);}}
 }
 function download(blob,name,key){const a=$(key),old=a.getAttribute('href');if(old){URL.revokeObjectURL(old);urls=urls.filter(u=>u!==old);}const url=URL.createObjectURL(blob);urls.push(url);a.href=url;a.download=name;a.textContent=tr("save_file","Save {file}",{file:name});a.hidden=false;}
 function exportIdentity(){
  const title=releaseInfo.title||tracks[0]?.name.replace(/\.(wav|mp3|aif|aiff)$/i,'').trim()||tr('record_default','Record');
  const slug=lcvrSlug(title)||'record',side=$('side').value==='SideB'?'B':'A';
  return {title,slug,side,base:slug+'_side'+side.toLowerCase()};
 }
function targetExportRate(sourceRate) {
  const value = $('exportRate')?.value || 'original';
  if (value === 'original') return sourceRate;
  const rate = Number(value);
  if (![44100, 48000, 88200, 96000].includes(rate)) throw Error(tr('export_rate_invalid','Choose Original, 44.1, 48, 88.2 or 96 kHz.'));
  return rate;
}
function exportPlan() {
  return lcvrExportPlan(tracks, transition, targetExportRate(tracks[0]?.rate), MAX);
}
async function prepareExportAudio() {
  const plan = exportPlan();
  if (plan.rate === tracks[0].rate) return await assemble();
  const converted = [], total = plan.tracks.reduce((sum, track) => sum + track.ch[0].length, 0);
  let completed = 0;
  for (let index = 0; index < tracks.length; index++) {
    assertAlive();
    const track = tracks[index], planned = plan.tracks[index];
    const renderedTrack = await rendered(track);
    const audio = await lcvrResampleAudio(renderedTrack, plan.rate, {
      maxOutputBytes: MAX, maxWorkingBytes: 2 * MAX, cancelled: () => dead, yieldTask: pause,
      onProgress: fraction => {
        const progress = (completed + planned.ch[0].length * fraction) / total * 100;
        if (!dead) note(tr('resampling_progress','Converting to {rate} kHz · {percent}%', {rate:plan.rate/1000,percent:Math.round(progress)}),false,progress);
      }
    });
    assertAlive();
    converted.push({...audio, markers:planned.markers, fade:emptyFade()});
    completed += planned.ch[0].length;
  }
  note(tr('export_assembling','Preparing the converted record side…'));
  await pause();
  const audio = await assembleInputs(converted);
  if (audio.rate !== plan.rate || audio.ch.length !== plan.channels || audio.ch.some(channel => channel.length !== plan.frames) ||
      JSON.stringify(audio.timing) !== JSON.stringify(plan.timing) || JSON.stringify(audio.exportMarks) !== JSON.stringify(plan.exportMarks)) {
    throw Error(tr('export_timing_mismatch','Converted audio did not match its markers. Export stopped.'));
  }
  return audio;
}

 async function exportWav(){
  assertEqSaved();
  const bits=Number($('bits').value);if(![16,24,32].includes(bits))throw Error(tr('wav_export_bits','Choose 16, 24 or 32-bit PCM WAV.'));
  const b=await prepareExportAudio(),n=b.ch[0].length,nc=b.ch.length,size=bits/8,bytes=n*nc*size,pad=bytes&1;
  const header=lcvrWavHeader({rate:b.rate,channels:nc,bits,block:nc*size,kind:'pcm'},n),metadata=lcvrWaveMarkers(b.exportMarks);
  const length=header.length+bytes+pad+metadata.reduce((total,chunk)=>total+chunk.length,0);
  if(!Number.isSafeInteger(length)||length-8>4294967295)throw Error(tr('wav_size_limit','This side is too large for a standard WAV file.'));
  const out=new Uint8Array(length),v=new DataView(out.buffer);out.set(header);v.setUint32(4,length-8,true);
  let p=header.length;const scale=2**(bits-1);for(let begin=0;begin<n;begin+=65536){for(let i=begin;i<Math.min(n,begin+65536);i++)for(let c=0;c<nc;c++){const x=b.ch[c][i];if(!Number.isFinite(x)||Math.abs(x)>1)throw Error(tr("export_peak_error","Export stopped: audio exceeds 0 dBFS. Select Whole side and lower its volume or peak-normalize first."));const z=clamp(Math.round(x*scale),-scale,scale-1);if(bits===16){v.setInt16(p,z,true);p+=2;}else if(bits===32){v.setInt32(p,z,true);p+=4;}else{v.setUint8(p,z&255);v.setUint8(p+1,(z>>8)&255);v.setUint8(p+2,(z>>16)&255);p+=3;}}const progress=Math.min(n,begin+65536)/n*100;note(tr("wav_preparing","Preparing WAV {percent}%",{percent:Math.round(progress)}),false,progress);await pause();}
  p+=pad;for(const chunk of metadata){out.set(chunk,p);p+=chunk.length;}
  download(new Blob([out],{type:'audio/wav'}),exportIdentity().base+'.wav','download');
  note(tr("wav_ready","WAV ready to save.")+(b.rate<44100||b.rate>96000?' '+tr('export_rate_warning','This file is {rate} Hz. Record uploads require 44.1–96 kHz. Its original sample rate was preserved.',{rate:b.rate}):''));
 }

 async function action(a){
  if(a==='undo'||a==='redo'){await historyNavigate(historyIndex+(a==='undo'?-1:1));return;}
  if(a==='applyMetadata'){applyMetadata();return;}
  if(a==='resetMetadata'){resetMetadata();return;}
  if(metadataDirty()&&!['play','pause','stop','returnStart','playSelection','selectAll','clearSelection','prevTransient','nextTransient'].includes(a)){metadataPending();return;}

  if(a==='previewCleanup'){await run(previewCleanup);if(cleanupDraft&&!dead)await play(false);return;}
  if(a==='applyCleanup'){await run(applyCleanup);return;}
  if(a==='resetCleanup'){if(busy||drag||eqGesture)return;stop(false);discardCleanup();stats();updateControls();note(tr('cleanup_reset','Processed preview reset. The committed audio is unchanged.'));return;}
  if(cleanupDraft&&!['play','pause','stop','returnStart','playSelection','selectAll','clearSelection','prevTransient','nextTransient'].includes(a)){note(tr('cleanup_pending','Apply or reset the processed preview before saving or starting another edit.'),true);return;}
  if(a==='split'){splitAtPlayhead();return;}
  if(a==='addMarker'){addMarker();return;}
  if(a==='play'){await play(!playPaused&&hasSelection());return;}if(a==='playSelection'){await play(true);return;}if(a==='pause'){pausePlayback();return;}if(a==='stop'){stopPlayback();return;}if(a==='returnStart'){returnToStart();return;}
  if(a==='selectAll'){setSelection(0,duration());return;}if(a==='clearSelection'){setSelection(0,0);return;}
  await run(async()=>{
   if(a==='applyEq'){await applyEq();return;}
   if(a==='resetEq'){eqBands=lcvrEqDefaultBands(viewBuffer?.rate||44100);eqBypassed=false;syncEqFields();note(tr('eq_reset','EQ preview reset.'));return;}
   if(['export','pq','markers','metadata'].includes(a))assertEqSaved();
   if(['setFades','resetFades','normalize','removeDc','mono','stereo'].includes(a))assertStereoTool();
   if(a==='prevTransient'||a==='nextTransient'){await navigateTransient(a==='prevTransient'?-1:1);return;}

   if(a==='reset'){if(!confirm(tr("clear_confirm","Clear this project? Unsaved edits will be lost.")))return;tracks=[];selected=0;undo=null;releaseInfo={title:'',artist:'',catalog:'',barcode:''};eqBands=lcvrEqDefaultBands(44100);eqBypassed=false;if($('component'))$('component').value='stereo';invalidate();await refresh();historyReset();note(tr("project_cleared","Add audio to begin."));return;}
   if(a==='preview'){$('view').value='side';await refresh();note(tr("side_preview_ready","Whole-side preview ready, including transitions and markers."));return;}
   if(a==='transition'){const next={mode:$('transition').value,gap:num('gap',0,10),cross:num('cross',.01,10)};checkpoint(tr('history_transitions','Transitions'));transition=next;invalidate();await refresh();note(tr("transitions_updated","Transitions updated. Preview the whole side to hear them."));return;}
   if(['cut','trim','trimStart','trimEnd'].includes(a)){await editAudioRegion(a);return;}
   if(a==='setFades'||a==='resetFades'){
    if(sideView())throw Error(tr("fades_song_only","Choose an individual song for adjustable fades."));
    await commitFade(a==='resetFades'?emptyFade():{in:num('fadeInTime',0,duration()),out:num('fadeOutTime',0,duration()),ic:num('inCurve',.25,4),oc:num('outCurve',.25,4)});return;
   }
   if(['insertSilence','insertSilenceStart','insertSilenceEnd'].includes(a)){
    if(sideView())throw Error(tr('silence_song_only','Choose an individual song to insert silence.'));
    const t=current(),seconds=num('silenceSeconds',0,600),frames=Math.round(seconds*t.rate),at=a==='insertSilenceStart'?0:a==='insertSilenceEnd'?t.ch[0].length/t.rate:snap(cursor),available=MAX-(projectBytes(tracks)-projectBytes([t]));
    if(!frames)throw Error(tr('silence_duration','Enter a silence duration of at least one audio sample.'));
    if((t.ch[0].length+frames)*t.ch.length*4>available)throw Error(tr('silence_memory','Inserting this silence would exceed the 256 MiB project limit. Use a shorter duration.'));
    await editTrack(async track=>{const b=await rendered(track);try{const next=await insertSilenceBuffer(b,at,seconds,{maxBytes:available,cancelled:()=>dead,onProgress:p=>note(tr('silence_progress','Inserting silence {percent}%',{percent:Math.round(p*100)}),false,p*100)});return {...next,fade:emptyFade(),markers:remapAudioMarkers(track.markers||[],{type:'insert',start:Math.round(at*track.rate),frames}),splits:remapEditSplits(track.splits,{type:'insert',start:Math.round(at*track.rate),frames},next.ch[0].length)};}catch(e){if(dead||e.name==='AbortError')throw e;throw Error(tr('silence_failed','Could not insert silence. The original song is unchanged.'));}},historyLabel(a));
    cursor=at;setSelection(at,at+frames/t.rate);note(tr('silence_done','Inserted {seconds} seconds of silence at {time}.',{seconds:(frames/t.rate).toFixed(6),time:time(at,6)}));return;
   }
   if(a==='removeDc'){
    const all=sideView(),next=tracks.slice(),count=all?tracks.length:1;let done=0;
    for(let i=0;i<tracks.length;i++){if(!all&&i!==selected)continue;const b=await rendered(tracks[i]);try{const result=await removeDcOffsetBuffer(b,{cancelled:()=>dead,onProgress:p=>{const progress=(done+p)/count*100;note(tr('dc_progress','Removing DC offset {percent}%',{percent:Math.round(progress)}),false,progress);}});next[i]={...result,fade:emptyFade()};done++;}catch(e){if(dead||e.name==='AbortError')throw e;throw Error(tr('dc_failed','Could not remove DC offset. The original audio is unchanged.'));}}
    assertAlive();checkpoint(historyLabel('removeDc'));tracks=next;invalidate();await refresh(true);note(all?tr('dc_done_side','Removed DC offset from each song. The silent gaps are unchanged.'):tr('dc_done_song','Removed DC offset from the selected song.'));return;
   }
   if(a==='gain'){const gain=num('gain',-60,24);await applyGain(gain);note(tr("gain_applied","Applied {gain} dB.",{gain:gain.toFixed(1)}));return;}
   if(a==='normalize'){
    if(!metrics||!Number.isFinite(metrics.peak))throw Error(tr("normalize_silent","Silent audio cannot be normalized."));let gain,limited=false;
    if($('normMode').value==='peak'){gain=num('peakTarget',-30,0)-metrics.peak;}else{if(!Number.isFinite(metrics.lufs))throw Error(tr("normalize_short","LUFS needs at least 400 ms of measurable audio."));const wanted=num('lufsTarget',-40,-5)-metrics.lufs,max=num('ceiling',-12,0)-metrics.tp;gain=Math.min(wanted,max);limited=gain<wanted-.01;}
    if(gain>60)throw Error(tr("normalize_gain_limit","Required gain exceeds 60 dB. Check the source instead of amplifying nearly silent audio."));await applyGain(gain,historyLabel('normalize'));note(limited?tr("normalization_limited","Applied {gain} dB · now {lufs} LUFS-I / {peak} dBTP estimated. Target not reached: estimated TP ceiling limited the gain. No limiter was applied.",{gain:gain.toFixed(2),lufs:show(metrics.lufs),peak:show(metrics.tp)}):tr("normalization_done","Applied {gain} dB · now {lufs} LUFS-I / {peak} dBTP estimated.",{gain:gain.toFixed(2),lufs:show(metrics.lufs),peak:show(metrics.tp)}));return;
   }
   if(a==='mono'||a==='stereo'){
    const all=sideView(),affected=tracks.filter((t,i)=>all||i===selected);
    if(a==='mono'&&affected.some(t=>t.ch.length===2)&&!confirm(tr("mono_confirm","Stereo to mono averages L + R. Opposite-phase material can cancel. Continue, then audition the result?")))return;
    const next=tracks.map((t,i)=>{if(!all&&i!==selected)return t;if(a==='mono'&&t.ch.length===2){const out=new Float32Array(t.ch[0].length);for(let k=0;k<out.length;k++)out[k]=(t.ch[0][k]+t.ch[1][k])*.5;return {...t,ch:[out]};}if(a==='stereo'&&t.ch.length===1)return {...t,ch:[t.ch[0],t.ch[0].slice()]};return t;});
    if(projectBytes(next)>MAX)throw Error(tr("stereo_memory","Stereo conversion would exceed the decoded-audio memory limit."));checkpoint(historyLabel(a));tracks=next;invalidate();await refresh();note(a==='mono'?tr("mono_done","Converted to mono. Listen for cancellation."):tr("stereo_done","Converted to stereo. Mono sources use identical L/R channels."));return;
   }
   if(a==='metadata'){
    const b=exportPlan(),id=exportIdentity(),release={...releaseInfo,title:id.title};
    download(new Blob([lcvrMetadataCsv(release,tracks,b.timing,id.side)],{type:'text/csv;charset=utf-8'}),id.base+'_metadata.csv','metadataDownload');
    note(tr('metadata_ready','Metadata sheet ready to save.'));return;
   }
   if(a==='export'){await exportWav();return;}
   if(a==='pq'){
    const b=exportPlan(),identity=exportIdentity();
    download(new Blob([lcvrPqCsv(identity.title,identity.side,b.timing)],{type:'text/csv;charset=utf-8'}),identity.slug+'_pq_sheet.csv','pq');
    note(tr('pq_ready',"PQ sheet ready to save."));return;
   }
   if(a==='markers'){
    const b=exportPlan(),cell=lcvrCsvCell;
    const rows=[[tr("csv_marker","Marker"),tr("csv_title","Title"),tr("csv_seconds","Start seconds"),tr("csv_sample","Start sample"),tr("csv_rate","Sample rate"),tr('csv_machine_label','Machine label'),tr('csv_marker_type','Marker type'),tr('csv_track','Track')].map(cell).join(',')];
    b.exportMarks.forEach((m,i)=>rows.push([i+1,cell(m.name),(m.frame/b.rate).toFixed(6),m.frame,b.rate,cell(m.label),cell(m.type),m.track].join(',')));
    download(new Blob([rows.join('\r\n')],{type:'text/csv;charset=utf-8'}),exportIdentity().base+'-markers.csv','csv');note(tr("markers_ready","Markers ready to save."));
   }
  });
 }

// Component views and the in-frame EQ. EQ settings are an audition draft until Apply.
function component(){return $('component')?.value||'stereo';}
function componentLabel(){return component()==='left'?tr('component_left','Left'):component()==='right'?tr('component_right','Right'):component()==='mid'?tr('component_mid','Mid'):component()==='side'?tr('component_side','Side'):tr('component_stereo','Stereo');}
function eqPass(b){return b.type==='highpass'||b.type==='lowpass';}
function eqDirty(){return eqBands.some(lcvrEqBandActive);}
function eqMaximum(){return Math.min(20000,(viewBuffer?.rate||44100)*.49);}
function eqPreviewActive(){return eqDirty()&&!eqBypassed;}
function assertStereoTool(){if(component()!=='stereo')throw Error(tr('stereo_tool_channels','Choose Stereo for fades, normalization, DC removal or channel conversion. Gain, EQ, de-essing and HF limiting can edit Left, Right, Mid or Side.'));}
function assertEqSaved(){if(cleanupDraft)throw Error(tr('cleanup_pending','Apply or reset the processed preview before saving or starting another edit.'));if(eqDirty()||eqInvalidFields.size)throw Error(tr('eq_pending','Apply or reset your EQ preview before saving.'));}
function setTransportLabel(){const label=tr('play','Play');transport.setAttribute('aria-label',label);transport.setAttribute('title',label);transport.setAttribute('aria-pressed',String(playing||playStarting));transport.dataset.playing=String(playing||playStarting);const text=transport.querySelector('[data-transport-label]');if(text)text.textContent=label;}
function setExpandLabel(){const button=root.querySelector('[data-ui="expand"]'),label=modal?tr('close_expanded','Close expanded'):tr('expand_editor','Expand editor');button.setAttribute('aria-label',label);button.setAttribute('title',label);button.setAttribute('aria-expanded',String(!!modal));button.dataset.expanded=String(!!modal);}
function syncEqFields(){
 if(!$('eqCanvas'))return;
 eqInvalidFields.clear();['eqFrequency','eqGain','eqQ'].forEach(key=>$(key).removeAttribute('aria-invalid'));
 const rate=viewBuffer?.rate||44100;eqBands=eqBands.map(b=>lcvrEqBand(b,rate));const band=eqBands[eqBandIndex];
 $('eqFrequency').max=String(eqMaximum());$('eqFrequency').value=String(Math.round(band.frequency));$('eqGain').value=band.gain.toFixed(1);$('eqQ').value=band.q.toFixed(2);
 const pass=eqPass(band);$('eqGainField').hidden=pass;$('eqQField').hidden=pass;$('eqFilterControls').hidden=!pass;$('eqEnabled').checked=pass&&band.enabled;$('eqSlope').value=String(band.slope||12);
 $('eqQ').disabled=busy||band.type!=='peaking';$('eqBypass').setAttribute('aria-pressed',String(eqBypassed));
 q('[data-eq-band]').forEach(b=>{const i=Number(b.dataset.eqBand);b.setAttribute('aria-pressed',String(i===eqBandIndex));b.dataset.enabled=String(lcvrEqBandActive(eqBands[i]));b.disabled=busy||!viewBuffer;});
 $('eqCanvas').setAttribute('aria-label',tr('eq_canvas_filters','EQ curve. Drag a numbered band to change frequency and gain. Drag HPF or LPF horizontally to enable it and set its cutoff. Arrow keys adjust the selected band.'));
 drawEq();
}
function updateEqPreview(){
 invalidateDownloads();
 if(context&&playEqNodes.length){const now=context.currentTime,stages=lcvrEqStages(eqBands,viewBuffer.rate,{bypass:eqBypassed});playEqNodes.forEach((node,i)=>{const stage=stages[i];node.type=stage.type;node.frequency.setTargetAtTime(stage.frequency,now,.012);node.Q.setTargetAtTime(stage.Q,now,.012);node.gain.setTargetAtTime(stage.gain,now,.012);});}
 $('eqBypass')?.setAttribute('aria-pressed',String(eqBypassed));stats();drawEq();updateControls();
 if(eqDirty())note(eqBypassed?tr('eq_original','Listening to the original. EQ preview is waiting.'):tr('eq_preview','EQ preview · Apply to keep it.'));
 else note(tr('eq_flat','EQ is flat.'));
}
function eqPlot(){const c=$('eqCanvas');if(!c)return null;const width=c.clientWidth||600,height=c.clientHeight||210;return {c,width,height,left:38,right:Math.max(80,width-20),top:22,bottom:Math.max(100,height-28)};}
function eqX(f,p){return p.left+Math.log(f/20)/Math.log(eqMaximum()/20)*(p.right-p.left);}
function eqY(g,p){return p.top+(18-clamp(g,-18,18))/36*(p.bottom-p.top);}
function eqHandleY(b,p){return eqY(eqPass(b)?(b.enabled?-3.0103:0):b.gain,p);}
function canvasSurface(c){if(!c)return null;const width=c.clientWidth,height=c.clientHeight;if(width<2||height<2)return null;const ratio=Math.min(2,window.devicePixelRatio||1),cw=Math.round(width*ratio),ch=Math.round(height*ratio);if(c.width!==cw||c.height!==ch){c.width=cw;c.height=ch;}const g=c.getContext('2d');g.setTransform(ratio,0,0,ratio,0,0);g.clearRect(0,0,width,height);return {g,width,height};}
function drawEq(){
 const p=eqPlot();if(!p)return;const surface=canvasSurface(p.c);if(!surface)return;const {g}=surface;
 g.fillStyle='#07100b';g.fillRect(0,0,p.width,p.height);g.font='12px ui-monospace,monospace';g.lineWidth=1;
 [-18,-12,-6,0,6,12,18].forEach(db=>{const y=eqY(db,p);g.strokeStyle=db===0?'#91a98b88':'#91a98b24';g.beginPath();g.moveTo(p.left,y);g.lineTo(p.right,y);g.stroke();if(db%12===0){g.fillStyle='#d9d2b8';g.textAlign='right';g.fillText(db>0?'+'+db:String(db),p.left-7,y+4);}});
 [20,50,100,200,500,1000,2000,5000,10000,20000].filter(f=>f<=eqMaximum()).forEach((f,i)=>{const x=eqX(f,p);g.strokeStyle='#91a98b24';g.beginPath();g.moveTo(x,p.top);g.lineTo(x,p.bottom);g.stroke();if(p.width>650||[20,100,1000,10000,20000].includes(f)){g.fillStyle='#d9d2b8';g.textAlign='center';g.fillText(f>=1000?f/1000+'k':String(f),x,p.height-8);}});
 if(playing&&meters.length){const points=spectrumPoints(120);if(points.length){g.beginPath();g.moveTo(p.left,p.bottom);for(const point of points)g.lineTo(eqX(point.frequency,p),p.bottom-clamp((point.level+90)/90,0,1)*(p.bottom-p.top));g.lineTo(p.right,p.bottom);g.closePath();g.fillStyle='#49ce7a25';g.fill();}}
 const colors=['#df7167','#edc75b','#55d58b','#a5c993','#ef9d72','#9dd4b7'],rate=viewBuffer?.rate||44100,coefficients=eqBands.map(b=>lcvrEqCoefficientList([b],rate));
 eqBands.forEach((band,i)=>{if(eqPass(band)&&!band.enabled)return;g.strokeStyle=colors[i]+'65';g.lineWidth=1;g.beginPath();for(let x=0;x<=160;x++){const f=20*Math.pow(eqMaximum()/20,x/160),y=eqY(lcvrEqResponseFromCoefficients(coefficients[i],rate,f),p);if(x)g.lineTo(eqX(f,p),y);else g.moveTo(eqX(f,p),y);}g.stroke();});
 const combined=coefficients.flat();g.beginPath();for(let x=0;x<=200;x++){const f=20*Math.pow(eqMaximum()/20,x/200),y=eqY(eqBypassed?0:lcvrEqResponseFromCoefficients(combined,rate,f),p);if(x)g.lineTo(eqX(f,p),y);else g.moveTo(eqX(f,p),y);}g.strokeStyle=eqBypassed?'#aaac9c':'#f0d174';g.lineWidth=2.5;g.stroke();
 eqBands.forEach((band,i)=>{const x=eqX(band.frequency,p),y=eqHandleY(band,p),pass=eqPass(band);g.beginPath();g.arc(x,y,i===eqBandIndex?12:10,0,Math.PI*2);g.fillStyle=pass&&!band.enabled?'#13281d':colors[i];g.fill();g.strokeStyle=i===eqBandIndex?'#fff2c9':pass?colors[i]:'#0b190e';g.lineWidth=2;g.stroke();g.fillStyle=pass&&!band.enabled?colors[i]:'#0a180e';g.font='bold 12px Arial';g.textAlign='center';g.fillText(pass?(band.type==='highpass'?'H':'L'):String(i+1),x,y+4);});
}
function spectrumPoints(count){
 if(!playing||!meters.length||!context)return [];
 meters.forEach(m=>{m.node.getFloatFrequencyData(m.frequency);});
 const top=Math.min(eqMaximum(),context.sampleRate/2),out=[],bins=meters[0].frequency.length;
 for(let i=0;i<count;i++){const lo=20*Math.pow(top/20,i/count),hi=20*Math.pow(top/20,(i+1)/count);let peak=-120;const first=clamp(Math.floor(lo/context.sampleRate*meters[0].node.fftSize),1,bins-1),last=clamp(Math.ceil(hi/context.sampleRate*meters[0].node.fftSize),first,bins-1);
  for(let k=first;k<=last;k++){let energy=0;for(const m of meters)energy+=10**(m.frequency[k]/10);peak=Math.max(peak,10*Math.log10(energy/meters.length||1e-12));}
  out.push({frequency:Math.sqrt(lo*hi),level:peak});
 }return out;
}
function drawSpectrumMeter(){
 const surface=canvasSurface($('spectrumMeter'));if(!surface)return;const {g,width,height}=surface,points=spectrumPoints(36),bottom=height-6,top=7;
 g.fillStyle='#07100b';g.fillRect(0,0,width,height);g.lineWidth=1;g.strokeStyle='#8d9f7d33';[.25,.5,.75].forEach(p=>{const y=bottom-p*(bottom-top);g.beginPath();g.moveTo(0,y);g.lineTo(width,y);g.stroke();});
 if(points.length){const gradient=g.createLinearGradient(0,bottom,0,top);gradient.addColorStop(0,'#49c47c');gradient.addColorStop(.72,'#e3c65a');gradient.addColorStop(1,'#df6a5f');g.fillStyle=gradient;const bw=width/points.length;points.forEach((p,i)=>{const level=clamp((p.level+90)/90,0,1)*(bottom-top);g.fillRect(i*bw+1,bottom-level,Math.max(1,bw-2),level);});}
 else{g.fillStyle='#b3bda7';g.font='12px Arial';g.textAlign='center';g.fillText(viewBuffer?tr('spectrum_play','Play to see frequencies'):tr('spectrum_empty','Frequency'),width/2,height/2);}
 if($('spectrumMax'))$('spectrumMax').textContent=(eqMaximum()/1000).toFixed(eqMaximum()===20000?0:1)+' kHz';
}
function syncPhaseFromPlayback(){
 if(!playing||meters.length<2)return;let left=0,right=0,cross=0;const a=meters[0].data,b=meters[1].data;for(let i=0;i<a.length;i++){left+=a[i]*a[i];right+=b[i]*b[i];cross+=a[i]*b[i];}const reading=stereoReadings(left,right,cross,a.length,viewBuffer?.ch.length===1&&component()!=='side');
 const valid=Number.isFinite(reading.correlation),needle=$('correlationNeedle');if($('correlation'))$('correlation').textContent=valid?(reading.correlation>=0?'+':'')+reading.correlation.toFixed(2):'—';if(needle){needle.hidden=!valid;needle.style.left=(valid?(reading.correlation+1)*50:50)+'%';needle.dataset.phase=reading.phaseKind;}
 [['mid','M'],['sideLevel','S']].forEach(([key,letter])=>{if($(key))$(key).textContent=show(reading[key]);if($('mask'+letter))$('mask'+letter).style.width=(100-clamp((reading[key]+60)/60*100,0,100))+'%';});
}
async function applyEq(){
 if(eqInvalidFields.size)throw Error(tr('eq_invalid_fields','Correct the highlighted EQ value before applying.'));
 if(!eqDirty()){note(tr('eq_flat','EQ is flat.'));return;}
 const target=component(),all=sideView(),next=tracks.slice(),count=all?tracks.length:1;let done=0;
 for(let i=0;i<tracks.length;i++){if(!all&&i!==selected)continue;const t=tracks[i];if(lcvrStereoOnlyComponent(target)&&t.ch.length===1){done++;continue;}
  const b=await rendered(t);next[i]={...await lcvrProcessTargetEq(b,eqBands,target,{cancelled:()=>dead,onProgress:p=>note(tr('eq_applying','Applying EQ {percent}%',{percent:Math.round((done+p)/count*100)}),false,(done+p)/count*100)}),fade:emptyFade()};done++;
 }
 assertAlive();checkpoint(historyLabel('applyEq'));tracks=next;invalidate();eqBands=lcvrEqDefaultBands(viewBuffer?.rate||44100);eqBypassed=false;await refresh(true);syncEqFields();note(tr('eq_applied','EQ applied to {component}. Undo restores the audio.',{component:componentLabel()}));
}
function installEqControls(){
 if(!$('eqCanvas'))return;
 q('[data-eq-band]').forEach(button=>on(button,'click',()=>{if(cleanupDraft||busy||eqGesture||!viewBuffer)return;eqBandIndex=Number(button.dataset.eqBand);syncEqFields();}));
 ['eqFrequency','eqGain','eqQ'].forEach(key=>on($(key),'input',()=>{if(cleanupDraft||busy||eqGesture||!viewBuffer)return;const raw=$(key).value.trim(),v=Number(raw),range=key==='eqFrequency'?[20,eqMaximum()]:key==='eqGain'?[-18,18]:[.1,18];if(!raw||!Number.isFinite(v)||v<range[0]||v>range[1]){eqInvalidFields.add(key);$(key).setAttribute('aria-invalid','true');invalidateDownloads();updateControls();note(tr('eq_range','Enter a value from {min} to {max}.',{min:range[0],max:range[1]}),true);return;}eqInvalidFields.delete(key);$(key).removeAttribute('aria-invalid');eqBands[eqBandIndex]={...eqBands[eqBandIndex],[key==='eqFrequency'?'frequency':key==='eqGain'?'gain':'q']:v};updateEqPreview();}));
 ['eqEnabled','eqSlope'].forEach(key=>on($(key),'change',()=>{if(cleanupDraft||busy||eqGesture||!viewBuffer||!eqPass(eqBands[eqBandIndex]))return;eqBands[eqBandIndex]={...eqBands[eqBandIndex],enabled:$('eqEnabled').checked,slope:Number($('eqSlope').value)};syncEqFields();updateEqPreview();}));
 on($('eqBypass'),'click',()=>{if(cleanupDraft||busy||eqGesture)return;eqBypassed=!eqBypassed;updateEqPreview();});
 on($('component'),'change',()=>run(async()=>{await refresh(true);note(tr('component_selected_channels','{component} view. Processing edits this channel or component; cuts and trims keep both channels aligned.',{component:componentLabel()}));}));
 const eqCanvas=$('eqCanvas');
 on(eqCanvas,'pointerdown',e=>{if(cleanupDraft||busy||!viewBuffer||eqGesture)return;const rect=eqCanvas.getBoundingClientRect(),p=eqPlot(),x=e.clientX-rect.left,y=e.clientY-rect.top;let nearest=-1,distance=29;eqBands.forEach((b,i)=>{const d=Math.hypot(eqX(b.frequency,p)-x,eqHandleY(b,p)-y);if(d<distance){nearest=i;distance=d;}});if(nearest<0)return;e.preventDefault();eqBandIndex=nearest;eqGesture={id:e.pointerId,start:eqBands.map(b=>({...b})),x,y,frame:{left:rect.left,top:rect.top},plot:{...p}};eqCanvas.setPointerCapture(e.pointerId);eqCanvas.focus({preventScroll:true});syncEqFields();updateControls();});
 on(eqCanvas,'pointermove',e=>{if(!eqGesture||e.pointerId!==eqGesture.id)return;const p=eqGesture.plot,rect=eqGesture.frame,x=e.clientX-rect.left,y=e.clientY-rect.top,initial=eqGesture.start[eqBandIndex],pass=eqPass(initial);eqBands[eqBandIndex]={...initial,frequency:clamp(initial.frequency*Math.pow(eqMaximum()/20,(x-eqGesture.x)/(p.right-p.left)),20,eqMaximum()),...(pass?{enabled:true}:{gain:clamp(initial.gain-(y-eqGesture.y)*36/(p.bottom-p.top),-18,18)})};syncEqFields();updateEqPreview();});
 const finish=e=>{if(!eqGesture||e.pointerId!==eqGesture.id)return;const initial=eqGesture.start;eqGesture=null;if(e.type==='pointercancel'||e.type==='lostpointercapture'){eqBands=initial;syncEqFields();updateEqPreview();}try{if(eqCanvas.hasPointerCapture(e.pointerId))eqCanvas.releasePointerCapture(e.pointerId);}catch(error){}flushGestureLayout();updateControls();};
 ['pointerup','pointercancel','lostpointercapture'].forEach(type=>on(eqCanvas,type,finish));
 on(eqCanvas,'keydown',e=>{if(cleanupDraft||busy||!viewBuffer)return;const b=eqBands[eqBandIndex];if(e.key==='Escape'&&eqGesture){eqBands=eqGesture.start;eqGesture=null;syncEqFields();updateEqPreview();return;}if(['ArrowLeft','ArrowRight','ArrowUp','ArrowDown'].includes(e.key)){e.preventDefault();const pass=eqPass(b),up=e.key==='ArrowRight'||(pass&&e.key==='ArrowUp'),down=e.key==='ArrowLeft'||(pass&&e.key==='ArrowDown');eqBands[eqBandIndex]={...b,frequency:clamp(b.frequency*(up?1.05:down?1/1.05:1),20,eqMaximum()),...(pass?{enabled:true}:{gain:clamp(b.gain+(e.key==='ArrowUp'?.5:e.key==='ArrowDown'?-.5:0),-18,18)})};syncEqFields();updateEqPreview();}});
 syncEqFields();
}

// Rendered previews share the exact candidate tracks later committed by Apply.
// Processing runs per song, before side assembly inserts silence or crossfades.
function cleanupMode(){return $('cleanupMode')?.value||'deess';}
function cleanupLabel(){return cleanupMode()==='ellipse'?tr('cleanup_ellipse','Elliptical filter'):cleanupMode()==='hf'?tr('cleanup_hf','HF limiter'):tr('cleanup_deess','De-esser');}
function cleanupFrequencyRange(){const max=Math.min(12000,(sourceBuffer?.rate||44100)*.45);return cleanupMode()==='ellipse'?[20,Math.min(500,max)]:[2000,max];}
function cleanupPreviewActive(){return !!cleanupDraft&&!cleanupBypass;}
function processingPreviewActive(){return eqPreviewActive()||cleanupPreviewActive();}
function cleanupValid(){
 const mode=cleanupMode(),range=cleanupFrequencyRange(),fields=[['cleanupFrequency',range[0],range[1]]];
 if(mode==='deess')fields.push(['cleanupAmount',0,100]);
 if(mode==='hf')fields.push(['cleanupCeiling',-36,0]);
 return fields.every(([key,min,max])=>{const raw=$(key)?.value.trim(),n=Number(raw);return raw!==''&&Number.isFinite(n)&&n>=min&&n<=max;})&&(mode!=='ellipse'||['12','24'].includes($('cleanupSlope').value));
}
function readCleanupSettings(){
 if(!cleanupValid())throw Error(tr('cleanup_invalid','Correct the highlighted processing value before previewing.'));
 const mode=cleanupMode(),settings={frequency:Number($('cleanupFrequency').value)};
 if(mode==='deess')settings.amount=Number($('cleanupAmount').value);
 if(mode==='hf')settings.ceilingDb=Number($('cleanupCeiling').value);
 if(mode==='ellipse')settings.slope=Number($('cleanupSlope').value);
 return settings;
}
function discardCleanup(){
 cleanupDraft=null;cleanupRemoved=null;cleanupBypass=false;cleanupMonitor=false;
 if($('cleanupOriginal'))$('cleanupOriginal').checked=false;
 if($('cleanupListen'))$('cleanupListen').checked=false;
 if($('cleanupReduction'))$('cleanupReduction').textContent='—';
}
function syncCleanupFields(normalizeRate=false){
 if(!$('cleanupMode'))return;
 const mode=cleanupMode(),range=cleanupFrequencyRange();
 $('cleanupAmountField').hidden=mode!=='deess';$('cleanupCeilingField').hidden=mode!=='hf';$('cleanupSlopeField').hidden=mode!=='ellipse';
 $('cleanupReductionLabel').textContent=mode==='ellipse'?tr('cleanup_slope','Slope'):tr('cleanup_max_reduction','Max reduction');
 $('cleanupReduction').setAttribute('aria-label',mode==='ellipse'?tr('cleanup_slope','Slope'):tr('cleanup_maximum_gain_reduction','Maximum gain reduction'));
 $('cleanupFrequency').min=String(range[0]);$('cleanupFrequency').max=String(range[1]);
 // A lower-rate source may have a smaller usable high-frequency range.
 const frequency=Number($('cleanupFrequency').value);
 if(normalizeRate&&Number.isFinite(frequency)&&frequency>range[1])$('cleanupFrequency').value=String(Math.round(range[1]));
 $('cleanupAmountValue').textContent=Math.round(Number($('cleanupAmount').value)||0)+'%';
 $('cleanupHint').textContent=mode==='ellipse'?tr('cleanup_ellipse_hint','Reduces Side bass and keeps Mid unchanged. Preview listens in Stereo.'):
  mode==='hf'?tr('cleanup_hf_hint','Limits the extracted high-frequency band. The ceiling is not a full-mix output limit.'):
  tr('cleanup_deess_hint','Tames strong high-frequency bursts. It can also reduce bright cymbals; use Hear removed to check.');
 $('cleanupOriginal').checked=cleanupBypass;$('cleanupListen').checked=cleanupMonitor;
 if(cleanupDraft)$('cleanupReduction').textContent=cleanupDraft.mode==='ellipse'?cleanupDraft.settings.slope+' dB/oct':cleanupDraft.stats.maxReductionDb.toFixed(1)+' dB';
}
function cleanupStateMatches(){
 return !!cleanupDraft&&cleanupDraft.sourceTracks===tracks&&cleanupDraft.selected===selected&&cleanupDraft.scope===sideView()&&cleanupDraft.component===component()&&cleanupDraft.mode===cleanupMode()&&JSON.stringify(cleanupDraft.transition)===JSON.stringify(transition)&&JSON.stringify(cleanupDraft.settings)===JSON.stringify(readCleanupSettings());
}
function cleanupPlayback(){
 if(!cleanupDraft||cleanupBypass)return viewBuffer;
 return cleanupMonitor&&cleanupRemoved?cleanupRemoved:cleanupDraft.buffer;
}
function cleanupUpdateControls(blocked,loaded){
 if(!$('cleanupMode'))return;
 const pending=!!cleanupDraft,enabled=loaded&&!blocked;
 ['cleanupMode','cleanupFrequency','cleanupAmount','cleanupCeiling','cleanupSlope'].forEach(key=>$(key).disabled=!enabled);
 $('cleanupOriginal').disabled=!enabled||!pending;$('cleanupListen').disabled=!enabled||!pending;
 if(pending){
  $('view').disabled=true;$('song').disabled=true;$('component').disabled=true;$('files').disabled=true;root.querySelector('[data-ui="load"]').disabled=true;
  q('[data-track],[data-marker],[data-marker-name],[data-eq-band]').forEach(control=>control.disabled=true);
  ['transition','gap','cross','gain','fadeInTime','fadeOutTime','inCurve','outCurve','peakTarget','lufsTarget','ceiling','normMode','silenceSeconds','eqFrequency','eqGain','eqQ','eqBypass','eqEnabled','eqSlope'].forEach(key=>$(key).disabled=true);
  q('[data-act]').forEach(button=>{if(!['play','pause','stop','returnStart','playSelection','selectAll','clearSelection','prevTransient','nextTransient','applyCleanup','resetCleanup'].includes(button.dataset.act))button.disabled=true;});
 }
 const preview=root.querySelector('[data-act="previewCleanup"]'),apply=root.querySelector('[data-act="applyCleanup"]'),reset=root.querySelector('[data-act="resetCleanup"]');
 if(preview)preview.disabled=!enabled||pending||!cleanupValid()||eqDirty()||eqInvalidFields.size>0||(cleanupMode()==='ellipse'&&sourceBuffer.ch.length!==2);
 if(apply)apply.disabled=!enabled||!pending;
 if(reset)reset.disabled=blocked||!pending;
}
async function previewCleanup(){
 assertEqSaved();
 const settings=readCleanupSettings(),mode=cleanupMode();
 invalidateDownloads();
 if(mode==='ellipse'&&sourceBuffer?.ch.length!==2)throw Error(tr('cleanup_stereo_required','The elliptical filter needs stereo audio.'));
 discardCleanup();
 if(mode==='ellipse'&&component()!=='stereo'){$('component').value='stereo';await refresh(true);}
 const target=mode==='ellipse'?'side':component(),all=sideView(),next=tracks.slice(),sourceTracks=tracks,scopeSelected=selected,scopeComponent=component(),count=all?tracks.length:1;
 let done=0,totalFrames=0,weightedReduction=0,maxReductionDb=0,processedCount=0;
 for(let i=0;i<tracks.length;i++){
  if(!all&&i!==selected)continue;
  const t=tracks[i];
  if(lcvrStereoOnlyComponent(target)&&t.ch.length===1){done++;continue;}
  const cooked=await rendered(t),options={cancelled:()=>dead,onProgress:p=>{const value=(done+p)/count*100;note(tr('cleanup_rendering','Preparing preview {percent}%',{percent:Math.round(value)}),false,value);}};
  let result;
  if(mode==='ellipse'){
   const buffer=await lcvrProcessTargetEq(cooked,[{type:'highpass',frequency:settings.frequency,enabled:true,slope:settings.slope,gain:0}],'side',options);
   result={buffer,stats:{maxReductionDb:0,meanReductionDb:0,frames:buffer.ch[0].length}};
  }else result=mode==='hf'?await lcvrProcessHfLimiter(cooked,settings,target,options):await lcvrProcessDeesser(cooked,settings,target,options);
  next[i]={...result.buffer,fade:emptyFade()};done++;processedCount++;
  const frames=result.buffer.ch[0].length;totalFrames+=frames;weightedReduction+=(result.stats.meanReductionDb||0)*frames;maxReductionDb=Math.max(maxReductionDb,result.stats.maxReductionDb||0);
 }
 if(!processedCount)throw Error(tr('cleanup_no_stereo','There is no stereo song to process in this scope.'));
 const prepared=all?await assembleInputs(next):await rendered(next[selected]);
 const projected=scopeComponent==='stereo'?prepared:await lcvrProjectChannels(prepared,scopeComponent,{cancelled:()=>dead});
 assertAlive();
 if(tracks!==sourceTracks||selected!==scopeSelected||component()!==scopeComponent)throw Error(tr('cleanup_project_changed','The project changed. Prepare the preview again.'));
 cleanupDraft={tracks:next,sourceTracks,selected:scopeSelected,scope:all,component:scopeComponent,transition:{...transition},mode,settings,buffer:projected,stats:{maxReductionDb,meanReductionDb:totalFrames?weightedReduction/totalFrames:0}};
 invalidateDownloads();syncCleanupFields();stats();
 note(tr('cleanup_ready','{tool} preview ready. Play to listen; Apply keeps it.',{tool:cleanupLabel()}));
}
async function applyCleanup(){
 if(!cleanupDraft)throw Error(tr('cleanup_preview_first','Prepare a preview before applying this edit.'));
 if(!cleanupStateMatches())throw Error(tr('cleanup_stale','Settings or audio changed. Reset and prepare a new preview.'));
 const next=cleanupDraft.tracks,label=cleanupLabel();
 checkpoint(historyTargetLabel(label));tracks=next;invalidate();await refresh(true);syncCleanupFields();
 note(tr('cleanup_applied','{tool} applied. Undo restores the previous audio.',{tool:label}));
}
async function cleanupCompare(which){
 if(busy||drag||eqGesture||!cleanupDraft)return;
 const resume=playing;stop(false);
 if(which==='original'){
  cleanupBypass=$('cleanupOriginal').checked;
  if(cleanupBypass)cleanupMonitor=false;
 }else{
  cleanupMonitor=$('cleanupListen').checked;
  if(cleanupMonitor){
   cleanupBypass=false;
   if(!cleanupRemoved)await run(async()=>{cleanupRemoved=await lcvrDeesserRemoved(viewBuffer,cleanupDraft.buffer,{cancelled:()=>dead,onProgress:p=>note(tr('cleanup_difference','Preparing removed audio {percent}%',{percent:Math.round(p*100)}),false,p*100)});});
   if(!cleanupRemoved){cleanupMonitor=false;syncCleanupFields();updateControls();return;}
  }
 }
 syncCleanupFields();stats();updateControls();
 note(cleanupMonitor?tr('cleanup_listen','Listening to removed audio. Apply still saves the processed full audio.'):cleanupBypass?tr('cleanup_original','Listening to the original. The processed preview is waiting.'):tr('cleanup_processed','Listening to the processed preview. Apply keeps it.'));
 if(resume)await play(false);
}
function installCleanupControls(){
 if(!$('cleanupMode'))return;
 const defaults={deess:{frequency:6000,amount:35},hf:{frequency:6000,ceilingDb:-12},ellipse:{frequency:120,slope:12}};
 const changed=()=>{
  if(busy||drag||eqGesture)return;stop(false);discardCleanup();invalidateDownloads();syncCleanupFields();
  const range=cleanupFrequencyRange(),fields=[['cleanupFrequency',range[0],range[1]],['cleanupAmount',0,100],['cleanupCeiling',-36,0]];
  fields.forEach(([key,min,max])=>{const raw=$(key).value.trim(),n=Number(raw);if(raw===''||!Number.isFinite(n)||n<min||n>max)$(key).setAttribute('aria-invalid','true');else $(key).removeAttribute('aria-invalid');});
  stats();updateControls();note(cleanupValid()?tr('cleanup_settings','Preview to hear these settings.'):tr('cleanup_invalid','Correct the highlighted processing value before previewing.'),!cleanupValid());
 };
 on($('cleanupMode'),'change',()=>{if(busy||drag||eqGesture)return;const settings=defaults[cleanupMode()];$('cleanupFrequency').value=String(settings.frequency);$('cleanupAmount').value=String(settings.amount??35);$('cleanupCeiling').value=String(settings.ceilingDb??-12);$('cleanupSlope').value=String(settings.slope??12);changed();});
 ['cleanupFrequency','cleanupAmount','cleanupCeiling'].forEach(key=>on($(key),'input',changed));
 on($('cleanupSlope'),'change',changed);
 on($('cleanupOriginal'),'change',()=>cleanupCompare('original'));
 on($('cleanupListen'),'change',()=>cleanupCompare('removed'));
 syncCleanupFields();
}

const FFT=2048,fftRe=new Float64Array(FFT),fftIm=new Float64Array(FFT),fftRev=new Uint16Array(FFT),fftWin=new Float64Array(FFT);
let windowSum=0;for(let i=0;i<FFT;i++){let x=i,r=0;for(let j=0;j<11;j++){r=(r<<1)|(x&1);x>>=1;}fftRev[i]=r;fftWin[i]=.5-.5*Math.cos(2*Math.PI*i/(FFT-1));windowSum+=fftWin[i];}
function fftPower(audio,center,out,scale){
 for(let i=0;i<FFT;i++){const at=center+i-FFT/2;fftRe[fftRev[i]]=(at>=0&&at<audio.length?audio[at]:0)*fftWin[i];fftIm[fftRev[i]]=0;}
 for(let len=2;len<=FFT;len*=2){const half=len/2,cr=Math.cos(-2*Math.PI/len),ci=Math.sin(-2*Math.PI/len);for(let base=0;base<FFT;base+=len){let wr=1,wi=0;for(let j=0;j<half;j++){const a=base+j,b=a+half,tr=wr*fftRe[b]-wi*fftIm[b],ti=wr*fftIm[b]+wi*fftRe[b];fftRe[b]=fftRe[a]-tr;fftIm[b]=fftIm[a]-ti;fftRe[a]+=tr;fftIm[a]+=ti;const next=wr*cr-wi*ci;wi=wr*ci+wi*cr;wr=next;}}}
 const s=4/(windowSum*windowSum)*scale;for(let i=0;i<=FFT/2;i++)out[i]+=(fftRe[i]*fftRe[i]+fftIm[i]*fftIm[i])*s;
}
function scheduleSpectrum(){
 clearTimeout(specTimer);const ticket=++specToken;if(!viewBuffer||$('display').value==='wave'){$('specStatus').hidden=true;return;}
 $('specStatus').hidden=false;$('specStatus').textContent=tr("spectrum_rendering","Rendering spectrum…");specTimer=setTimeout(()=>buildSpectrum(ticket).catch(e=>{if(!dead&&ticket===specToken){$('specStatus').textContent=tr("spectrum_unavailable","Spectrum unavailable");console.error(e);}}),160);
}
async function buildSpectrum(ticket){
 const b=viewBuffer,a=v0,z=v1;if(!b||z<=a)return;const cw=Math.min(800,Math.max(160,Math.floor(w))),ch=224,c=document.createElement('canvas');c.width=cw;c.height=ch;
 const g=c.getContext('2d'),image=g.createImageData(cw,ch),power=new Float64Array(FFT/2+1),ny=Math.min(20000,b.rate/2);
 const palette=[[4,9,7],[16,57,35],[44,153,86],[228,192,75],[238,92,65],[255,239,197]],lookup=[];
 for(let i=0;i<256;i++){const t=i/255*(palette.length-1),j=Math.min(palette.length-2,Math.floor(t)),f=t-j;lookup.push(palette[j].map((v,k)=>Math.round(v+(palette[j+1][k]-v)*f)));}
 const bins=Array.from({length:ch},(_,y)=>{const lo=20*Math.pow(ny/20,1-(y+1)/ch),hi=20*Math.pow(ny/20,1-y/ch);return [clamp(Math.floor(lo*FFT/b.rate),1,FFT/2),clamp(Math.max(Math.ceil(hi*FFT/b.rate),Math.floor(lo*FFT/b.rate)+1),1,FFT/2)];});
 for(let x=0;x<cw;x++){power.fill(0);const center=Math.round((a+(x+.5)/cw*(z-a))*b.rate);b.ch.forEach(audio=>fftPower(audio,center,power,1/b.ch.length));
  for(let y=0;y<ch;y++){let p=0;for(let k=bins[y][0];k<=bins[y][1];k++)p=Math.max(p,power[k]);const color=lookup[clamp(Math.round((10*Math.log10(p||1e-18)+90)/90*255),0,255)],at=(y*cw+x)*4;image.data[at]=color[0];image.data[at+1]=color[1];image.data[at+2]=color[2];image.data[at+3]=255;}
  if(x%16===0){await pause();if(dead||ticket!==specToken)return;}
 }
 if(ticket!==specToken||dead)return;g.putImageData(image,0,0);spec={canvas:c,buffer:b,a,b:z};$('specStatus').textContent=tr("spectrum_scale","20 Hz–{frequency} kHz",{frequency:Math.round(ny/1000)});drawBase();draw();
}
async function refresh(keep=false){
 const oldCursor=cursor,oldSel=selection.slice(),oldView=[v0,v1];draft=null;spec=null;++specToken;
 sourceBuffer=tracks.length?(sideView()?await assemble():await rendered(current())):null;assertAlive();if(sourceBuffer&&sourceBuffer.ch.length===1&&lcvrStereoOnlyComponent(component()))$('component').value='stereo';viewBuffer=sourceBuffer?(component()==='stereo'?sourceBuffer:await lcvrProjectChannels(sourceBuffer,component(),{cancelled:()=>dead})):null;metrics=null;syncEqFields();syncCleanupFields(true);drawSpectrumMeter();cursor=keep?snap(oldCursor):0;selection=keep?oldSel.map(snap):[0,0];
 syncSelection();$('name').textContent=viewBuffer?(sideView()?tr("side_heading","{name}",{name:viewBuffer.name}):tr("song_heading","{name}",{number:selected+1,name:viewBuffer.name})):tr("choose_audio","Add audio to begin");
 list();syncFades();stats();$('warning').textContent='';if(viewBuffer)await makePeaks(viewBuffer);
 if(keep&&oldView[1]>oldView[0])setViewport(oldView[0],oldView[1]);else setViewport(0,duration());drawBase();draw();
 if(viewBuffer){metrics=await analyze(viewBuffer);stats();$('warning').textContent=metrics.peak>0?tr("warning_peak","Above 0 dBFS: reduce level before export."):metrics.tp>0?tr("warning_true_peak","Estimated true peak exceeds 0 dBTP."):Number.isNaN(metrics.lufs)?tr("warning_short","Too short for integrated LUFS."):'';}
}
function editSplitFrames(track=current()){
 const length=track?.ch?.[0]?.length||0;
 return [...new Set((Array.isArray(track?.splits)?track.splits:[]).filter(frame=>Number.isSafeInteger(frame)&&frame>0&&frame<length))].sort((a,b)=>a-b);
}
function remapEditSplits(splits,edit,length){
 const markers=(Array.isArray(splits)?splits:[]).filter(frame=>Number.isSafeInteger(frame)&&frame>0).map(frame=>({name:'',frame}));
 return [...new Set(remapAudioMarkers(markers,edit).map(marker=>marker.frame).filter(frame=>frame>0&&frame<length))].sort((a,b)=>a-b);
}
function splitPlayheadFrame(){
 const track=current(),at=position();
 return track&&Number.isFinite(at)?Math.round(at*track.rate):-1;
}
function splitBoundaryAvailable(){
 const track=current(),frame=splitPlayheadFrame();
 return !!track&&frame>0&&frame<track.ch[0].length&&!editSplitFrames(track).includes(frame);
}
function editSectionsLocked(){
 return busy||dead||!!drag||!!eqGesture||!viewBuffer||sideView()||!!cleanupDraft||metadataDirty()||eqDirty()||eqInvalidFields.size>0;
}
function splitAtPlayhead(){
 if(editSectionsLocked())return;
 const old=current(),frame=splitPlayheadFrame();
 if(frame<=0||frame>=old.ch[0].length){note(tr('split_inside','Place the playhead inside the song to split it.'),true);return;}
 const splits=editSplitFrames(old);
 if(splits.includes(frame))return;
 const next={...old,splits:[...splits,frame].sort((a,b)=>a-b)};
 checkpoint(tr('history_split','Split section'));tracks=tracks.map((track,index)=>index===selected?next:track);
 updateMetadataCaches(old,next);listEditSections();draw();updateControls();
 note(tr('split_done','Section added. Tap a section to select it.'));
}
function listEditSections(){
 const box=$('editSections');if(!box)return;box.replaceChildren();
 const track=current(),splits=editSplitFrames(track);
 box.hidden=!track||!splits.length||sideView();if(box.hidden)return;
 const bounds=[0,...splits,track.ch[0].length];
 for(let index=0;index<bounds.length-1;index++){
  const button=document.createElement('button');button.type='button';button.className='ae-edit-section';button.dataset.editSection=String(index);
  button.textContent=tr('edit_section_button','Section {number}',{number:index+1});
  button.setAttribute('aria-label',tr('edit_section_label','Select section {number}, {start} to {end}',{number:index+1,start:time(bounds[index]/track.rate,6),end:time(bounds[index+1]/track.rate,6)}));
  box.append(button);
 }
 updateEditSectionControls();
}
function updateEditSectionControls(){
 const track=current(),bounds=track?[0,...editSplitFrames(track),track.ch[0].length]:[],locked=editSectionsLocked();
 q('[data-edit-section]').forEach(button=>{
  const index=Number(button.dataset.editSection);
  button.disabled=locked||!Number.isInteger(index)||index<0||index>=bounds.length-1;
  button.setAttribute('aria-pressed',String(!!track&&Math.round(selection[0]*track.rate)===bounds[index]&&Math.round(selection[1]*track.rate)===bounds[index+1]));
 });
}
function selectEditSection(index){
 if(editSectionsLocked()||!Number.isInteger(index))return;
 const track=current(),bounds=[0,...editSplitFrames(track),track.ch[0].length];
 if(index<0||index>=bounds.length-1)return;
 stop(false);cursor=bounds[index]/track.rate;setSelection(cursor,bounds[index+1]/track.rate);revealPlayhead();
 canvas.focus({preventScroll:true});
}
function drawEditSectionGuides(g){
 if(sideView()||!viewBuffer)return;
 const track=current(),splits=editSplitFrames(track);if(!splits.length)return;
 g.save();g.strokeStyle='#70d7a0cc';g.fillStyle='#70d7a0';g.lineWidth=1;g.setLineDash([5,4]);
 for(const frame of splits){const x=xAt(frame/track.rate);if(x<0||x>w)continue;g.beginPath();g.moveTo(x+.5,0);g.lineTo(x+.5,h-24);g.stroke();g.fillRect(x-3,0,6,7);}
 g.restore();
}

function trimBoundaryAvailable(){
 const track=current();
 if(!track||!Number.isFinite(cursor))return false;
 const frame=Math.round(cursor*track.rate),frames=track.ch[0].length;
 return frame>0&&frame<frames;
}
async function editAudioRegion(actionName){
 assertEqSaved();
 const boundary=actionName==='trimStart'||actionName==='trimEnd';
 if(sideView()||!current())throw Error(tr('region_required','Choose Selected song and drag across an audio region first.'));
 if(boundary&&!trimBoundaryAvailable())throw Error(tr('trim_playhead_inside','Place the playhead inside the song to trim its start or end.'));
 if(!boundary&&!hasSelection())throw Error(tr('region_required','Choose Selected song and drag across an audio region first.'));
 const track=current(),frames=track.ch[0].length,at=clamp(Math.round(cursor*track.rate),0,frames);
 const start=actionName==='trimEnd'?0:actionName==='trimStart'?at:clamp(Math.round(selection[0]*track.rate),0,frames);
 const end=actionName==='trimStart'?frames:actionName==='trimEnd'?at:clamp(Math.round(selection[1]*track.rate),0,frames);
 const cut=actionName==='cut',length=cut?frames-(end-start):end-start;
 if(!Number.isFinite(start)||!Number.isFinite(end)||end<=start||length<1)throw Error(tr('edit_empty','This edit would leave no audio. Remove the song in Tracks instead.'));
 await editTrack(async original=>{
  const audio=await rendered(original);
  const ch=audio.ch.map(channel=>{
   if(!cut)return channel.slice(start,end);
   const output=new Float32Array(length);
   output.set(channel.subarray(0,start));output.set(channel.subarray(end),start);
   return output;
  });
  const edit={type:cut?'cut':'trim',start,end};
  return {...original,ch,fade:emptyFade(),markers:remapAudioMarkers(original.markers||[],edit),splits:remapEditSplits(original.splits,edit,length)};
 },historyLabel(actionName));
 const message=actionName==='trimStart'?tr('trim_start_done','Removed the audio before the playhead.'):
  actionName==='trimEnd'?tr('trim_end_done','Removed the audio after the playhead.'):
  cut?tr('selection_cut','Selection removed. Audition the splice for clicks.'):
  tr('selection_trimmed','Kept the selection and removed the rest.');
 note(message);
}

async function editTrack(transform,label=tr('history_edit','Audio edit')){const next=await transform(current());checkpoint(label);tracks=tracks.map((t,i)=>i===selected?next:t);invalidate();await refresh();}
async function commitFade(f){
 if(metadataDirty())throw Error(tr('metadata_pending','Save or reset song details before another edit.'));
 if(cleanupDraft)throw Error(tr('cleanup_pending','Apply or reset the processed preview before saving or starting another edit.'));assertStereoTool();
 const t=current(),d=t.ch[0].length/t.rate;f={in:clamp(f.in,0,d),out:clamp(f.out,0,d),ic:clamp(f.ic,.25,4),oc:clamp(f.oc,.25,4)};if(f.in+f.out>d+.000001)throw Error(tr("fade_length_error","The two fades together cannot exceed the song length."));
 const previous=t.fade||emptyFade();if(['in','out','ic','oc'].every(k=>Math.abs(f[k]-previous[k])<1e-9))return;
 checkpoint(tr('history_fades','Fades'));tracks=tracks.map((x,i)=>i===selected?{...t,fade:f}:x);invalidate();await refresh(true);note(tr("fade_updated","Fade updated. Drag again to adjust it; audio is not faded twice."));
}
function pane(name){if(eqGesture||pointers.size)return;const now=root.querySelector('[data-tab="'+name+'"]')?.getAttribute('aria-pressed')!=='true';q('[data-tab]').forEach(b=>b.setAttribute('aria-pressed',String(now&&b.dataset.tab===name)));q('[data-pane]').forEach(p=>p.hidden=!now||p.dataset.pane!==name);$('tray').hidden=!now;root.classList.toggle('ae-tool-open',now);if(name==='fades'&&now){$('tool').value='fade';draw();}requestAnimationFrame(layout);}
function setTransientMode(enabled){
 transientMode=!!enabled;const toggle=root.querySelector('[data-ui="transientMode"]');if(toggle)toggle.setAttribute('aria-pressed',String(transientMode));
 note(transientMode?tr('transient_mode_on','Tab moves to the next transient; Shift+Tab moves back. Press Escape to restore normal Tab navigation.'):tr('transient_mode_off','Normal Tab navigation restored.'));
}
async function navigateTransient(direction){
 if(!viewBuffer)return;const buffer=viewBuffer;let points=transientCache.get(buffer);
 if(!points){try{points=await findTransients(buffer,{cancelled:()=>dead||viewBuffer!==buffer,onProgress:p=>note(tr('transient_search','Finding transients {percent}%',{percent:Math.round(p*100)}),false,p*100)});}catch(e){if(dead||e.name==='AbortError')throw e;throw Error(tr('transient_failed','Could not find transients in this audio. You can still place the cursor manually.'));}assertAlive();if(buffer!==viewBuffer)return;transientCache.set(buffer,points);}
 const epsilon=.5/buffer.rate,target=direction>0?points.find(t=>t>cursor+epsilon):[...points].reverse().find(t=>t<cursor-epsilon);
 if(target===undefined){note(points.length?tr('transient_end','No further transient in this direction.'):tr('transient_none','No clear transients found. You can still place the cursor manually.'));return;}
 cursor=snap(target);if(cursor<v0||cursor>v1){const span=v1-v0;setViewport(cursor-span*.2,cursor+span*.8);}draw();note(tr('transient_found','Transient at {time}',{time:time(cursor,6)}));
}
let editorPageScroll = null;
function showEditorModal(){
 const hadAutofocus=root.hasAttribute('autofocus');
 root.setAttribute('autofocus','');
 try{modal.showModal();root.focus({preventScroll:true});}
 finally{if(!hadAutofocus)root.removeAttribute('autofocus');}
}
function lockEditorPageScroll(){
 if(editorPageScroll)return;
 const body=document.body,html=document.documentElement;
 const remember=(node,properties)=>properties.map(name=>({name,value:node.style.getPropertyValue(name),priority:node.style.getPropertyPriority(name)}));
 editorPageScroll={x:window.scrollX||0,y:window.scrollY||0,body:remember(body,['position','top','left','right','width','overflow','box-sizing']),html:remember(html,['overflow','overscroll-behavior'])};
 body.style.setProperty('position','fixed');
 body.style.setProperty('top',-editorPageScroll.y+'px');
 body.style.setProperty('left',-editorPageScroll.x+'px');
 body.style.setProperty('right','0');
 body.style.setProperty('width','100%');
 body.style.setProperty('box-sizing','border-box');
 body.style.setProperty('overflow','hidden');
 html.style.setProperty('overflow','hidden');
 html.style.setProperty('overscroll-behavior','none');
}
function restoreEditorPageScroll(){
 if(!editorPageScroll)return;
 const saved=editorPageScroll;editorPageScroll=null;
 const restore=(node,values)=>values.forEach(({name,value,priority})=>{if(value)node.style.setProperty(name,value,priority);else node.style.removeProperty(name);});
 restore(document.body,saved.body);restore(document.documentElement,saved.html);
 window.scrollTo({left:saved.x,top:saved.y,behavior:'instant'});
}

function expand(){
 if(!dead&&(eqGesture||pointers.size))return;
 if(modal){const m=modal;modal=null;root.classList.remove('ae-expanded');placeholder.replaceWith(root);placeholder=null;m.close();m.remove();restoreEditorPageScroll();if(!dead)root.focus({preventScroll:true});setExpandLabel();layout();return;}
 lockEditorPageScroll();placeholder=document.createComment('LCVR editor location');root.before(placeholder);modal=document.createElement('dialog');modal.className='lcvr-ae-modal';document.body.append(modal);modal.append(root);root.classList.add('ae-expanded');on(modal,'cancel',e=>{e.preventDefault();expand();});try{showEditorModal();}catch(error){const failed=modal;modal=null;root.classList.remove('ae-expanded');placeholder.replaceWith(root);placeholder=null;failed.remove();restoreEditorPageScroll();setExpandLabel();layout();throw error;}setExpandLabel();requestAnimationFrame(layout);
}
function zoom(factor){if(!viewBuffer||busy||drag)return;const center=hasSelection()?(selection[0]+selection[1])/2:cursor>=v0&&cursor<=v1?cursor:(v0+v1)/2,len=clamp((v1-v0)*factor,minZoom(),duration());setViewport(center-len/2,center+len/2);}
function captureGestureFrame(){const rect=canvas.getBoundingClientRect();return {left:rect.left,top:rect.top,width:Math.max(1,rect.width),height:Math.max(1,rect.height),w,h};}
function flushGestureLayout(){if(pointers.size||eqGesture)return;gestureFrame=null;if(layoutDeferred){layoutDeferred=false;requestAnimationFrame(layout);}}
function visibleTimeAt(x){return tAt(clamp(x,0,w));}
function cancelPinchFrame(){if(pinchFrame!==null){cancelAnimationFrame(pinchFrame);pinchFrame=null;}}
function applyPinchViewport(){
 if(!drag||drag.kind!=='pinch'||pointers.size<2)return;const g=gesture();
 if(!drag.active){if(Math.abs(g.dist-drag.dist)<Math.max(12,Math.min(24,drag.dist*.04)))return;drag.active=true;drag.dist=g.dist;drag.mid=g.mid;drag.anchor=tAt(g.mid);drag.len=v1-v0;return;}
 const len=clamp(drag.len*drag.dist/g.dist,minZoom(),duration());setViewport(drag.anchor-g.mid/w*len,drag.anchor+(1-g.mid/w)*len);
}
function queuePinchViewport(){if(pinchFrame!==null||!drag||drag.kind!=='pinch'||pointers.size<2)return;pinchFrame=requestAnimationFrame(()=>{pinchFrame=null;applyPinchViewport();});}
const eventXY=e=>{const frame=gestureFrame||captureGestureFrame();return {x:(e.clientX-frame.left)/frame.width*frame.w,y:(e.clientY-frame.top)/frame.height*frame.h};};
function gesture(){const a=Array.from(pointers.values());return {mid:(a[0].x+a[1].x)/2,dist:Math.max(10,Math.hypot(a[0].x-a[1].x,a[0].y-a[1].y))};}
function cancelGesture(){
 cancelPinchFrame(); const old=drag,ids=[...pointers.keys()];pointers.clear();drag=null;draft=null;
 if(old?.origin){selection=old.origin.selection.slice();cursor=old.origin.cursor;if(old.kind==='pan'||old.kind==='pinch')setViewport(...old.origin.viewport);syncSelection();}
 ids.forEach(id=>{try{if(canvas.hasPointerCapture(id))canvas.releasePointerCapture(id);}catch(e){}});flushGestureLayout();updateControls();draw();
}
on(canvas,'pointerdown',e=>{
 if(!viewBuffer||busy||eqGesture||((cleanupDraft||metadataDirty())&&$('tool').value==='fade')||e.button>0||pointers.size>=2)return;if(pointers.size&&(e.pointerType!=='touch'||Array.from(pointers.values()).some(point=>point.type!=='touch')))return;e.preventDefault();canvas.focus({preventScroll:true});if(!pointers.size)gestureFrame=captureGestureFrame();const p={...eventXY(e),type:e.pointerType||'mouse'};pointers.set(e.pointerId,p);canvas.setPointerCapture(e.pointerId);stop(false);
 if(pointers.size===2){const origin=drag?.origin||{selection:selection.slice(),cursor,viewport:[v0,v1]};selection=origin.selection.slice();cursor=origin.cursor;syncSelection();draft=null;const g=gesture();drag={kind:'pinch',dist:g.dist,mid:g.mid,active:false,anchor:tAt(g.mid),len:v1-v0,origin};updateControls();draw();return;}
 const origin={selection:selection.slice(),cursor,viewport:[v0,v1]};
 const handles=handlePoints().map(x=>({...x,d:Math.hypot(x.x-p.x,x.y-p.y)})).filter(x=>x.d<27).sort((a,b)=>a.d-b.d),hit=handles[0];
 if(hit){draft={...fadeState()};drag={kind:'fade',which:hit.kind,initial:{...draft},x:p.x,y:p.y,origin};}
 else if($('tool').value==='pan'){drag={kind:'pan',x:p.x,a:v0,b:v1,origin};}
 else if(hasSelection()&&p.y>h-61&&Math.min(Math.abs(p.x-xAt(selection[0])),Math.abs(p.x-xAt(selection[1])))<25){drag={kind:'edge',edge:Math.abs(p.x-xAt(selection[0]))<Math.abs(p.x-xAt(selection[1]))?0:1,origin};}
 else{drag={kind:'select',x:p.x,touch:e.pointerType==='touch',start:snap(visibleTimeAt(p.x)),origin};cursor=drag.start;setSelection(drag.start,drag.start);}
 updateControls();draw();
});
on(canvas,'pointermove',e=>{
 if(!pointers.has(e.pointerId)||!drag)return;const p={...eventXY(e),type:pointers.get(e.pointerId).type};pointers.set(e.pointerId,p);
 if(drag.kind==='pinch'){queuePinchViewport();return;}
 if(drag.kind==='pan'){const delta=(p.x-drag.x)/w*(drag.b-drag.a);setViewport(drag.a-delta,drag.b-delta);return;}
 if(drag.kind==='select'){if(drag.touch&&Math.abs(p.x-drag.x)<6)return;setSelection(drag.start,visibleTimeAt(p.x));return;}
 if(drag.kind==='edge'){if(drag.edge===0)setSelection(Math.min(visibleTimeAt(p.x),selection[1]),selection[1]);else setSelection(selection[0],Math.max(visibleTimeAt(p.x),selection[0]));return;}
 if(drag.kind==='fade'){
  const k=drag.which,d=duration(),delta=(p.x-drag.x)/w*(v1-v0);if(k==='in')draft.in=clamp(drag.initial.in+delta,0,d-drag.initial.out);else if(k==='out')draft.out=clamp(drag.initial.out-delta,0,d-drag.initial.in);else{const value=clamp(curve(.5,drag.initial[k])-(p.y-drag.y)/(h-24-62),.01,.99);draft[k]=clamp(Math.log(value)/Math.log(.5),.25,4);}draw();
 }
});
async function finishPointer(e){
 if(!pointers.has(e.pointerId))return;if(e.type==='pointercancel'){cancelGesture();return;}const point=eventXY(e);if(drag?.kind==='pinch')cancelPinchFrame();pointers.delete(e.pointerId);try{if(canvas.hasPointerCapture(e.pointerId))canvas.releasePointerCapture(e.pointerId);}catch(error){}const old=drag;
 if(!old){flushGestureLayout();return;}if(old.kind==='pinch'){if(pointers.size)return;drag=null;flushGestureLayout();updateControls();return;}
 if(pointers.size)return;drag=null;flushGestureLayout();
 if(old.kind==='fade'){const f=draft;draft=null;draw();updateControls();if(e.type==='pointercancel')return;await run(()=>commitFade(f));return;}
 if(old.kind==='select'&&Math.abs(point.x-old.x)<(old.touch?6:5)){cursor=snap(visibleTimeAt(point.x));setSelection(0,0);}
 updateControls();draw();
}
on(canvas,'pointerup',finishPointer);on(canvas,'pointercancel',finishPointer);
on(canvas,'lostpointercapture',e=>{if(pointers.has(e.pointerId))cancelGesture();});
on(canvas,'wheel',e=>{if(!viewBuffer||busy||drag)return;if(e.ctrlKey||e.metaKey){e.preventDefault();const p=eventXY(e),anchor=tAt(p.x),len=clamp((v1-v0)*Math.exp(e.deltaY*.004),minZoom(),duration());setViewport(anchor-p.x/w*len,anchor+(1-p.x/w)*len);}else if(e.shiftKey){e.preventDefault();const delta=(e.deltaX||e.deltaY)/w*(v1-v0);setViewport(v0+delta,v1+delta);}},{passive:false});
on(root,'keydown',transportKeydown,{capture:true});
on(root,'keyup',transportKeyup,{capture:true});
on(canvas,'keydown',e=>{if(e.key.toLowerCase()==='m'&&!e.ctrlKey&&!e.metaKey&&!e.altKey){e.preventDefault();if(!e.repeat)addMarker();return;}if(e.key==='Escape'){if(drag)cancelGesture();else if(transientMode)setTransientMode(false);else setSelection(0,0);}if(['+','=','ArrowUp'].includes(e.key)){e.preventDefault();zoom(.5);}if(['-','ArrowDown'].includes(e.key)){e.preventDefault();zoom(2);}if(e.key==='Tab'&&transientMode&&!busy&&!drag){e.preventDefault();action(e.shiftKey?'prevTransient':'nextTransient');}});

 on(root,'click',e=>{
 const sectionButton=e.target.closest('[data-edit-section]');if(sectionButton){if(!sectionButton.disabled)selectEditSection(Number(sectionButton.dataset.editSection));return;}
 const markerButton=e.target.closest('[data-marker]');if(markerButton){
  if(busy||drag||sideView()||markerButton.disabled)return;const index=Number(markerButton.dataset.index),markers=current()?.markers||[],marker=markers[index];if(!marker)return;
  if(markerButton.dataset.marker==='remove'){commitMarkers(markers.filter((_,i)=>i!==index));note(tr('marker_removed','Marker removed.'));}
  else{stop(false);cursor=marker.frame/current().rate;if(cursor<v0||cursor>v1){const span=v1-v0;setViewport(cursor-span/2,cursor+span/2);}draw();canvas.focus({preventScroll:true});}
  return;
 }
 const ui=e.target.closest('[data-ui]');if(ui&&!ui.disabled){const a=ui.dataset.ui;if(a==='expand'){expand();return;}if(a==='load'){$('files').click();return;}if(!viewBuffer)return;if(a==='transientMode'){setTransientMode(!transientMode);canvas.focus({preventScroll:true});return;}if(a==='fit')setViewport(0,duration());if(a==='zoomIn')zoom(.5);if(a==='zoomOut')zoom(2);if(a==='zoomSelection'&&hasSelection())setViewport(...selection);return;}

  const tab=e.target.closest('[data-tab]');if(tab){pane(tab.dataset.tab);return;}
  const button=e.target.closest('[data-act]');if(button&&!button.disabled){action(button.dataset.act);return;}
  const tb=e.target.closest('[data-track]');if(tb&&!busy){const i=Number(tb.dataset.index),a=tb.dataset.track;run(async()=>{if(a==='select'){selected=i;$('view').value='track';await refresh();note(tr("song_selected","Selected {name}",{name:current().name}));return;}if(a==='remove'){checkpoint(tr('history_remove_song','Remove song'));tracks=tracks.filter((_,j)=>j!==i);selected=clamp(selected,0,Math.max(0,tracks.length-1));}else{const to=i+(a==='up'?-1:1);if(to<0||to>=tracks.length)return;checkpoint(tr('history_reorder','Reorder songs'));const next=tracks.slice();[next[i],next[to]]=[next[to],next[i]];tracks=next;selected=to;}invalidate();await refresh();note(tr("track_order_updated","Track order updated."));});}
 });
 on(root,'change',commitMarkerName);
 on(root,'focusout',commitMarkerName);
 on(root,'keydown',e=>{if(e.key==='Enter'&&!e.isComposing&&e.target.closest?.('[data-marker-name]')){e.preventDefault();commitMarkerName(e);}});
 on($('files'),'change',()=>{const files=Array.from($('files').files||[]);$('files').value='';if(!files.length)return;run(async()=>{if(files.length+tracks.length>5)throw Error(tr("song_limit","Five songs maximum. Choose fewer files or remove a song first."));const staged=[];let used=projectBytes(tracks),rate=tracks[0]?.rate;for(const file of files){note(tr("reading_file","Reading {file}…",{file:file.name}));const t=await readAudio(file,used,rate);assertAlive();t.sourceName=file.name;staged.push(t);rate=t.rate;used+=projectBytes([t]);}checkpoint(tracks.length?tr('history_add_audio','Add audio'):tr('history_original','Original'));selected=tracks.length;tracks=tracks.concat(staged);$('view').value='track';invalidate();await refresh();note(tr('audio_loaded',"Audio ready.",{count:staged.length}));});});
 on($('view'),'change',()=>run(async()=>{await refresh();note(sideView()?tr("side_ready","Whole side ready."):tr("song_ready","Song ready."));}));
 on($('meterMode'),'change',stats);
 ['start','end'].forEach(k=>on($(k),'change',()=>{if(busy)return;stop(false);const a=Number($('start').value),b=Number($('end').value);if(Number.isFinite(a)&&Number.isFinite(b))setSelection(a,b);}));


on($('song'),'change',()=>run(async()=>{selected=Number($('song').value);$('view').value='track';await refresh();note(tr("song_selected","Selected {name}",{name:current().name}));}));
on($('display'),'change',()=>{drawBase();draw();scheduleSpectrum();});
on($('tool'),'change',()=>{draw();});
on($('normMode'),'change',()=>{const l=$('normMode').value==='lufs';$('peakField').hidden=l;$('lufsField').hidden=!l;$('ceilingField').hidden=!l;});
on($('pan'),'input',()=>{const len=v1-v0,a=(duration()-len)*Number($('pan').value)/1000;setViewport(a,a+len);});
const exportSettingsChanged=()=>{const hadDownloads=urls.length>0;invalidateDownloads();if(hadDownloads)note(tr('export_settings_changed','Export settings changed. Prepare new downloads to use them.'));};

['side','bits','exportRate'].forEach(key=>{if($(key))on($(key),'change',exportSettingsChanged);});
const onResize=()=>layout();on(window,'resize',onResize);
const observer=typeof ResizeObserver!=='undefined'?new ResizeObserver(()=>{if(Math.abs(canvas.clientWidth-w)>1||Math.abs(canvas.clientHeight-h)>1)layout();}):null;if(observer)observer.observe(canvas);
const unload=e=>{if(playing||tracks.length){e.preventDefault();e.returnValue='';}};on(window,'beforeunload',unload);
installEqControls();installCleanupControls();installMetadataControls();installHistoryControls();layout();updateControls();note(tr("editor_ready","Add audio to begin."));
return {version:'2.2',belongsTo:s=>s.contains(root)||(placeholder&&s.contains(placeholder)),getState:()=>({busy,playing,selected,history:{index:historyIndex,labels:historyEntries.map(entry=>entry.label),canUndo:historyIndex>0,canRedo:historyIndex<historyEntries.length-1,retainedBytes:historyRetainedBytes,maxRetainedBytes:HISTORY_EXTRA_BYTES,maxStates:HISTORY_STATES,earliestPruned:historyEarliestPruned},release:{...releaseInfo},scope:$('view').value,transientMode,selection:selection.slice(),duration:duration(),viewport:[v0,v1],metrics:metrics?{lufs:metrics.lufs,rms:metrics.rms,tp:metrics.tp,crest:metrics.crest,peak:metrics.peak,correlation:metrics.correlation,mid:metrics.mid,sideLevel:metrics.sideLevel,phaseKind:metrics.phaseKind}:null,tracks:tracks.map(t=>({name:t.name,sourceName:t.sourceName||t.name,artist:t.artist||"",isrc:t.isrc||"",rate:t.rate,channels:t.ch.length,frames:t.ch[0].length,sourceFormat:t.sourceFormat,sourceBits:t.sourceBits,splits:editSplitFrames(t),markers:(t.markers||[]).map(m=>({...m})),fade:{...(t.fade||emptyFade())}}))}),execute:a=>action(a),destroy(){dead=true;listeners.abort();cancelGesture();specToken++;clearTimeout(specTimer);stop(false);if(modal)expand();restoreEditorPageScroll();window.removeEventListener('resize',onResize);window.removeEventListener('beforeunload',unload);if(observer)observer.disconnect();urls.forEach(URL.revokeObjectURL);if(context)context.close().catch(()=>{});discardCleanup();historyEntries=[];historyPending=null;tracks=[];viewBuffer=sourceBuffer=sideCache=metrics=undo=null;}};
}
function boot(){document.querySelectorAll('[data-lcvr-ae2]').forEach(root=>{if(!root.__lcvrAE2)try{root.__lcvrAE2=init(root);instances.add(root);}catch(e){console.error(e);const s=root.querySelector('[data-el="status"]');if(s){let messages={};try{messages=JSON.parse(root.querySelector('[data-el="messages"]')?.textContent||'{}');}catch(error){}s.textContent=String(messages.engine_start_error??"The audio editor could not start. Reload the page and try again.");s.dataset.error='true';}}});}
window.LCVRAudioEditorV2=boot;
document.addEventListener('shopify:section:load',boot);
document.addEventListener('shopify:section:unload',e=>{instances.forEach(root=>{if(root.__lcvrAE2?.belongsTo(e.target)){root.__lcvrAE2.destroy();delete root.__lcvrAE2;instances.delete(root);}});});
if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',boot,{once:true});boot();
})();
